/**
 * Coportal API wrappers (Round-5 frontend port; extended in Round-7).
 *
 * Backs the CS-Portal pages — Dashboard, Agents, Issues, plus the R7 batch
 * (weekly-arrivals, flight-check, no-show, paging-boards, issues-report,
 * modifications, suppliers, hot-plate).
 *
 * Endpoints mirrored from the salvaged controllers under
 *   api/Modules/Coportal/Controllers/*
 *
 *   DashboardController.cs           → GET /api/coportal/dashboard/summary
 *   AgentsController.cs              → GET /api/coportal/agents
 *   IssuesController.cs              → GET /api/coportal/issues?...
 *   LogsController.cs                → GET /api/coportal/logs?page=logs-all&...
 *   NotesController.cs               → GET /api/notes  (user_notes)
 *   WeeklyArrivalsController.cs      → GET /api/coportal/weekly-arrivals
 *                                      POST /generate · DELETE
 *   FlightCheckController.cs         → GET /rows · POST /check · POST /save
 *                                      GET /status
 *   NoShowController.cs              → GET /api/coportal/no-show/report-pdf
 *   PagingBoardsController.cs        → GET /api/coportal/paging-boards
 *   IssuesReportController.cs        → GET /api/coportal/issues-report
 *   ModificationsLogController.cs    → GET /api/coportal/modifications
 *   SuppliersController.cs           → /api/coportal/suppliers (CRUD)
 *   HotPlateTasksController.cs       → /api/coportal/hot-plate-tasks (CRUD)
 *
 * Server shapes mirror `api/Modules/Coportal/Models/CoportalModels.cs`.
 * Every field is nullable on the wire — partial rows from Supabase must not
 * crash the React surface.
 */
import { COMPANY } from '@/shared/company';
import { ApiError, apiFetch, apiFetchRaw } from '@/auth/api';
import { readXsrfToken } from '@/auth/csrf';
import { forRegion, regionName, type AgentRegion } from './agentRegion';
import { countryNamesRe } from '@/shared/geo/countries';
import { defaultDestination, destinationOfLog } from '@/shared/geo/opsGeo';

// ─── Dashboard ────────────────────────────────────────────────────────

export interface DashboardSummary {
  issuesOpen: number;
  issuesPending: number;
  vipsToday: number;
  welcomeMeetingsToday: number;
  hotPlateTodo: number;
  hotPlateDoing: number;
  hotPlateDone: number;
  /** Fourth hot-plate column — the board renders todo/doing/waiting/done. */
  hotPlateWaiting: number;
  // Tomorrow KPIs (PHP hero parity — additive). Older servers that predate
  // these fields simply return 0 via the `?? 0` fallback below.
  welcomeMeetingsTomorrow: number;
  logsToday: number;
  logsTomorrow: number;
  /**
   * The dates the server took these counts against, in the PORTAL's timezone
   * (Asia/Kuala_Lumpur). Null on servers that predate the field.
   */
  todayIso: string | null;
  tomorrowIso: string | null;
}

/**
 * The wire shape, accepted in BOTH casings.
 *
 * This is the bug that made every KPI tile on the CS/OPS Portal dashboard read
 * 0 while the panels beside them showed correct data. `DashboardSummary` on the
 * server was the one Coportal model without explicit [JsonPropertyName]
 * attributes, and `AddControllers()` serialises with JsonSerializerDefaults.Web
 * (camelCase) — so the server sent `{"issuesOpen":9,…}` while this mapper read
 * `raw.IssuesOpen` and got `undefined` ten times, silently `?? 0`-ing each one.
 *
 * The server now pins camelCase explicitly, so `issuesOpen` is the real
 * contract; the PascalCase half is kept purely so a browser holding this bundle
 * against an older API build still renders real numbers. Same defensive shape
 * the Hub-home dashboard already used (features/dashboard/DashboardPage.tsx) —
 * that copy is exactly why the Hub home tile showed a number while this one
 * showed zeros.
 */
interface RawDashboardSummary {
  issuesOpen?: number;              IssuesOpen?: number;
  issuesPending?: number;           IssuesPending?: number;
  vipsToday?: number;               VipsToday?: number;
  welcomeMeetingsToday?: number;    WelcomeMeetingsToday?: number;
  hotPlateTodo?: number;            HotPlateTodo?: number;
  hotPlateDoing?: number;           HotPlateDoing?: number;
  hotPlateDone?: number;            HotPlateDone?: number;
  hotPlateWaiting?: number;         HotPlateWaiting?: number;
  welcomeMeetingsTomorrow?: number; WelcomeMeetingsTomorrow?: number;
  logsToday?: number;               LogsToday?: number;
  logsTomorrow?: number;            LogsTomorrow?: number;
  todayIso?: string | null;         TodayIso?: string | null;
  tomorrowIso?: string | null;      TomorrowIso?: string | null;
}

/**
 * Exported for test: maps the raw envelope onto the client shape. Kept separate
 * from the fetch so the casing contract can be pinned without a network stub.
 */
export function parseDashboardSummary(
  raw: RawDashboardSummary | null | undefined,
): DashboardSummary {
  const r = raw ?? {};
  return {
    issuesOpen: r.issuesOpen ?? r.IssuesOpen ?? 0,
    issuesPending: r.issuesPending ?? r.IssuesPending ?? 0,
    vipsToday: r.vipsToday ?? r.VipsToday ?? 0,
    welcomeMeetingsToday: r.welcomeMeetingsToday ?? r.WelcomeMeetingsToday ?? 0,
    hotPlateTodo: r.hotPlateTodo ?? r.HotPlateTodo ?? 0,
    hotPlateDoing: r.hotPlateDoing ?? r.HotPlateDoing ?? 0,
    hotPlateDone: r.hotPlateDone ?? r.HotPlateDone ?? 0,
    hotPlateWaiting: r.hotPlateWaiting ?? r.HotPlateWaiting ?? 0,
    welcomeMeetingsTomorrow:
      r.welcomeMeetingsTomorrow ?? r.WelcomeMeetingsTomorrow ?? 0,
    logsToday: r.logsToday ?? r.LogsToday ?? 0,
    logsTomorrow: r.logsTomorrow ?? r.LogsTomorrow ?? 0,
    todayIso: r.todayIso ?? r.TodayIso ?? null,
    tomorrowIso: r.tomorrowIso ?? r.TomorrowIso ?? null,
  };
}

export async function getDashboardSummary(
  signal?: AbortSignal,
): Promise<DashboardSummary> {
  const raw = await apiFetch<RawDashboardSummary>('/coportal/dashboard/summary', {
    method: 'GET',
    signal,
  });
  return parseDashboardSummary(raw);
}

// ─── Agents ───────────────────────────────────────────────────────────

/**
 * `Agent` mirrors `Agent` in `CoportalModels.cs`. Only the subset of fields
 * the React UI surfaces today — extra SuperTransfers branding fields are
 * present on the server but not needed in the directory drawer yet.
 */
export interface Agent {
  id: string | null;
  agent_name: string | null;
  market: string | null;
  logo_url: string | null;
  welcome_letter: string | null;
  remarks: string | null;
  /** Emergency / hotline numbers, free text, one per line. */
  emergency_contacts: string | null;
  vip_agent: boolean | null;
  st_excluded: boolean | null;
  st_excluded_condition: string | null;
  alternate_names: string | null;
  // Super-transfers white-label branding (PHP parity T1-02b).
  // Drives Audley / Kuoni co-brand on letter PDFs + the public guest
  // transfer page. `st_brand_sig` overrides the computed sign-off when
  // present (closes the Audley double-suffix bug; see composeBrandSig).
  st_brand_name: string | null;
  st_brand_sig: string | null;
  st_logo_url: string | null;
  st_second_logo: string | null;
  st_accent_color: string | null;
  st_use_default_logo: boolean | null;
  /**
   * Paging boards print the company logo instead of the agent's. Absent until
   * `db/2026-10-02_agents_paging_default_logo.sql` runs (reads as false).
   */
  paging_use_default_logo?: boolean | null;
  /**
   * The country its letters name: 'my', 'sg', or null for both. Absent until
   * `db/2026-10-05_agents_st_region.sql` runs (reads as both).
   */
  st_region?: string | null;
  /**
   * Agent features (2026-10-15): quick filter, row marker, welcome meetings,
   * courtesy calls, partner ref; one jsonb object (`agents/agentFeatures.ts`).
   * Absent until `db/2026-10-15_agent_features.sql` runs; null = none.
   */
  features?: unknown;
  /** Custom fields (brief §5 M6), by id. Absent until db/2026-10-26_custom_fields.sql runs. */
  custom?: Record<string, unknown> | null;
}

/** A saved agent, plus the server's note when part of the save waits on SQL. */
export interface SavedAgent extends Agent {
  saveWarning?: string | null;
}

export interface SaveAgentRequest {
  id?: string | null;
  agentName: string;
  market?: string | null;
  logoUrl?: string | null;
  welcomeLetter?: string | null;
  remarks?: string | null;
  emergencyContacts?: string | null;
  vipAgent?: boolean | null;
  stExcluded?: boolean | null;
  stExcludedCondition?: string | null;
  alternateNames?: string | null;
  stBrandName?: string | null;
  stBrandSig?: string | null;
  stLogoUrl?: string | null;
  stSecondLogo?: string | null;
  stAccentColor?: string | null;
  stUseDefaultLogo?: boolean | null;
  pagingUseDefaultLogo?: boolean | null;
  /** Omitted = left as it is; 'mysg' clears the column (both countries). */
  stRegion?: AgentRegion;
  /** Agent features (`featuresPayload`); omitted = left as it is. */
  features?: Record<string, unknown>;
  /** Custom fields (brief §5 M6): `{ fieldId: value | null }`, the changed ones. */
  custom?: Record<string, unknown>;
}

/**
 * Per-agent logo field key — must match the .NET `AllowedLogoFields`
 * whitelist in `AgentsController.UploadLogo`. PHP source: `upload-agent-logo.php`.
 * `logo` is the agent window's ONE logo: it stamps logo_url and st_logo_url.
 */
export type AgentLogoField = 'logo' | 'logo_url' | 'st_logo_url' | 'st_second_logo';

/**
 * Compose the letter sign-off line from an agent's brand fields.
 *
 * Mirrors `composeBrandSig()` in `hub/coportal/includes/helpers.php` and
 * `_composeBrandSig()` in `assets/js/super-transfers.js`. Prefers the
 * explicit `st_brand_sig` override when set; otherwise appends
 * " Malaysia & Singapore" to the brand name — unless the brand name
 * already contains "Malaysia" or "Singapore", which would double the
 * suffix (the original Audley bug).
 *
 * Falls back to `agent_name` when `st_brand_name` is empty so the
 * helper still produces a usable line for agents that haven't been
 * white-labelled.
 *
 * `region` narrows it for an agent working with us in one country: the
 * suffix is that country, and a "Malaysia & Singapore" already in the line
 * becomes it (`forRegion`). Mirrors `SuperTransfersController.ComposeBrandSig`.
 */
export function composeBrandSig(
  agent: Pick<Agent, 'st_brand_sig' | 'st_brand_name' | 'agent_name'> | null,
  region: AgentRegion = 'mysg',
): string {
  if (!agent) return '';
  const sig = (agent.st_brand_sig ?? '').trim();
  if (sig) return forRegion(sig, region);
  const name = (agent.st_brand_name ?? agent.agent_name ?? '').trim();
  if (!name) return '';
  if ((countryNamesRe() ?? /\b(malaysia|singapore)\b/i).test(name)) return forRegion(name, region);
  return `${name} ${regionName(region)}`;
}

export interface AgentsFilters {
  market?: string;
  vipOnly?: boolean;
}

export async function listAgents(
  filters: AgentsFilters,
  signal?: AbortSignal,
): Promise<Agent[]> {
  const qs = new URLSearchParams();
  if (filters.market) qs.set('market', filters.market);
  if (filters.vipOnly) qs.set('vipOnly', 'true');
  const path = `/coportal/agents${qs.size ? `?${qs}` : ''}`;
  const data = await apiFetch<Agent[]>(path, { method: 'GET', signal });
  return data ?? [];
}

export async function saveAgent(req: SaveAgentRequest): Promise<SavedAgent> {
  // Controller expects PascalCase property names per .NET defaults; the
  // salvaged record `SaveAgentRequest` declares `Id`, `AgentName`, etc.
  // The `St*` fields carry the white-label brand columns Audley + Kuoni
  // rely on (T1-02b parity).
  const body = {
    Id: req.id ?? null,
    AgentName: req.agentName,
    Market: req.market ?? null,
    LogoUrl: req.logoUrl ?? null,
    WelcomeLetter: req.welcomeLetter ?? null,
    Remarks: req.remarks ?? null,
    EmergencyContacts: req.emergencyContacts ?? null,
    StExcludedCondition: req.stExcludedCondition ?? null,
    VipAgent: req.vipAgent ?? null,
    StExcluded: req.stExcluded ?? null,
    AlternateNames: req.alternateNames ?? null,
    StBrandName: req.stBrandName ?? null,
    StBrandSig: req.stBrandSig ?? null,
    StLogoUrl: req.stLogoUrl ?? null,
    StSecondLogo: req.stSecondLogo ?? null,
    StAccentColor: req.stAccentColor ?? null,
    StUseDefaultLogo: req.stUseDefaultLogo ?? null,
    PagingUseDefaultLogo: req.pagingUseDefaultLogo ?? null,
    // null = not sent, left as it is (the server reads "mysg" as both).
    StRegion: req.stRegion ?? null,
    // null = not sent, left as it is (db/2026-10-15_agent_features.sql).
    Features: req.features ?? null,
    // Custom fields (brief §5 M6): only when the drawer has some; absent = untouched.
    ...(req.custom ? { Custom: req.custom } : {}),
  };
  // Raw: the reply may carry a `warning` beside `data` (the paging-board
  // switch saved nothing until its SQL file runs; everything else did).
  const res = await apiFetchRaw<{ ok?: boolean; data?: Agent | null; warning?: string | null; error?: string }>(
    '/coportal/agents',
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
  if (res.ok === false || !res.data) throw new ApiError(500, res.error || 'Could not save the agent.');
  return { ...res.data, saveWarning: res.warning ?? null };
}

/**
 * Multipart logo upload — mirrors the attachments uploader pattern
 * (`uploadAttachment` below). Posts a single `file` plus a `field`
 * discriminator that the controller's whitelist validates. The endpoint
 * stamps the agent row with the resulting public URL and returns it so
 * the drawer can refresh its preview without a list refetch.
 */
export async function uploadAgentLogo(
  agentId: string,
  file: File,
  field: AgentLogoField,
): Promise<{ url: string; field: AgentLogoField; patch_failed: boolean }> {
  const fd = new FormData();
  fd.set('file', file);
  fd.set('field', field);
  const xsrf =
    typeof document !== 'undefined'
      ? document.cookie
          .split(';')
          .map((s) => s.trim())
          .find((s) => s.startsWith('XSRF-TOKEN='))
          ?.split('=')[1]
      : undefined;
  const res = await fetch(
    `/api/coportal/agents/${encodeURIComponent(agentId)}/logo-upload`,
    {
      method: 'POST',
      credentials: 'include',
      headers: xsrf ? { 'X-XSRF-TOKEN': decodeURIComponent(xsrf) } : {},
      body: fd,
    },
  );
  if (!res.ok) {
    let msg = `Upload failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const json = (await res.json()) as {
    url?: string;
    field?: AgentLogoField;
    patch_failed?: boolean;
  };
  return {
    url: json.url ?? '',
    field: json.field ?? field,
    patch_failed: !!json.patch_failed,
  };
}

export async function deleteAgent(id: string): Promise<void> {
  await apiFetch<void>(
    `/coportal/agents/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Issues ───────────────────────────────────────────────────────────

/** Status literals served by the .NET `IssuesController.AllowedStatus` set. */
export type IssueStatus =
  | 'open'
  | 'investigating'
  | 'resolved'
  | 'closed'
  | 'escalated';

/**
 * The STORABLE statuses — the drawer's picker and the chip row.
 *
 * `'resolved'` is deliberately absent even though it stays in the
 * `IssueStatus` union (a legacy row could still be spelled that way and has
 * to normalise onto a pill). PHP has no resolved state: the whitelist is
 * Open / Closed / Infos Needed / Report Needed / Reply Needed, and the API
 * maps `resolved` → `'Closed'` on save. So offering it here meant the user
 * picked "resolved", got "closed" back with no explanation, and stared at a
 * `resolved` chip that was pinned at 0 forever (live: 0 of 1,011 rows).
 * Mirrors `IssueStatusBuckets.ChipOrder` on the server.
 */
export const ISSUE_STATUSES: readonly IssueStatus[] = [
  'open',
  'investigating',
  'closed',
  'escalated',
] as const;

/**
 * Filter-only bucket: "every issue that is not closed", which is how BOTH PHP
 * pages define an open issue (dashboard.php:59 `status neq.Closed`,
 * issues.php:48 `strtolower(status) !== 'closed'`, NULL counting as open).
 *
 * It is deliberately NOT part of `ISSUE_STATUSES`: that list is the set of
 * STORABLE statuses and also drives the drawer's status picker, so adding
 * `unresolved` there would offer an unsavable value as a status.
 *
 * The dashboard "open issues" tile counts this bucket (9 live) rather than the
 * `open` chip (8 live) — before this existed, the tile and the page it linked
 * to could not agree.
 */
export const ISSUE_STATUS_UNRESOLVED = 'unresolved';

/**
 * What the DRAWER offers — the five literals PHP actually stores
 * (`hub/coportal/api/issues.php:56`, picker at `issues.php:420-426`).
 *
 * This is deliberately NOT {@link ISSUE_STATUSES}. Those four are display
 * BUCKETS for the chip row, and two of them are lossy: `investigating` covers
 * both `Infos Needed` and `Reply Needed`, `escalated` covers `Report Needed`.
 * Offering buckets in the editor meant three of PHP's five states were simply
 * unreachable from React, and picking `investigating` on an `Infos Needed` row
 * silently rewrote it to `Reply Needed` (server `ToStoredStatus` has to guess).
 *
 * Sending a literal skips the guess entirely — `ToStoredStatus` returns any of
 * these five verbatim. The bucket layer still owns filtering and the chips.
 */
export const ISSUE_STORED_STATUSES = [
  'Open',
  'Infos Needed',
  'Report Needed',
  'Reply Needed',
  'Closed',
] as const;

export type IssueStoredStatus = (typeof ISSUE_STORED_STATUSES)[number];

/**
 * Map any stored spelling onto one of the five, for seeding the drawer's
 * picker. Mirrors the server's `IssueStatusBuckets.ToStoredStatus`; a value
 * that is already one of the five is returned as-is.
 */
export function toStoredIssueStatus(raw: string | null | undefined): IssueStoredStatus {
  const s = String(raw ?? '').trim();
  const exact = ISSUE_STORED_STATUSES.find(
    (v) => v.toLowerCase() === s.toLowerCase(),
  );
  if (exact) return exact;
  const l = s.toLowerCase();
  if (l.includes('close') || l.startsWith('resolv')) return 'Closed';
  if (l.includes('infos')) return 'Infos Needed';
  if (l.startsWith('report') || l.startsWith('escalat')) return 'Report Needed';
  if (l.includes('reply') || l.includes('investig')) return 'Reply Needed';
  return 'Open';
}

/** Status values accepted as a `?status=` filter (chips + deep links). */
/**
 * Visible status chips.
 *
 * `unresolved` is NOT among them any more: "open" now carries the same
 * meaning it has in PHP — everything not closed — so the two chips were
 * identical and one of them had to be a lie about the other. The bucket
 * still resolves server-side, so the dashboard tile's `?status=unresolved`
 * deep links keep working.
 */
export const ISSUE_STATUS_FILTERS = [...ISSUE_STATUSES] as const;

/**
 * Cause categories — mirrors the PHP `issues.php` cause-category <select>.
 * Used both as the drawer's quick-tag set and the toolbar's cause filter.
 */
export const ISSUE_CAUSE_CATEGORIES = [
  'transporter',
  'hotel',
  'guests',
  'internal',
  'supplier',
  'agent',
  'guide',
] as const;

export type IssueCauseCategory = (typeof ISSUE_CAUSE_CATEGORIES)[number];

/**
 * Drawer quick-pick tags — the broad categories above plus granular sub-causes
 * the CS team records verbatim (mirrors PHP `issues.php` `$_issueCategories`).
 * Used only for the issue drawer's cause chips; the toolbar/filter keep the
 * broad {@link ISSUE_CAUSE_CATEGORIES} set. Granular values are stored verbatim
 * in `cause` and still match the broad "internal" filter via ilike substring.
 */
export const ISSUE_CAUSE_QUICK_TAGS: readonly string[] = [
  ...ISSUE_CAUSE_CATEGORIES,
  'Internal - Airport Representative',
];

/** Severity ladder — `importance` in PHP maps to 1 (low) / 2 (med) / 3 (high+). */
export type IssueSeverity = 1 | 2 | 3;

export interface IssueLog {
  id: number | null;
  created_at: string | null;
  service_date: string | null;
  sector: string | null;
  market: string | null;
  agent: string | null;
  ref: string | null;
  guest_name: string | null;
  supplier: string | null;
  issue: string | null;
  cause: string | null;
  solution: string | null;
  status: IssueStatus | string | null;
  importance: IssueSeverity | number | null;
  handled_by: string | null;
  team_member: string | null;
  compensation: string | null;
  remarks: string | null;
  /** Explanation sent to the agent — distinct from `solution` (what CS did). */
  reason_given: string | null;
  covered_by: string | null;
  compensation_value: string | null;
  /** Derived server-side from the number of causes; never typed by hand. */
  issue_count: number | null;
  /** Stamped on every save (PHP `issues.php:1167`). */
  updated_date: string | null;
  /**
   * Not a column: the booking's File Manager (`logs.file_manager` on the
   * lines carrying this ref), added by the list endpoint. Absent when the ref
   * matches no log line, and on rows that did not come from the list.
   */
  booking_fm?: string | null;
  /** Custom fields (brief §5 M6), by id. Absent until db/2026-10-26_custom_fields.sql runs. */
  custom?: Record<string, unknown> | null;
}

export interface SaveIssueRequest {
  id?: number | null;
  serviceDate?: string | null;
  sector?: string | null;
  market?: string | null;
  agent?: string | null;
  ref?: string | null;
  guestName?: string | null;
  supplier?: string | null;
  issue?: string | null;
  /** Cause — free text and/or a cause-category tag (PHP core field). */
  cause?: string | null;
  solution?: string | null;
  /**
   * Either a display bucket or — preferred, and what the drawer sends — one of
   * the five PHP literals in {@link ISSUE_STORED_STATUSES}. The server accepts
   * both; a literal is passed through untouched, a bucket has to be guessed at.
   */
  status?: IssueStatus | IssueStoredStatus | null;
  /** Severity 1 (low) / 2 (med) / 3 (high) — PHP `importance` column. */
  importance?: IssueSeverity | null;
  handledBy?: string | null;
  /** Team member the issue is attributed to (PHP `team_member` display string). */
  teamMember?: string | null;
  compensation?: string | null;
  remarks?: string | null;
  reasonGiven?: string | null;
  coveredBy?: string | null;
  compensationValue?: string | null;
  /** Custom fields (brief §5 M6): `{ fieldId: value | null }`, the changed ones. */
  custom?: Record<string, unknown>;
}

export interface IssuesFilters {
  status?: string;
  sector?: string;
  agent?: string;
  market?: string;
  /** Severity ladder 1/2/3 — now a server filter (PHP `importance`). */
  severity?: 1 | 2 | 3;
  /** Cause-category filter (PHP `issues.php` <select>). */
  cause?: string;
  dateFrom?: string;
  dateTo?: string;
  q?: string;
}

/** One server-paged slice of the issues log. */
export interface IssuesPage {
  rows: IssueLog[];
  /** Rows matching the full filter set (from PostgREST Content-Range). */
  total: number;
  page: number;
  limit: number;
}

/** Chip badge counts across the full filtered set (keyed by status + `all`). */
export type IssueStatusCounts = Record<string, number>;

interface ListIssuesRawEnvelope {
  ok?: boolean;
  data?: IssueLog[] | null;
  total?: number | null;
  page?: number | null;
  limit?: number | null;
}

function issuesQueryString(filters: IssuesFilters): URLSearchParams {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.sector) qs.set('sector', filters.sector);
  if (filters.market) qs.set('market', filters.market);
  if (filters.severity) qs.set('severity', String(filters.severity));
  if (filters.cause) qs.set('cause', filters.cause);
  if (filters.agent) qs.set('agent', filters.agent);
  if (filters.dateFrom) qs.set('date_from', filters.dateFrom);
  if (filters.dateTo) qs.set('date_to', filters.dateTo);
  if (filters.q) qs.set('q', filters.q);
  return qs;
}

/**
 * Fetch one server-paged slice of the issues log. All filters — including
 * market + severity, which used to be applied client-side — are now pushed to
 * the .NET `IssuesController.List`, so the slice and its `total` stay coherent.
 */
export async function listIssues(
  filters: IssuesFilters,
  page: number,
  limit: number,
  signal?: AbortSignal,
): Promise<IssuesPage> {
  const qs = issuesQueryString(filters);
  qs.set('page', String(page));
  qs.set('limit', String(limit));
  const res = await apiFetchRaw<ListIssuesRawEnvelope>(
    `/coportal/issues?${qs}`,
    { method: 'GET', signal },
  );
  const rows = (res.data ?? []) as IssueLog[];
  return {
    rows,
    total: res.total ?? rows.length,
    page: res.page ?? page,
    limit: res.limit ?? limit,
  };
}

/**
 * Fetch the per-status chip counts across the full filtered set (all active
 * filters EXCEPT status). Backs the status chips independent of the page.
 */
export async function getIssueStatusCounts(
  filters: Omit<IssuesFilters, 'status'>,
  signal?: AbortSignal,
): Promise<IssueStatusCounts> {
  const qs = issuesQueryString({ ...filters, status: undefined });
  const path = `/coportal/issues/status-counts${qs.size ? `?${qs}` : ''}`;
  const res = await apiFetchRaw<{ ok?: boolean; data?: IssueStatusCounts | null }>(
    path,
    { method: 'GET', signal },
  );
  return res.data ?? {};
}

export async function saveIssue(req: SaveIssueRequest): Promise<IssueLog> {
  const body = {
    Id: req.id ?? null,
    ServiceDate: req.serviceDate ?? null,
    Sector: req.sector ?? null,
    Market: req.market ?? null,
    Agent: req.agent ?? null,
    Ref: req.ref ?? null,
    GuestName: req.guestName ?? null,
    Supplier: req.supplier ?? null,
    Issue: req.issue ?? null,
    Cause: req.cause ?? null,
    Solution: req.solution ?? null,
    Status: req.status ?? null,
    Importance: req.importance ?? null,
    HandledBy: req.handledBy ?? null,
    TeamMember: req.teamMember ?? null,
    Compensation: req.compensation ?? null,
    Remarks: req.remarks ?? null,
    ReasonGiven: req.reasonGiven ?? null,
    CoveredBy: req.coveredBy ?? null,
    CompensationValue: req.compensationValue ?? null,
    // Custom fields (brief §5 M6): only when the drawer has some; absent = untouched.
    ...(req.custom ? { Custom: req.custom } : {}),
  };
  return apiFetch<IssueLog>(
    '/coportal/issues',
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
}

/**
 * Reference data for the issue drawer's selects and tag pickers.
 *
 * PHP reads all of this inline when it renders `issues.php`; a SPA drawer has
 * to fetch it. Server-cached for 5 minutes, so this is cheap to hold with a
 * long `staleTime` on the client too.
 */
export interface IssueFormOptions {
  sectors: { value: string; label: string }[];
  markets: string[];
  fileManagers: string[];
  /** `handled_by` candidates — `file_managers` rows whose market is CS. */
  csTeam: string[];
  /** Supplier and hotel names in one list, as PHP's single datalist has them. */
  suppliers: string[];
  /** Admin-editable `issue_categories`, falling back to PHP's default 15. */
  causeCategories: string[];
}

export async function getIssueFormOptions(
  signal?: AbortSignal,
): Promise<IssueFormOptions> {
  const res = await apiFetchRaw<{ ok?: boolean; data?: IssueFormOptions | null }>(
    '/coportal/issues/form-options',
    { method: 'GET', signal },
  );
  return (
    res.data ?? {
      sectors: [],
      markets: [],
      fileManagers: [],
      csTeam: [],
      suppliers: [],
      causeCategories: [],
    }
  );
}

export async function deleteIssue(id: number): Promise<void> {
  await apiFetch<void>(
    `/coportal/issues/${id}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Logs (dashboard widget) ──────────────────────────────────────────

/**
 * The dashboard's "Today's logs" widget pulls a slim slice of the `logs`
 * table — same endpoint the main logs page uses, scoped to today.
 */
export interface LogRow {
  /**
   * The tenant's custom fields of service lines (brief §5 M6), by id. Sent only
   * when a custom field is in use; absent for a tenant with none.
   */
  custom?: Record<string, unknown> | null;
  id: string | null;
  ref: string | null;
  log: string | null;
  sector: string | null;
  /**
   * Why the line has its sector (the database's sector engine, brief
   * §8.4.2a): manual, supplier:<name>, rule:<id>, area:<name>, default...
   * Absent before db/2026-10-26_sector_engine.sql, null on lines written
   * while the engine was off. The sector pill's hover reads it.
   */
  sector_source?: string | null;
  service_date: string | null;
  pickup_time: string | null;
  pickup_location: string | null;
  dropoff_location: string | null;
  agent: string | null;
  passenger_name: string | null;
  pax: number | null;
  supplier: string | null;
  product: string | null;
  status: string | null;
  courtesy_call_status: string | null;
  /**
   * The courtesy note and the transfer message: add-service and the import
   * always wrote them, and the API's projection reads them back since
   * 2026-10-28 (LogRow, LogRowProjection); absent from an older API.
   */
  courtesy?: string | null;
  transfer_message?: string | null;
  vip: boolean | null;
  is_group: boolean | null;
  transfer: string | null;
  /**
   * Optional kuoni_ref (PHP `linked-refs` ref cell — Chunk 4 R15). When
   * present and distinct from `ref`, the ref cell renders a `K:<ref>` pill
   * that opens the Guest Timeline drawer on the kuoni ref.
   */
  kuoni_ref?: string | null;
  /**
   * Optional linked-refs (sibling bookings that share the same trip key).
   * Rendered as `↔ <ref>` pills in the ref cell — each clickable and
   * scoped through the same `onRefClick` handler so the union timeline
   * picks up rows from any of the linked refs. PHP source: table.js 1169.
   * .NET will surface `linked_refs` as a string[] (JSONB column) when the
   * server SELECT grows the column list; today the field stays optional
   * so the wire payload doesn't change for callers that haven't updated.
   */
  linked_refs?: string[] | null;
  /**
   * Optional line_ref carried for parity with the PHP `line_ref` column.
   * Surfaces as the line code (e.g. `KUL-2026-04-09-v1`) inside the SIC
   * sub-rows where it gets suffixed with `-v<i>` per virtual line.
   */
  line_ref?: string | null;
  /**
   * SIC per-line overrides (logs-parity audit Chunk 5 · R30 / E9 / E10).
   * Each entry overrides a subset of fields on the corresponding virtual
   * sub-row beneath the parent SIC row. PHP source: assets/js/table.js
   * lines 1667-1777 (sub-row rendering) + edit-modal.js lines 156-161 +
   * 185-201 (modal banner + override read-back).
   *
   * Wire shape: the live PHP column is a JSONB **object** keyed by string
   * index (`{"0": {...}, "1": {...}}`); the React port normalizes it to
   * the simpler array form (see `normalizeSicLineOverrides`) so the type
   * matches the per-passenger semantic the UI surfaces. Both shapes are
   * tolerated on read so an old row written by the PHP modal keeps
   * rendering correctly.
   */
  sic_line_overrides?: SicLineOverride[] | Record<string, SicLineOverride> | null;
  /**
   * No-show audit pair (logs-parity audit Chunk 7 · Part B). Server stamps
   * `no_show_at` + `no_show_by` when the row's `status` transitions into
   * 'no-show'; `no_show_reason` is the free-text justification captured in
   * the no-show modal. Cleared automatically by the server when the status
   * transitions back to 'active' / 'cancelled' / 'pending'.
   */
  no_show_at?: string | null;
  no_show_by?: string | null;
  no_show_reason?: string | null;
}

/**
 * Per-guest override applied on a SIC sub-row. Any field absent on a given
 * entry falls back to the parent row's value when the sub-row is rendered
 * (see `LogsVirtualTbody` · SIC virtual sub-row branch). PHP populates a
 * narrow subset of fields (agent / pickup_location / pickup_time /
 * pickup_remark / passenger_name / status); we surface the same fields
 * here. Unknown extra keys carried by old rows are tolerated on read but
 * NOT typed because the operator UI never edits them.
 */
export interface SicLineOverride {
  agent?: string | null;
  pickup_location?: string | null;
  pickup_time?: string | null;
  pickup_remark?: string | null;
  passenger_name?: string | null;
  status?: string | null;
}

/**
 * Normalize the wire `sic_line_overrides` value (object-keyed map or array
 * or null) into a dense `SicLineOverride[]` indexed 0..N-1. PHP's writer
 * uses the object form (`{"0": {...}}`); the modal save handler in this
 * port writes the array form. Both shapes round-trip through this helper.
 *
 * Returns an empty array when the input is null/undefined/empty so callers
 * can use `arr.length` to decide whether to show the SIC banner / chip.
 */
export function normalizeSicLineOverrides(
  raw: LogRow['sic_line_overrides'] | unknown,
): SicLineOverride[] {
  if (raw == null) return [];
  // Defensive: a server that hasn't fully decoded the JSONB might hand us
  // back the raw text. Try to parse, otherwise treat as empty.
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return normalizeSicLineOverrides(parsed);
    } catch {
      return [];
    }
  }
  if (Array.isArray(raw)) {
    return raw.filter((e): e is SicLineOverride => typeof e === 'object' && e !== null);
  }
  if (typeof raw === 'object') {
    // Object-keyed map: keys are stringified indices ("0", "1", ...).
    const entries = Object.entries(raw as Record<string, unknown>)
      .map(([k, v]) => [Number.parseInt(k, 10), v] as const)
      .filter(([k, v]) => Number.isFinite(k) && k >= 0 && v && typeof v === 'object');
    if (entries.length === 0) return [];
    const maxIdx = Math.max(...entries.map(([k]) => k));
    const out: SicLineOverride[] = new Array(maxIdx + 1).fill(null).map(() => ({}));
    for (const [k, v] of entries) {
      out[k] = v as SicLineOverride;
    }
    return out;
  }
  return [];
}

interface LogsResponse {
  page?: string;
  table?: string;
  data?: LogRow[];
  /** Optional server-supplied total row count (PostgREST Content-Range). */
  total?: number | null;
}

export interface LogsFilters {
  page?: string;
  limit?: number;
  offset?: number;
  dateFrom?: string;
  dateTo?: string;
  q?: string;
  /** Optional sector hint forwarded to the server (silently ignored if unknown). */
  sector?: string;
  /**
   * Optional sort. Wire shape mirrors the PHP `order=<col>.<dir>` param
   * forwarded as-is to PostgREST. When omitted the server falls back to
   * its built-in `service_date.asc.nullslast,pickup_time.asc` default.
   */
  order?: string;
  /**
   * Comma-separated ref / kuoni_ref tokens — server returns every logs
   * row whose `ref` or `kuoni_ref` ilike-matches any token. Powers the
   * Guest Timeline drawer (`LogsGuestTimelineDrawer`). Mirrors PHP
   * `refs_or` deep-link (data.php:155-183). Server caps to 10 tokens and
   * silently drops tokens < 4 chars or containing PostgREST metachars.
   */
  refsOr?: string;
  /**
   * Excel-style per-column filter map (Chunk 4 B8 / G5). Keys are column
   * slugs (e.g. `agent`, `supplier`); values are arrays of distinct values
   * the operator ticked in the funnel dropdown. Forwarded on the wire as
   * `cf_in_<col>=v1||v2||v3` so the server can translate into a PostgREST
   * `<col>=in.(v1,v2,v3)` filter. Empty arrays are dropped.
   */
  columnFilters?: Record<string, string[]>;
}

/** Distinct-value bucket for the per-column filter dropdown. */
export interface ColumnDistinctValue {
  value: string;
  count: number;
}

export interface ListLogsResult {
  rows: LogRow[];
  /** Total matching rows when the server reports it; null otherwise. */
  total: number | null;
}

export async function listLogs(
  filters: LogsFilters,
  signal?: AbortSignal,
): Promise<LogRow[]> {
  const out = await listLogsPaged(filters, signal);
  return out.rows;
}

/**
 * Paged sibling of `listLogs`. Returns both the data and the server's
 * total-count hint when present, so toolbars can render real
 * `Showing X-Y of Z` strings. The legacy `listLogs(...)` keeps the bare
 * `LogRow[]` shape so existing callers (dashboards, widgets) need no
 * touching during Chunk 1.
 */
export async function listLogsPaged(
  filters: LogsFilters,
  signal?: AbortSignal,
): Promise<ListLogsResult> {
  const qs = new URLSearchParams();
  qs.set('page', filters.page ?? 'logs-all');
  qs.set('limit', String(filters.limit ?? 100));
  qs.set('offset', String(filters.offset ?? 0));
  if (filters.dateFrom) qs.set('date_from', filters.dateFrom);
  if (filters.dateTo) qs.set('date_to', filters.dateTo);
  if (filters.q) qs.set('q', filters.q);
  if (filters.order) qs.set('order', filters.order);
  if (filters.refsOr) qs.set('refs_or', filters.refsOr);
  if (filters.columnFilters) {
    for (const [col, values] of Object.entries(filters.columnFilters)) {
      if (!Array.isArray(values) || values.length === 0) continue;
      // PHP wire shape: `cf_in_<col>=v1||v2`. Pipe separator (not comma)
      // because logs values can legitimately contain commas — e.g. an
      // agent display name like "Doe, John" — and the server splits on
      // `||` before reassembling into PostgREST `in.()`.
      qs.set(`cf_in_${col}`, values.join('||'));
    }
  }
  // apiFetchRaw (NOT apiFetch): apiFetch auto-unwraps {ok,data} to `data`,
  // discarding the sibling `total` the server sends — which silently broke
  // every pager that relies on total (page count collapses to 1). Raw keeps
  // the full envelope so `total` survives.
  const res = await apiFetchRaw<LogsResponse | LogRow[]>(`/coportal/logs?${qs}`, {
    method: 'GET',
    signal,
  });
  if (Array.isArray(res)) return { rows: res, total: null };
  const total =
    typeof res?.total === 'number' && Number.isFinite(res.total)
      ? res.total
      : null;
  return { rows: res?.data ?? [], total };
}

// ─── Logs — per-column distinct values (funnel dropdown) ──────────────
//
// PHP source: assets/js/table.js `_toggleColDropdown` → `?action=distinct
// &page=<slug>&col=<col>&date_from=&date_to=`. The server already
// respects the date range + every other active cf_in_<col> filter, so
// the dropdown only shows values that would narrow the result set
// further.
//
// React side: parent passes the same `dateFrom` / `dateTo` it sent to
// `listLogsPaged` plus the column slug. Result is capped at 200 entries
// server-side; the dropdown's search input handles high-cardinality
// columns (supplier, agent) when the cap is hit.
export interface ListColumnDistinctOptions {
  page: string;
  col: string;
  dateFrom?: string;
  dateTo?: string;
  /**
   * Other active column filters — forwarded as `cf_in_<col>=v1||v2` so
   * the distinct list narrows alongside the table. The column being
   * enumerated is filtered out by the server before computing distinct
   * values (otherwise picking value A would hide every other A-eligible
   * row's competing values).
   */
  columnFilters?: Record<string, string[]>;
  /**
   * Optional substring filter — used by the pickup/dropoff autocomplete
   * (logs-parity audit Chunk 6 · E7). When set, the endpoint pushes an
   * ilike(*<q>*) filter into PostgREST so the row-scan stays narrow.
   */
  q?: string;
  /**
   * Optional cap on returned values. Default 200 (funnel dropdown). The
   * autocomplete passes 20 to match PHP's `top 20` contract.
   */
  limit?: number;
}

export async function listColumnDistinct(
  opts: ListColumnDistinctOptions,
  signal?: AbortSignal,
): Promise<ColumnDistinctValue[]> {
  const qs = new URLSearchParams();
  qs.set('page', opts.page);
  qs.set('col', opts.col);
  if (opts.dateFrom) qs.set('date_from', opts.dateFrom);
  if (opts.dateTo) qs.set('date_to', opts.dateTo);
  if (opts.q) qs.set('q', opts.q);
  if (opts.limit) qs.set('limit', String(opts.limit));
  if (opts.columnFilters) {
    for (const [col, values] of Object.entries(opts.columnFilters)) {
      if (!Array.isArray(values) || values.length === 0) continue;
      if (col === opts.col) continue;
      qs.set(`cf_in_${col}`, values.join('||'));
    }
  }
  // NOTE: apiFetchRaw (NOT apiFetch). The endpoint envelope is
  // `{ ok, values }` — apiFetch auto-unwraps `.data`, which is undefined
  // here, so it silently returned [] for every column (the funnel showed
  // no values + search did nothing). apiFetchRaw hands back the raw body.
  const res = await apiFetchRaw<{ ok: boolean; values?: ColumnDistinctValue[] }>(
    `/coportal/logs/distinct?${qs}`,
    { method: 'GET', signal },
  );
  return Array.isArray(res?.values) ? res.values : [];
}

// ─── Logs — modified-field map (red-outline indicators) ───────────────
//
// Mirrors PHP `api/data.php?action=mod_fields&table=logs&ids=...`.
// React calls this once per logs-page fetch with the visible row IDs so
// the table can paint a red 1.5px outline (+ tooltip) on every <td> that
// was approved-edited by another user. Agents see an empty map (server
// short-circuits).
//
// Wire shape: { [rowId]: { [columnName]: { modifiedBy, modifiedAt, oldValue } } }
// Server folds the rows so the client doesn't have to parse JsonElement.
export interface LogModFieldEntry {
  modifiedBy: string | null;
  modifiedAt: string | null;
  oldValue: string | null;
}

export type LogModFieldsMap = Record<
  string,
  Record<string, LogModFieldEntry>
>;

export async function listLogModifications(
  args: { table?: string; ids: ReadonlyArray<string> },
  signal?: AbortSignal,
): Promise<LogModFieldsMap> {
  if (!args.ids || args.ids.length === 0) return {};
  const qs = new URLSearchParams();
  qs.set('table', args.table ?? 'logs');
  qs.set('ids', args.ids.filter((id) => !!id).join(','));
  try {
    const res = await apiFetchRaw<{ ok?: boolean; data?: LogModFieldsMap }>(
      `/coportal/logs/modifications?${qs}`,
      { method: 'GET', signal },
    );
    return res?.data ?? {};
  } catch {
    // Endpoint is best-effort decoration — never block the table render.
    return {};
  }
}

// ─── Logs — per-row badge fan-outs (issues / reminders / attachments) ─
//
// Chunk 3 of the logs-parity audit. PHP `table.js` fires three batched
// fetches when a logs page renders so each row can render the small
// secondary badges next to the passenger name:
//   · ⚠ open-issue pill — count + latest title for tooltip
//   · 🔔 reminder pill   — list of active reminder texts
//   · 📎 attachment count — drives the shared-attachments drawer onClick
//
// Server endpoint sources live in `LogsBadgesController.cs`. Each call
// is keyed off the visible ref list, capped at 200 refs server-side so
// a hand-crafted URL can't fan out an unbounded `in.()` clause.
//
// Every response degrades to an empty map when the network fails — the
// badges are decoration, not blocking state, and PHP behaves the same.

export interface LogsIssueSummary {
  count: number;
  latestTitle?: string;
  latestSummary?: string;
}

export interface LogsReminderEntry {
  id?: string;
  text: string;
  due_date?: string;
  remind_at?: string;
}

export type LogsIssuesByRef = Record<string, LogsIssueSummary>;
export type LogsRemindersByRef = Record<string, LogsReminderEntry[]>;
export type LogsAttachmentCountsByRef = Record<string, number>;

function dedupedRefsQS(refs: ReadonlyArray<string>): URLSearchParams | null {
  // Keep ref tokens above a minimum length so the server's `in.()` clause
  // doesn't waste cycles on degenerate single-character tokens; mirrors
  // the same floor `searchLogsByRef` uses for the timeline drawer.
  const seen = new Set<string>();
  for (const r of refs) {
    const t = (r ?? '').trim();
    if (t.length >= 4) seen.add(t);
  }
  if (seen.size === 0) return null;
  // Cap matches the server's `MaxRefs = 200`; trimming client-side here
  // means the URL stays under common proxy header caps even on large
  // page sizes.
  const qs = new URLSearchParams();
  qs.set('refs', [...seen].slice(0, 200).join(','));
  return qs;
}

export async function listIssuesByRef(
  refs: ReadonlyArray<string>,
  signal?: AbortSignal,
): Promise<LogsIssuesByRef> {
  const qs = dedupedRefsQS(refs);
  if (!qs) return {};
  try {
    const res = await apiFetchRaw<{ ok?: boolean; data?: LogsIssuesByRef }>(
      `/coportal/logs/issues-by-ref?${qs}`,
      { method: 'GET', signal },
    );
    return res?.data ?? {};
  } catch {
    return {};
  }
}

export async function listRemindersByRef(
  refs: ReadonlyArray<string>,
  signal?: AbortSignal,
): Promise<LogsRemindersByRef> {
  const qs = dedupedRefsQS(refs);
  if (!qs) return {};
  try {
    const res = await apiFetchRaw<{ ok?: boolean; data?: LogsRemindersByRef }>(
      `/coportal/logs/reminders-by-ref?${qs}`,
      { method: 'GET', signal },
    );
    return res?.data ?? {};
  } catch {
    return {};
  }
}

export async function listAttachmentCountsByRef(
  refs: ReadonlyArray<string>,
  signal?: AbortSignal,
): Promise<LogsAttachmentCountsByRef> {
  const qs = dedupedRefsQS(refs);
  if (!qs) return {};
  try {
    const res = await apiFetchRaw<{
      ok?: boolean;
      data?: LogsAttachmentCountsByRef;
    }>(`/coportal/logs/attachment-counts-by-ref?${qs}`, {
      method: 'GET',
      signal,
    });
    return res?.data ?? {};
  } catch {
    return {};
  }
}

// ─── Logs — shared attachments drawer (per-ref) ───────────────────────
//
// Powers the 📎 click-through on each row. Returns the small attachment
// metadata list a drawer needs to render filenames + sizes + uploader
// without a separate detail fetch. PHP `Portal._showSharedAttachments`
// hits `api/attachments.php?action=list&ref=…` for the same shape.
export interface LogsSharedAttachment {
  id: string | null;
  ref: string | null;
  original_name: string | null;
  filename: string | null;
  file_url: string | null;
  file_type: string | null;
  file_size: number | null;
  uploaded_by: string | null;
  shared_with_guests: boolean | null;
  created_at: string | null;
}

export async function listSharedAttachmentsByRef(
  ref: string,
  signal?: AbortSignal,
): Promise<LogsSharedAttachment[]> {
  const clean = ref.trim();
  if (!clean) return [];
  const qs = new URLSearchParams();
  qs.set('ref', clean);
  qs.set('limit', '500');
  try {
    const res = await apiFetchRaw<{
      ok?: boolean;
      data?: LogsSharedAttachment[];
    }>(`/coportal/attachments?${qs}`, { method: 'GET', signal });
    return res?.data ?? [];
  } catch {
    return [];
  }
}

// ─── Logs — guest-timeline search (per-ref drawer) ────────────────────
//
// Returns every logs row whose `ref` or `kuoni_ref` matches the union of
// the supplied ref tokens. Powers `LogsGuestTimelineDrawer`. The .NET
// `LogsController.List` honours the same `refs_or` token list (server-
// side cap: 10 tokens, min length 4). Mirrors PHP
// `api/data.php?action=fetch&page=search&refs_or=...`.
//
// Wide date range on purpose — the drawer is "every service for this
// guest" across both arrival and return legs, and operators often
// open it on a row whose sibling is months away (e.g. a January arrival
// and a February departure share the same `ref`).
export async function searchLogsByRef(
  refs: ReadonlyArray<string>,
  signal?: AbortSignal,
): Promise<LogRow[]> {
  const tokens = refs
    .map((r) => r.trim())
    .filter((r) => r.length >= 4);
  if (tokens.length === 0) return [];
  const qs = new URLSearchParams();
  qs.set('page', 'logs-all');
  qs.set('limit', '500');
  qs.set('offset', '0');
  qs.set('refs_or', tokens.join(','));
  // No date filter — the drawer scopes by ref, not by date.
  const res = await apiFetch<LogsResponse | LogRow[]>(`/coportal/logs?${qs}`, {
    method: 'GET',
    signal,
  });
  if (Array.isArray(res)) return res;
  return res?.data ?? [];
}

// ─── Welcome meetings (dashboard widget) ──────────────────────────────
//
// PHP source `coportal/api/welcome-meetings.php` is the historical
// endpoint. Salvaged .NET surface is still a stub on the API side
// (SimpleCrudControllers.cs covers welcome_meetings via a generic CRUD
// route once it's wired). Until then we hit the conventional
// `/coportal/welcome-meetings` slug — when it returns 404/501 the widget
// degrades to a quiet empty state (caught in the page).

// Wire shape mirrors the C# `WelcomeMeeting` model after the iter-3
// BUG-QAr2-7 live-schema audit. Live `welcome_meetings` is 14 columns:
//   id, agent, atma_ref, kuoni_ref, wm_date, wm_time, guest_name,
//   wm_location, cs_to_meet, report, sectors, reported_by,
//   created_at, updated_at.
// The iter-2 hotfix kept four phantom names alive for back-compat
// (language/guide/status/notes) — none of them exist server-side. They
// are removed here in lockstep with `WelcomeMeetingModelTests` (which
// pins the JsonPropertyName set on the C# model so a future regression
// re-introducing `meeting_date` / `status` fails the build).
export interface WelcomeMeeting {
  id: string | null;
  wm_date: string | null;
  wm_time: string | null;
  guest_name: string | null;
  atma_ref: string | null;
  kuoni_ref: string | null;
  agent: string | null;
  wm_location: string | null;
  cs_to_meet: string | null;
  report: string | null;
  sectors: string | null;
  reported_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  /**
   * Page list only (`?include_logs=true`): a log row whose WM was set inline
   * (wm_date on the log) with no welcome_meetings row. Its id is
   * `log_<uuid>`, as in PHP, and `log_id` is the bare log id.
   */
  from_log?: boolean;
  log_id?: string | null;
}

/**
 * The Welcome Meetings page's list — every meeting, newest first, plus the
 * log-sourced ones PHP merges in (pages/welcome-meetings.php:18-63).
 * Unlike `listWelcomeMeetings` this does NOT swallow errors: the page shows
 * a retry state instead of an empty table that looks like "no meetings".
 */
export async function listWelcomeMeetingsPage(
  signal?: AbortSignal,
): Promise<WelcomeMeeting[]> {
  const data = await apiFetch<WelcomeMeeting[]>(
    '/coportal/welcome-meetings?include_logs=true',
    { method: 'GET', signal },
  );
  return data ?? [];
}

export interface WelcomeMeetingFormOptions {
  /** `file_managers` where market = CS — the CS to Meet / Reported by choices. */
  csTeam: string[];
  /** `wm_default_location` (PHP default "Hotel") — Set WM's fallback location. */
  defaultLocation: string;
}

export async function getWelcomeMeetingFormOptions(
  signal?: AbortSignal,
): Promise<WelcomeMeetingFormOptions> {
  const data = await apiFetch<WelcomeMeetingFormOptions | null>(
    '/coportal/welcome-meetings/form-options',
    { method: 'GET', signal },
  );
  return {
    csTeam: data?.csTeam ?? [],
    defaultLocation: data?.defaultLocation || 'Hotel',
  };
}

/** PHP `clear_wm`: removes the WM from its linked log rows and deletes it. */
export async function clearWelcomeMeeting(id: string): Promise<void> {
  await apiFetch<void>(
    `/coportal/welcome-meetings/${encodeURIComponent(id)}/clear`,
    { method: 'POST' },
    { mutation: true },
  );
}

/**
 * "No need": `logs.wm_status = 'not_needed'` on the log (and, from the
 * drawer, clears the meeting too) so the arrival leaves "WM to set" for good.
 */
export async function dismissWelcomeMeeting(req: {
  logId?: string | null;
  meetingId?: string | null;
}): Promise<void> {
  await apiFetch<void>(
    '/coportal/welcome-meetings/dismiss',
    {
      method: 'POST',
      body: JSON.stringify({ LogId: req.logId ?? null, MeetingId: req.meetingId ?? null }),
    },
    { mutation: true },
  );
}

export async function listWelcomeMeetings(
  signal?: AbortSignal,
): Promise<WelcomeMeeting[]> {
  try {
    const data = await apiFetch<WelcomeMeeting[]>(
      '/coportal/welcome-meetings',
      { method: 'GET', signal },
    );
    return data ?? [];
  } catch {
    // The endpoint may return 501 / 404 today — render the widget empty.
    return [];
  }
}

// ─── Notes (dashboard widget) ─────────────────────────────────────────

export interface UserNote {
  id: string | null;
  title: string | null;
  content: string | null;
  color: string | null;
  pinned: boolean | null;
  sort_order: number | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Server returns `{ ok, notes }` (NotesController.cs:39). `apiFetch`
 *  blindly strips any body with an `ok` key by returning `.data`, so the
 *  custom `notes` field is silently dropped → list was always empty.
 *  Route through the canonical `apiFetchRaw` (hoisted to `@/auth/api`)
 *  to keep the full envelope; B28 audit P3
 *  (.relentless/apifetch-non-data-audit.md site #20). */
export async function listMyNotes(signal?: AbortSignal): Promise<UserNote[]> {
  try {
    const env = await apiFetchRaw<{ ok?: boolean; notes?: UserNote[] }>(
      '/notes',
      { method: 'GET', signal },
    );
    return env?.notes ?? [];
  } catch {
    return [];
  }
}

// ═══════════════════════════════════════════════════════════════════════
// Round-7 wrappers (next batch of pages).
// ═══════════════════════════════════════════════════════════════════════
//
// `apiFetch<T>` auto-unwraps `{ ok, data }` envelopes. For endpoints that
// return richer custom envelopes (e.g. issues-report with `{ ok, filters,
// kpis, monthly, ... }`) we read the raw body via `apiFetchRaw` so we get
// the full object back. `apiFetchRaw` is now the canonical helper hoisted
// to `@/auth/api` (P2 APIFETCHRAW-CONSOLIDATE-PROMOTED — was a per-module
// clone here until the same defect class hit three iters running:
// B27 / B30 / B33).
//
// IMPORTANT: the canonical `apiFetchRaw` does NOT auto-set the XSRF header
// on non-GET requests — callers must opt in with the third arg
// `{ mutation: true }` (mirrors `apiFetch`). The deleted local clone
// inferred mutation from `init.method`; the canonical helper requires the
// explicit opt-in. Every POST/PUT/PATCH/DELETE call site below passes
// `{ mutation: true }` (P5 COPORTAL-MUTATION-XSRF-HOTFIX).

// ─── Weekly arrivals ──────────────────────────────────────────────────

export interface WeeklyArrivalRow {
  id: number | null;
  period: string | null;
  year: number | null;
  sector: string | null;
  arrivals: number | null;
  pax: number | null;
  movements: number | null;
  total_arrivals: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface WeeklyArrivalsFilters {
  year?: number | null;
  period?: string | null;
}

export async function listWeeklyArrivals(
  filters: WeeklyArrivalsFilters,
  signal?: AbortSignal,
): Promise<WeeklyArrivalRow[]> {
  const qs = new URLSearchParams();
  if (filters.year) qs.set('year', String(filters.year));
  if (filters.period) qs.set('period', filters.period);
  const path = `/coportal/weekly-arrivals${qs.size ? `?${qs}` : ''}`;
  const data = await apiFetch<WeeklyArrivalRow[]>(path, {
    method: 'GET',
    signal,
  });
  return data ?? [];
}

/**
 * The PARITY view of Weekly Arrivals.
 *
 * `listWeeklyArrivals` above is a plain read of the stored `weekly_arrivals`
 * table — whatever the generator last wrote. The PHP page reads that table for
 * HISTORICAL years and recomputes the CURRENT year live from `logs` on every
 * load, which is why the two surfaces disagreed and drifted apart between
 * generations. This endpoint returns what PHP actually renders.
 */
export interface WeeklyArrivalsCell {
  arrivals: number;
  pax: number;
  movements: number;
  total_arrivals: number;
  notes: string;
  id: string | null;
}

export interface WeeklyArrivalsMatrix {
  periods: string[];
  selected_period: string | null;
  prev_period: string | null;
  years: number[];
  /** year → sector → cell. PEN/LGK already folded into KUL. */
  data: Record<string, Record<string, WeeklyArrivalsCell>>;
  matched_periods: Record<string, string>;
  /** OL is EXCLUDED — it is a subset of the sector figures, not an addition. */
  year_totals: Record<string, { arrivals: number; pax: number; total_arrivals: number }>;
  live_year: number;
  prev_week: Record<string, WeeklyArrivalsCell>;
  /** Rota slots OVERLAPPING the selected week — not equal to it. */
  hotline: WeeklyArrivalsHotlineSlot[];
}

export interface WeeklyArrivalsHotlineSlot {
  period: string | null;
  /** "Fri–Sun", resolved from the slot's own period. */
  days: string | null;
  my: string | null;
  sg: string | null;
}

export async function getWeeklyArrivalsMatrix(
  period: string | null | undefined,
  signal?: AbortSignal,
): Promise<WeeklyArrivalsMatrix> {
  const qs = new URLSearchParams();
  if (period) qs.set('period', period);
  const path = `/coportal/weekly-arrivals/matrix${qs.size ? `?${qs}` : ''}`;
  const data = await apiFetch<WeeklyArrivalsMatrix>(path, { method: 'GET', signal });
  return (
    data ?? {
      periods: [], selected_period: null, prev_period: null, years: [],
      data: {}, matched_periods: {}, year_totals: {}, live_year: 0, prev_week: {},
      hotline: [],
    }
  );
}

export interface WeeklyArrivalsPeriod {
  period: string;
  year: number;
}

export async function listWeeklyArrivalsPeriods(
  year: number | null,
  signal?: AbortSignal,
): Promise<WeeklyArrivalsPeriod[]> {
  const qs = new URLSearchParams();
  if (year) qs.set('year', String(year));
  const path = `/coportal/weekly-arrivals/periods${qs.size ? `?${qs}` : ''}`;
  const data = await apiFetch<WeeklyArrivalsPeriod[]>(path, {
    method: 'GET',
    signal,
  });
  return data ?? [];
}

export interface GenerateWeeklyArrivalsRequest {
  period: string;
  year?: number | null;
}

export async function generateWeeklyArrivals(
  req: GenerateWeeklyArrivalsRequest,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/coportal/weekly-arrivals/generate',
    {
      method: 'POST',
      body: JSON.stringify({ Period: req.period, Year: req.year ?? null }),
    },
    { mutation: true },
  );
}

export async function deleteWeeklyArrivalsPeriod(
  period: string,
  year: number,
): Promise<void> {
  const qs = new URLSearchParams({ period, year: String(year) });
  await apiFetch<void>(
    `/coportal/weekly-arrivals?${qs}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

export interface WeeklyArrivalsSaveRow {
  sector: string;
  arrivals: number;
  pax: number;
  notes?: string | null;
}

export async function saveWeeklyArrivalsYear(
  period: string,
  year: number,
  data: WeeklyArrivalsSaveRow[],
): Promise<void> {
  await apiFetch<unknown>(
    '/coportal/weekly-arrivals/save-year',
    {
      method: 'POST',
      body: JSON.stringify({
        Period: period,
        Year: year,
        Data: data.map((d) => ({
          Sector: d.sector,
          Arrivals: d.arrivals,
          Pax: d.pax,
          Notes: d.notes ?? null,
        })),
      }),
    },
    { mutation: true },
  );
}

export async function sendWeeklyArrivalsReport(
  period: string,
): Promise<{ ok: boolean; recipient?: string; error?: string; mailSuppressed?: boolean }> {
  // Server returns `{ ok, recipient }` on success (no `data` key —
  // WeeklyArrivalsController.cs:453). `apiFetch` would strip the body
  // by reading `.data` → undefined and the UI could not show the
  // recipient. Use `apiFetchRaw` to keep the full envelope intact.
  // B28 audit P3 (.relentless/apifetch-non-data-audit.md site #21).
  return apiFetchRaw<{ ok: boolean; recipient?: string; error?: string; mailSuppressed?: boolean }>(
    '/coportal/weekly-arrivals/send-report',
    {
      method: 'POST',
      body: JSON.stringify({ Period: period }),
    },
    { mutation: true },
  );
}

// ─── Flight check ─────────────────────────────────────────────────────

export interface FlightCheckStatus {
  ok: boolean;
  configured: boolean;
  host: string | null;
  note: string | null;
}

export async function getFlightCheckStatus(
  signal?: AbortSignal,
): Promise<FlightCheckStatus> {
  return apiFetchRaw<FlightCheckStatus>('/coportal/flight-check/status', {
    method: 'GET',
    signal,
  });
}

export interface FlightCheckRow {
  id: string | null;
  ref: string | null;
  line_ref: string | null;
  service_date: string | null;
  passenger_name: string | null;
  pax: number | null;
  flight: string | null;
  pickup_time: string | null;
  pickup_location: string | null;
  dropoff_location: string | null;
  log: string | null;
  sector: string | null;
  match_status: string | null;
  agent: string | null;
  supplier: string | null;
  product: string | null;
}

export interface FlightCheckRowsFilters {
  dateFrom: string;
  dateTo: string;
  log?: string;
}

export async function listFlightCheckRows(
  filters: FlightCheckRowsFilters,
  signal?: AbortSignal,
): Promise<FlightCheckRow[]> {
  const qs = new URLSearchParams({
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
  });
  if (filters.log) qs.set('log', filters.log);
  const res = await apiFetchRaw<{ data?: FlightCheckRow[] }>(
    `/coportal/flight-check/rows?${qs}`,
    { method: 'GET', signal },
  );
  return res?.data ?? [];
}

export interface FlightCheckResult {
  status: string;
  label: string;
  api_airport: string | null;
  api_time: string | null;
  input_time: string | null;
  check_iata: string | null;
  is_dep: boolean;
  flight_num: string | null;
  tried: string[] | null;
  /** "KUL T1", "KUL T2 (klia2)": shown beside the result, never saved. */
  terminal?: string | null;
}

export interface FlightCheckRequest {
  flight: string;
  date: string;
  pickup?: string | null;
  dropoff?: string | null;
  time?: string | null;
}

export async function checkFlight(
  req: FlightCheckRequest,
): Promise<FlightCheckResult> {
  return apiFetchRaw<FlightCheckResult>(
    '/coportal/flight-check/check',
    {
      method: 'POST',
      body: JSON.stringify({
        Flight: req.flight,
        Date: req.date,
        Pickup: req.pickup ?? null,
        Dropoff: req.dropoff ?? null,
        Time: req.time ?? null,
      }),
    },
    { mutation: true },
  );
}

export async function saveFlightCheckResult(
  id: string,
  result: string,
): Promise<void> {
  await apiFetchRaw<{ ok: boolean }>(
    '/coportal/flight-check/save',
    {
      method: 'POST',
      body: JSON.stringify({ Id: id, Result: result }),
    },
    { mutation: true },
  );
}

export interface FlightCheckEmailRow {
  service_date: string | null;
  sector: string | null;
  flight: string | null;
  pickup_time: string | null;
  pickup_location: string | null;
  dropoff_location: string | null;
  passenger_name: string | null;
  ref: string | null;
  result_status: string | null;
  result_label: string | null;
}

export interface FlightCheckEmailPayload {
  date_from: string;
  date_to: string;
  log?: string;
  rows: FlightCheckEmailRow[];
}

export interface FlightCheckEmailResult {
  ok: boolean;
  message?: string;
  to?: string;
  cc?: string;
  email_warning?: string;
}

// Emails the flight-check board to the CS/OPS distribution list (T1-07).
// Server resolves recipients from portal_settings and returns the full
// envelope — including a soft `email_warning` on failure (HTTP 200) rather
// than throwing — so we read it via `apiFetchRaw` to keep it intact.
export async function emailFlightCheckReport(
  payload: FlightCheckEmailPayload,
): Promise<FlightCheckEmailResult> {
  return apiFetchRaw<FlightCheckEmailResult>(
    '/coportal/flight-check/email',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    { mutation: true },
  );
}

// ─── No-show (PDF) ────────────────────────────────────────────────────

export function noShowReportPdfUrl(
  dateFrom: string,
  dateTo: string,
  sector?: string,
): string {
  const qs = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
  if (sector) qs.set('sector', sector);
  return `/api/coportal/no-show/report-pdf?${qs}`;
}

// ─── No-show (table view from logs) ───────────────────────────────────
//
// The no-show.php page queries `logs` where status='no-show' OR an
// ops/cs no-show report has been filled. We re-use the generic logs
// endpoint to mirror this view, applying the same OR-filter client-side.

export interface NoShowRow extends LogRow {
  ops_noshow_report?: string | null;
  cs_noshow_report?: string | null;
  /**
   * Shared multi-image pool. Ground-truthed 2026-09-10 against
   * `coportal/setup/migration.sql:518-521` and the live modal in
   * `pages/_table.php:1122-1262` — it is ONE pool for both sides, with
   * `noshow_images_cs` naming the subset that also appears in the CS report.
   * `ops_noshow_images` / `cs_noshow_images` are the pre-split legacy columns,
   * still read for backwards compatibility (PHP merges them when the new pool
   * is empty) and never written.
   */
  noshow_images?: string[] | null;
  noshow_images_cs?: string[] | null;
  ops_noshow_images?: string[] | null;
  cs_noshow_images?: string[] | null;
}

/**
 * Replay desk notifications that were never sent, for both the queued
 * modifications and the no-show reports affected by the unported/dead mail
 * paths. Admin only; the server decides what is eligible.
 */
export async function resendUnsentNotifications(): Promise<{
  pending: number;
  noshow: number;
  bookings: string[];
}> {
  // apiFetchRaw, NOT apiFetch: the latter unwraps the `{ ok, data }`
  // envelope and returns `data`, so every sibling field — ok, sent,
  // bookings — is stripped before the caller sees it. Reading `.ok` off the
  // unwrapped body gave undefined, and the guard below reported "Resend
  // failed" on a run that had in fact sent 13 emails.
  const [noshow, pending] = await Promise.all([
    apiFetchRaw<{ ok?: boolean; sent?: number; bookings?: string[]; error?: string }>(
      '/coportal/modifications/resend-noshow-notifications',
      { method: 'POST' },
      { mutation: true },
    ),
    apiFetchRaw<{ ok?: boolean; sent?: number; error?: string }>(
      '/coportal/modifications/resend-pending-notifications',
      { method: 'POST' },
      { mutation: true },
    ),
  ]);
  if (!noshow?.ok && !pending?.ok)
    throw new Error(noshow?.error || pending?.error || 'Resend failed.');
  return {
    pending: pending?.sent ?? 0,
    noshow: noshow?.sent ?? 0,
    bookings: noshow?.bookings ?? [],
  };
}

/**
 * Draft the CS no-show report from the OPS one. Mirrors PHP
 * `api/generate-noshow-report.php` — the server re-reads the booking facts
 * from the row, so only the OPS text and the row id are sent.
 *
 * Returns the draft text ONLY; nothing is persisted. The operator reviews it
 * in the textarea and saves like any hand-written report.
 */
export async function generateNoShowCsReport(
  rowId: string,
  opsReport: string,
  signal?: AbortSignal,
): Promise<string> {
  const res = await apiFetch<{ ok?: boolean; report?: string; error?: string }>(
    '/coportal/no-show/generate-report',
    {
      method: 'POST',
      body: JSON.stringify({ rowId, opsReport }),
      signal,
    },
    { mutation: true },
  );
  if (!res?.ok || !res.report) throw new Error(res?.error || 'Could not generate the report.');
  return res.report;
}

/**
 * Upload one no-show photo. Mirrors PHP `api/upload-noshow-image.php`; the
 * .NET endpoint accepts the same `image` field name and answers
 * `{ ok, url, filename }`. The caller then PATCHes the logs row with the
 * returned URL — the upload itself is not tied to a row.
 */
export async function uploadNoShowImage(file: File): Promise<string> {
  const form = new FormData();
  form.append('image', file);
  // No Content-Type header: the browser must set the multipart boundary
  // itself (see the `typeof init.body === 'string'` guard in auth/api.ts —
  // setting it manually is what broke every upload before that fix).
  const res = await apiFetchRaw<{ ok?: boolean; url?: string; error?: string }>(
    '/coportal/no-show/upload-image',
    { method: 'POST', body: form },
    { mutation: true },
  );
  if (!res?.url) throw new Error(res?.error || 'Upload failed.');
  return res.url;
}

/**
 * A no-show line, by the PHP page's rule: the status, or either report
 * present. Untrimmed on purpose: a whitespace-only report still lists the
 * line (it then reads N/A, not Ready), as no-show.php does.
 */
export function isNoShowLine(r: Pick<NoShowRow, 'status' | 'ops_noshow_report' | 'cs_noshow_report'>): boolean {
  const status = (r.status ?? '').toLowerCase();
  return (
    status === 'no-show' ||
    status === 'no_show' ||
    (r.ops_noshow_report ?? '').length > 0 ||
    (r.cs_noshow_report ?? '').length > 0
  );
}

/**
 * A no-show still owed its CS report: the same rule the CS/OPS rail pill
 * counts (NavBadgesController: status no-show, CS report NULL or blank).
 */
export function awaitsCsReport(r: Pick<NoShowRow, 'status' | 'cs_noshow_report'>): boolean {
  const status = (r.status ?? '').toLowerCase();
  return (status === 'no-show' || status === 'no_show') && (r.cs_noshow_report ?? '').trim().length === 0;
}

/** The logs endpoint's per-request ceiling (LogsController.List clamps to 500). */
const LOGS_PAGE_SIZE = 500;
/** Safety stop: 20 pages = 10,000 no-show lines, far beyond any real range. */
const NO_SHOW_MAX_PAGES = 20;

/**
 * The No-show page's lines, from the server's own `no-show` scope
 * (LogsPageConfig: status no-show OR an OPS / CS report written), so the
 * filtering happens in SQL and the row budget is spent on no-shows only.
 *
 * Until 2026-10-01 this read `page=logs-all` with `limit=1000` and picked the
 * no-shows out in the browser. The server clamps every request to 500 rows,
 * newest service date first, and a 30-day window holds ~1,400 lines: the page
 * only ever saw the last ~11 days, so older no-shows (and the one the rail pill
 * was counting) never appeared. It now pages until the server runs out.
 */
export async function listNoShowRows(
  filters: { dateFrom?: string; dateTo?: string; sector?: string },
  signal?: AbortSignal,
): Promise<NoShowRow[]> {
  const out: NoShowRow[] = [];
  for (let pageNo = 0; pageNo < NO_SHOW_MAX_PAGES; pageNo += 1) {
    const qs = new URLSearchParams({
      page: 'no-show',
      limit: String(LOGS_PAGE_SIZE),
      offset: String(pageNo * LOGS_PAGE_SIZE),
    });
    if (filters.dateFrom) qs.set('date_from', filters.dateFrom);
    if (filters.dateTo) qs.set('date_to', filters.dateTo);
    const res = await apiFetch<{ data?: NoShowRow[] } | NoShowRow[]>(
      `/coportal/logs?${qs}`,
      { method: 'GET', signal },
    );
    const batch = Array.isArray(res) ? res : (res?.data ?? []);
    out.push(...batch);
    if (batch.length < LOGS_PAGE_SIZE) break;
  }
  return out.filter((r) => {
    const sectorMatch =
      !filters.sector ||
      (r.sector ?? '').toUpperCase() === filters.sector.toUpperCase();
    // The server scope applies the same rule; the re-check also drops an
    // empty-string report, which `not.is.null` lets through.
    return isNoShowLine(r) && sectorMatch;
  });
}

// ─── Paging boards ────────────────────────────────────────────────────

/**
 * One SIC-grouped paging board (ref+flight+time+date). Mirrors the
 * enriched `cards` shape from PagingBoardsController. `guest` may contain
 * newlines for multi-guest (SIC) boards — each line is "Name (pax)".
 * Editor-only fields (logo2Url, noLogo, logoBig, custom) are optional and
 * default sensibly when absent.
 */
export interface PagingBoardCard {
  idx: number;
  /**
   * Stable board identity — `{ref}|{flight}|{time}|{date}`, the server's own
   * grouping key. Saved edits key on THIS, never on `idx`: `idx` is a
   * position in the generated list and shifts whenever a log row changes,
   * so an edit stored against it would re-point at a different guest's
   * board on the next load.
   *
   * Optional because a custom (hand-made) board has no generated
   * counterpart and therefore no board key.
   */
  boardKey?: string;
  region: string;
  agent: string;
  guest: string;
  pax: number;
  flight: string;
  pickup: string;
  dropoff: string;
  time: string;
  date: string;
  ref: string;
  sector: string;
  supplier: string;
  vip: boolean;
  vipAgent: boolean;
  logoUrl: string;
  pagingLogo: string;
  logoOnly: boolean;
  returnArrival: boolean;
  // Editor / custom-board extras (Phase 2)
  logo2Url?: string;
  noLogo?: boolean;
  logoBig?: boolean;
  custom?: boolean;
}

export interface PagingKliaRow {
  id: string | null;
  terminal: 'KLIA1' | 'KLIA2';
  serviceDate: string | null;
  time: string | null;
  ref: string | null;
  agent: string | null;
  guest: string | null;
  pax: number | null;
  flight: string | null;
  pickup: string | null;
  dropoff: string | null;
  supplier?: string | null;
  opsRemarks?: string | null;
  vip: boolean;
}

export interface PagingBoardsResponse {
  ok: boolean;
  dateFrom: string;
  dateTo: string;
  rangeLabel: string;
  cards: PagingBoardCard[];
  total: number;
  sectorCounts: Record<string, number>;
  suppliers: string[];
  fallbackLogo: string;
  allAgents: { name: string; logo: string }[];
  klia: { counts: { KLIA1: number; KLIA2: number }; rows: PagingKliaRow[] };
  /**
   * Saved edits, by `boardKey`. Returned SEPARATELY from `cards` rather than
   * merged into them, so the page can mark a board as edited and offer
   * "reset to generated" instead of silently presenting an override as if it
   * came from the logs.
   */
  overrides?: Record<string, PagingBoardCard>;
  /** Hand-made boards with no generated counterpart. */
  customBoards?: SavedCustomBoard[];
}

export interface SavedCustomBoard {
  id: string;
  serviceDate: string | null;
  card: PagingBoardCard;
}

/** Persist an edit to a generated board. Upserts on `boardKey`. */
export async function savePagingOverride(
  boardKey: string,
  card: PagingBoardCard,
): Promise<void> {
  await apiFetch<unknown>(
    '/coportal/paging-boards/save',
    {
      method: 'POST',
      body: JSON.stringify({
        BoardKey: boardKey,
        IsCustom: false,
        ServiceDate: card.date || null,
        Card: card,
      }),
    },
    { mutation: true },
  );
}

/** Create (no id) or update (id) a hand-made board. */
export async function savePagingCustomBoard(
  card: PagingBoardCard,
  id?: string | null,
): Promise<void> {
  await apiFetch<unknown>(
    '/coportal/paging-boards/save',
    {
      method: 'POST',
      body: JSON.stringify({
        Id: id ?? null,
        IsCustom: true,
        ServiceDate: card.date || null,
        Card: card,
      }),
    },
    { mutation: true },
  );
}

/**
 * Drop a saved board — an override reverts to generated, a custom board is
 * deleted. POST rather than DELETE because `boardKey` contains `|` and may
 * contain `/`, which does not survive a route segment cleanly.
 */
export async function resetPagingBoard(
  target: { boardKey?: string; id?: string },
): Promise<void> {
  await apiFetch<unknown>(
    '/coportal/paging-boards/reset',
    {
      method: 'POST',
      body: JSON.stringify({
        BoardKey: target.boardKey ?? null,
        Id: target.id ?? null,
      }),
    },
    { mutation: true },
  );
}

export async function getPagingBoards(
  dateFrom: string,
  dateTo: string,
  signal?: AbortSignal,
): Promise<PagingBoardsResponse> {
  const qs = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
  return apiFetchRaw<PagingBoardsResponse>(
    `/coportal/paging-boards?${qs}`,
    { method: 'GET', signal },
  );
}

// Per-ref attachments shown on each paging board (📎 chips) + share toggle.
export interface PagingAttachment {
  id: string;
  ref: string | null;
  originalName?: string | null;
  original_name?: string | null;
  filename?: string | null;
  fileUrl?: string | null;
  file_url?: string | null;
  fileType?: string | null;
  file_type?: string | null;
  sharedWithGuests?: boolean | null;
  shared_with_guests?: boolean | null;
}

export async function getPagingAttachmentsByRefs(
  refs: string[],
  signal?: AbortSignal,
): Promise<PagingAttachment[]> {
  const list = [...new Set(refs.filter(Boolean))];
  if (list.length === 0) return [];
  const qs = new URLSearchParams({ refs: list.join(',') });
  const res = await apiFetchRaw<{ ok: boolean; data: PagingAttachment[] }>(
    `/coportal/attachments/by-refs?${qs}`,
    { method: 'GET', signal },
  );
  return res.data ?? [];
}

export async function togglePagingAttachmentShare(
  id: string,
  shared: boolean,
): Promise<void> {
  await apiFetchRaw(
    `/coportal/attachments/${id}/share`,
    { method: 'POST', body: JSON.stringify({ shared }) },
    { mutation: true },
  );
}

// ─── Issues report ────────────────────────────────────────────────────

export type IssuesReportMode = 'market' | 'agent' | 'compare';

export interface IssuesReportKpis {
  total: number;
  open: number;
  resolved: number;
  critical: number;
  /** Share of `total` that reached a closed state. */
  resolvedPct: number;
  /** Average over ACTIVE months only — a 4-month year isn't divided by 12. */
  avgPerMonth: number;
  /** `total` minus the previous year's. Null when there's nothing to compare. */
  deltaVsPrev: number | null;
  /** The year `deltaVsPrev` compares against, or null in "all years" mode. */
  prevYear: string | null;
}

/** One name-and-count row — leaderboards, market split, cause breakdown. */
export interface IssuesReportCount {
  name: string;
  count: number;
}

/**
 * The controllable / external split. `configured` is false when no
 * `issue_cause_groups` setting exists, in which case EVERY cause falls to
 * "external" by default and the numbers describe an unset setting rather
 * than the operation — the card has to say so instead of reporting 0%.
 */
export interface IssuesReportImpact {
  controllable: number;
  external: number;
  controllablePct: number;
  externalPct: number;
  controllableCauses: { cause: string; count: number }[];
  externalCauses: { cause: string; count: number }[];
  configured: boolean;
}

export interface IssuesReportPerAgent {
  agent: string;
  total: number;
  open: number;
  resolved: number;
  critical: number;
}

export interface IssuesReportResponse {
  ok: boolean;
  filters: {
    year: string;
    market: string;
    /** Selected supplier, or `'all'`. Applies in every mode. */
    supplier: string;
    mode: IssuesReportMode;
    agents: string[];
    allMarkets: string[];
    allYears: number[];
    /** Every supplier named on an issue — the picker's full option set. */
    allSuppliers: string[];
    allAgents: string[];
  };
  kpis: IssuesReportKpis;
  monthly: { month: string; count: number }[];
  byStatus: { status: string; count: number }[];
  byImportance: { importance: number; count: number }[];
  bySector: { sector: string; count: number }[];
  topAgents: { agent: string; count: number }[];
  topSuppliers: { supplier: string; count: number }[];
  topCauses: { cause: string; count: number }[];
  perAgent: IssuesReportPerAgent[];
  /**
   * Comparison series for the monthly chart — exactly ONE of these is
   * non-null. A specific year yields 12-slot current/previous arrays (bars +
   * ghost line); "all years" has no previous year, so it yields a per-year
   * series instead and the same chart re-labels its axis.
   */
  monthlyCurrent: number[] | null;
  monthlyPrevious: number[] | null;
  yearly: { year: string; count: number }[] | null;
  /** Cause grouped by its prefix ("Hotel - Cleanliness" → "Hotel"). */
  causeCategories: { category: string; count: number }[];
  /** Every distinct cause, not just the top 10. */
  causeDetail: { cause: string; count: number }[];
  impact: IssuesReportImpact;
  byMarket: { market: string; count: number }[];
  /** Handler credited on CLOSED rows only. */
  closedBy: IssuesReportCount[];
  teamMembers: IssuesReportCount[];
}

export interface IssuesReportFilters {
  year?: string;
  market?: string;
  /** One supplier name, or `'all'` / omitted for no supplier filter. */
  supplier?: string;
  mode?: IssuesReportMode;
  agents?: string[];
}

export async function getIssuesReport(
  filters: IssuesReportFilters,
  signal?: AbortSignal,
): Promise<IssuesReportResponse> {
  const qs = new URLSearchParams();
  if (filters.year) qs.set('year', filters.year);
  if (filters.market) qs.set('market', filters.market);
  if (filters.supplier) qs.set('supplier', filters.supplier);
  if (filters.mode) qs.set('mode', filters.mode);
  if (filters.agents && filters.agents.length > 0)
    qs.set('agents', filters.agents.join(','));
  const path = `/coportal/issues-report${qs.size ? `?${qs}` : ''}`;
  return apiFetchRaw<IssuesReportResponse>(path, { method: 'GET', signal });
}

// ─── Modifications (audit log) ────────────────────────────────────────

export interface ModificationRow {
  id: string | null;
  user_id: string | null;
  user_email: string | null;
  user_name: string | null;
  table_name: string | null;
  row_id: string | null;
  action_type: string | null;
  changed_fields: unknown;
  old_values: unknown;
  new_values: unknown;
  portal_page: string | null;
  ip_address: string | null;
  status: string | null;
  /** The requester's reason for the change. */
  modification_remark: string | null;
  /** The reviewer's note on the decision (PHP approve.php). Optional: rows
   *  decided before 2026-10-21 put it in `modification_remark` instead. */
  approval_remark?: string | null;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string | null;
}

/**
 * One change, for the review page the "pending approval" email links to
 * (`GET /coportal/modifications/{id}/review`). `canDecide` is the server's
 * answer (manager, admin or the auto-approve grant; never a viewer): the page
 * shows the form only then, and the API refuses a decision either way.
 */
export interface ModificationReview {
  row: ModificationRow & { context?: Record<string, unknown> | null };
  canDecide: boolean;
  reviewer: string;
}

export async function fetchModificationReview(
  id: string,
  signal?: AbortSignal,
): Promise<ModificationReview> {
  const res = await apiFetchRaw<{
    data?: ModificationReview['row'];
    canDecide?: boolean;
    reviewer?: string;
  }>(`/coportal/modifications/${encodeURIComponent(id)}/review`, { method: 'GET', signal });
  if (!res?.data) throw new ApiError(404, 'This change could not be found.');
  return { row: res.data, canDecide: res.canDecide === true, reviewer: res.reviewer ?? '' };
}

export interface ModificationsFilters {
  status?: string;
  table?: string;
  /** `contact-links`: only the hotline's WhatsApp → contact_details links,
   *  which the default view leaves out. */
  view?: 'contact-links';
  /** Log changes view: every log modification, not only the ones touching
   *  the reviewed fields (remarks, pick-up / drop-off, time, date, supplier,
   *  product, guest, pax). Ignored by the contact-links view. */
  full?: boolean;
  /** Malaysia calendar days, `YYYY-MM-DD`, inclusive. */
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

/**
 * One page of a server-filtered list. `total` counts the SAME filtered set
 * (PostgREST `count=exact`); null when the server could not say, in which
 * case a short page is the last one.
 */
export interface ModificationsPage<T = ModificationRow> {
  rows: T[];
  total: number | null;
}

function modificationsQuery(filters: {
  status?: string; table?: string; view?: string; full?: boolean;
  from?: string; to?: string; limit?: number; offset?: number;
}): string {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.table) qs.set('table', filters.table);
  if (filters.view) qs.set('view', filters.view);
  if (filters.full) qs.set('full', 'true');
  if (filters.from) qs.set('from', filters.from);
  if (filters.to) qs.set('to', filters.to);
  if (filters.limit) qs.set('limit', String(filters.limit));
  if (filters.offset) qs.set('offset', String(filters.offset));
  return qs.size ? `?${qs}` : '';
}

export async function listModifications(
  filters: ModificationsFilters,
  signal?: AbortSignal,
): Promise<ModificationsPage> {
  const res = await apiFetchRaw<{ data?: ModificationRow[]; total?: number | null }>(
    `/coportal/modifications${modificationsQuery(filters)}`,
    { method: 'GET', signal },
  );
  return { rows: res?.data ?? [], total: res?.total ?? null };
}

/**
 * One transfer message, folded server-side from its audit row
 * (`ModificationsLogController.ToTransferMessage`).
 *   · `whatsapp`    — the WhatsApp message itself (Super Transfers send),
 *                     with the first 200 characters of its text;
 *   · `hotel_email` — the hotel e-mail, which also stamps the line;
 *   · `marked`      — `logs.transfer` set to how the guest was told
 *                     (WA-AUTOLINK, CALL, NO WA…), PHP's "done" mark.
 */
export interface TransferMessage {
  id: string | null;
  at: string | null;
  kind: 'whatsapp' | 'hotel_email' | 'marked';
  channel: 'whatsapp' | 'email' | 'call' | 'other' | 'mixed' | 'none';
  /** The stamped type (a SIC map reads "WA-AUTOLINK · CALL"), or the hotline. */
  method: string | null;
  /** The type the line carried before, when the row recorded it (PHP rows). */
  before: string | null;
  /** A phone (WhatsApp), a hotel address, "hotel" (older e-mails), or null. */
  recipient: string | null;
  /** The stored text: WhatsApp's first 200 characters, an e-mail's subject. */
  text: string | null;
  by: string | null;
  status: string | null;
  /** Written by the PHP Hub (table `transfer_messages`). */
  legacy: boolean;
  logRowId: string | null;
  ref: string | null;
  guest: string | null;
  line: string | null;
  sector: string | null;
  serviceDate: string | null;
}

export interface TransferMessagesFilters {
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export async function listTransferMessages(
  filters: TransferMessagesFilters,
  signal?: AbortSignal,
): Promise<ModificationsPage<TransferMessage>> {
  const res = await apiFetchRaw<{ data?: TransferMessage[]; total?: number | null }>(
    `/coportal/modifications/transfer-messages${modificationsQuery(filters)}`,
    { method: 'GET', signal },
  );
  return { rows: res?.data ?? [], total: res?.total ?? null };
}

// Approve / decline endpoints (manager + admin). Mirror PHP
// modifications.php buttons. Single-row endpoints return `void`;
// bulk endpoints return per-id failure list so the UI can highlight
// partial successes (e.g. "12 approved, 3 already approved").
export interface BulkApprovalOutcome {
  approved?: number;
  declined?: number;
  failed: ReadonlyArray<{ id: string; error?: string }>;
}

export async function approveModification(
  id: string,
  remark: string | null,
): Promise<void> {
  await apiFetch<void>(
    `/coportal/modifications/${encodeURIComponent(id)}/approve`,
    {
      method: 'POST',
      body: JSON.stringify({ Remark: remark }),
    },
    { mutation: true },
  );
}

export async function declineModification(
  id: string,
  remark: string | null,
): Promise<void> {
  await apiFetch<void>(
    `/coportal/modifications/${encodeURIComponent(id)}/decline`,
    {
      method: 'POST',
      body: JSON.stringify({ Remark: remark }),
    },
    { mutation: true },
  );
}

// Server returns `{ ok, approved, failed }` / `{ ok, declined, failed }`
// (ModificationsLogController.cs:139 / :161 — no `data` key). `apiFetch`
// would strip the body by reading `.data` → undefined and the UI could
// not display the per-id outcome. Use `apiFetchRaw` to keep the counts
// and the `failed` list intact. B28 audit P3 sites #22 + #23.
export async function approveModificationsMany(
  ids: string[],
  remark: string | null = null,
): Promise<BulkApprovalOutcome> {
  return apiFetchRaw<BulkApprovalOutcome>(
    '/coportal/modifications/approve-many',
    {
      method: 'POST',
      body: JSON.stringify({ Ids: ids, Remark: remark }),
    },
    { mutation: true },
  );
}

export async function declineModificationsMany(
  ids: string[],
  remark: string | null = null,
): Promise<BulkApprovalOutcome> {
  return apiFetchRaw<BulkApprovalOutcome>(
    '/coportal/modifications/decline-many',
    {
      method: 'POST',
      body: JSON.stringify({ Ids: ids, Remark: remark }),
    },
    { mutation: true },
  );
}

// ─── Suppliers ────────────────────────────────────────────────────────

export interface Supplier {
  id: string | null;
  name: string | null;
  category: string | null;
  location: string | null;
  phone: string | null;
  email: string | null;
  alt_names: string | null;
  /** Station code (KUL / PEN / LGK / SIN / BN); legacy rows carry MY / SG. */
  sector?: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** What the Suppliers drawer offers (SuppliersController.Sectors). */
export const SUPPLIER_SECTORS = ['KUL', 'PEN', 'LGK', 'SIN', 'BN'] as const;
export type SupplierSector = (typeof SUPPLIER_SECTORS)[number];

export interface SaveSupplierRequest {
  id?: string | null;
  name: string;
  category?: string | null;
  location?: string | null;
  phone?: string | null;
  email?: string | null;
  altNames?: string | null;
  notes?: string | null;
  sector?: string | null;
}

export interface SuppliersFilters {
  category?: string;
  q?: string;
}

export async function listSuppliers(
  filters: SuppliersFilters,
  signal?: AbortSignal,
): Promise<Supplier[]> {
  const qs = new URLSearchParams();
  if (filters.category) qs.set('category', filters.category);
  if (filters.q) qs.set('q', filters.q);
  const path = `/coportal/suppliers${qs.size ? `?${qs}` : ''}`;
  const data = await apiFetch<Supplier[]>(path, { method: 'GET', signal });
  return data ?? [];
}

export async function saveSupplier(req: SaveSupplierRequest): Promise<Supplier> {
  const body = {
    Id: req.id ?? null,
    Name: req.name,
    Category: req.category ?? null,
    Location: req.location ?? null,
    Phone: req.phone ?? null,
    Email: req.email ?? null,
    AltNames: req.altNames ?? null,
    Notes: req.notes ?? null,
    Sector: req.sector ?? null,
  };
  return apiFetch<Supplier>(
    '/coportal/suppliers',
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
}

export async function deleteSupplier(id: string): Promise<void> {
  await apiFetch<void>(
    `/coportal/suppliers/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Hot Plate tasks ──────────────────────────────────────────────────
//
// REMOVED 2026-07-31 (spec Part B · B1.4). This file used to carry a
// second, parallel hot-plate client — `listHotPlateTasks` /
// `saveHotPlateTask` / `deleteHotPlateTask` plus its own `HotPlateTask`
// interface — that sent NO `mod` query param and had no `mod` field. The
// server's `TryResolveMod(null)` resolves to 'coportal', so any component
// that had picked these up would have read and written the wrong board on
// twelve of the thirteen modules. Nothing imported them (verified by grep
// before deletion), which made them a loaded gun sitting in the module's
// main api file rather than a live bug.
//
// The single canonical client is `shared/components/hotplate/api.ts`; the
// types live in `shared/components/hotplate/types.ts` and are re-exported
// from `shared/components/HotPlateBoard.tsx`. Import from there.

// ═══════════════════════════════════════════════════════════════════════
// Round-8 wrappers — next 10 priority CS Portal pages.
// ═══════════════════════════════════════════════════════════════════════

// ─── Petty cash ───────────────────────────────────────────────────────

export interface PettyCashEntry {
  id: number | null;
  batch_id: number | null;
  transaction_ref: string | null;
  month: string | null;
  transaction_date: string | null;
  type: string | null;
  status: string | null;
  invoice_no: string | null;
  item: string | null;
  handled_by: string | null;
  fm: string | null;
  booking_ref: string | null;
  item_id: string | null;
  amount_in: number | null;
  amount_out: number | null;
  balance: number | null;
  receipt_url: string | null;
  attachments: unknown;
  notes: string | null;
  is_tng: boolean | null;
  is_on_hold: boolean | null;
  sort_order: number | null;
  created_at: string | null;
}

export interface PettyCashBatch {
  id: number | null;
  batch_number: number | null;
  status: string | null;
  opening_balance: number | null;
  opened_at: string | null;
  closed_at: string | null;
  label: string | null;
  claim_status: string | null;
  submitted_by: string | null;
  submitted_at: string | null;
  date_range_start: string | null;
  date_range_end: string | null;
}

export interface PettyCashFilters {
  batchId?: number | null;
  onHold?: boolean | null;
  q?: string | null;
}

export async function listPettyCashEntries(
  filters: PettyCashFilters,
  signal?: AbortSignal,
): Promise<PettyCashEntry[]> {
  const qs = new URLSearchParams();
  if (filters.batchId != null) qs.set('batch_id', String(filters.batchId));
  if (filters.onHold != null) qs.set('is_on_hold', filters.onHold ? 'true' : 'false');
  if (filters.q) qs.set('q', filters.q);
  const path = `/coportal/petty-cash${qs.size ? `?${qs}` : ''}`;
  const data = await apiFetch<PettyCashEntry[]>(path, { method: 'GET', signal });
  return data ?? [];
}

export async function listPettyCashBatches(
  signal?: AbortSignal,
): Promise<PettyCashBatch[]> {
  const data = await apiFetch<PettyCashBatch[]>('/coportal/petty-cash/batches', {
    method: 'GET',
    signal,
  });
  return data ?? [];
}

export interface SavePettyCashEntryRequest {
  id?: number | null;
  batchId?: number | null;
  transactionDate?: string | null;
  type?: string | null;
  status?: string | null;
  invoiceNo?: string | null;
  item?: string | null;
  handledBy?: string | null;
  fm?: string | null;
  bookingRef?: string | null;
  itemId?: string | null;
  amountIn?: number | null;
  amountOut?: number | null;
  notes?: string | null;
  isTng?: boolean | null;
  isOnHold?: boolean | null;
  sortOrder?: number | null;
}

export async function savePettyCashEntry(
  req: SavePettyCashEntryRequest,
): Promise<PettyCashEntry> {
  const body = {
    Id: req.id ?? null,
    BatchId: req.batchId ?? null,
    TransactionDate: req.transactionDate ?? null,
    Type: req.type ?? null,
    Status: req.status ?? null,
    InvoiceNo: req.invoiceNo ?? null,
    Item: req.item ?? null,
    HandledBy: req.handledBy ?? null,
    Fm: req.fm ?? null,
    BookingRef: req.bookingRef ?? null,
    ItemId: req.itemId ?? null,
    AmountIn: req.amountIn ?? null,
    AmountOut: req.amountOut ?? null,
    Notes: req.notes ?? null,
    IsTng: req.isTng ?? null,
    IsOnHold: req.isOnHold ?? null,
    SortOrder: req.sortOrder ?? null,
  };
  return apiFetch<PettyCashEntry>(
    '/coportal/petty-cash',
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
}

export async function deletePettyCashEntry(id: number): Promise<void> {
  await apiFetch<void>(
    `/coportal/petty-cash/${id}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Self-drive-kit picker (drawer autofill) ──────────────────────────
//
// Feeds the "new entry" drawer's booking picker. The server decides which
// kits are already accounted for in the ledger (`is_reported`) across every
// batch, so the dropdown can default to unreported bookings only.

export interface PettyCashKitOption {
  id: string | null;
  pnr: string | null;
  guest_name: string | null;
  agent: string | null;
  requested_by: string | null;
  co_date: string | null;
  co_location: string | null;
  is_reported: boolean;
  /** tx ref of the entry that already reported this kit, when known. */
  reported_ref: string | null;
}

export interface PettyCashKitPicker {
  kits: PettyCashKitOption[];
  /** Lowest on-hold TnG card number; null when nothing is on hold. */
  next_tng_number: number | null;
  reported_count: number;
}

export async function listPettyCashKitOptions(
  signal?: AbortSignal,
): Promise<PettyCashKitPicker> {
  const data = await apiFetch<PettyCashKitPicker>(
    '/coportal/petty-cash/self-drive-kits',
    { method: 'GET', signal },
  );
  return {
    kits: data?.kits ?? [],
    next_tng_number: data?.next_tng_number ?? null,
    reported_count: data?.reported_count ?? 0,
  };
}

// ─── Bulk receipts (TnG card-range invoices) ──────────────────────────
//
// One supplier invoice covering a RANGE of Touch & Go card numbers. The
// report PDF pulls it in automatically whenever the batch claims a card
// inside the range; `addToHold` also registers each card as on-hold
// inventory in one pass. Server: PettyCashBulkReceiptsController.

export interface PettyCashBulkReceipt {
  id: number | null;
  label: string | null;
  card_range_start: number | null;
  card_range_end: number | null;
  receipt_url: string | null;
  notes: string | null;
  created_at: string | null;
}

export interface CreateBulkReceiptResult {
  ok: boolean;
  data?: PettyCashBulkReceipt | null;
  url?: string;
  /** Cards newly registered as on-hold inventory. */
  cards_added?: number;
  /** Cards in the range that were already on hold. */
  cards_skipped?: number;
  /** Set when the receipt saved but the inventory rows did not. */
  cards_error?: string | null;
  error?: string;
}

export async function listPettyCashBulkReceipts(
  signal?: AbortSignal,
): Promise<PettyCashBulkReceipt[]> {
  const data = await apiFetch<PettyCashBulkReceipt[]>(
    '/coportal/petty-cash/bulk-receipts',
    { method: 'GET', signal },
  );
  return data ?? [];
}

export interface CreateBulkReceiptRequest {
  file: File;
  cardRangeStart: number;
  cardRangeEnd: number;
  label?: string | null;
  notes?: string | null;
  addToHold: boolean;
  faceValue: number;
}

export async function createPettyCashBulkReceipt(
  req: CreateBulkReceiptRequest,
): Promise<CreateBulkReceiptResult> {
  const fd = new FormData();
  fd.append('file', req.file);
  fd.append('card_range_start', String(req.cardRangeStart));
  fd.append('card_range_end', String(req.cardRangeEnd));
  fd.append('label', req.label ?? '');
  fd.append('notes', req.notes ?? '');
  fd.append('add_to_hold', req.addToHold ? '1' : '0');
  fd.append('face_value', String(req.faceValue));
  // apiFetchRaw, not apiFetch: the partial-outcome fields (cards_added /
  // cards_skipped / cards_error) are siblings of `data`, and unwrapping the
  // envelope would throw them away — which is how "receipt saved but the
  // cards did not" would silently read as a clean success.
  return apiFetchRaw<CreateBulkReceiptResult>(
    '/coportal/petty-cash/bulk-receipts',
    { method: 'POST', body: fd },
    { mutation: true },
  );
}

export async function deletePettyCashBulkReceipt(id: number): Promise<void> {
  await apiFetchRaw<{ ok: boolean }>(
    `/coportal/petty-cash/bulk-receipts/${id}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Buzz File Managers ───────────────────────────────────────────────
//
// One email to every FM who still owes something on the batch. Preview
// first — the operator must see the recipient list before it sends.

export interface PettyCashBuzzPreview {
  ok: boolean;
  recipients: string[];
  cc: string;
  subject: string;
  html: string;
  entries: number;
  missing_fms: string[];
}

export async function previewPettyCashBuzz(
  batchId: number,
  signal?: AbortSignal,
): Promise<PettyCashBuzzPreview> {
  return apiFetchRaw<PettyCashBuzzPreview>(
    `/coportal/petty-cash/batches/${batchId}/buzz`,
    { method: 'GET', signal },
  );
}

export async function sendPettyCashBuzz(
  batchId: number,
  test = false,
): Promise<{ ok: boolean; message?: string; to?: string[]; entries?: number }> {
  return apiFetchRaw<{ ok: boolean; message?: string; to?: string[]; entries?: number }>(
    `/coportal/petty-cash/batches/${batchId}/buzz${test ? '?test=true' : ''}`,
    { method: 'POST' },
    { mutation: true },
  );
}

// ─── Batch lifecycle ──────────────────────────────────────────────────
// Mirrors PettyCashController batch transition endpoints:
//   status:       current → submitted (locked)
//   claim_status: pending → approved → received (or rejected)

export interface PettyCashBatchActionResult {
  ok: boolean;
  message?: string;
  /** Submit only: the batch it closed, whose report is emailed next. */
  submitted_batch_id?: number | null;
  new_batch_id?: number | null;
}

export interface PettyCashReportRecipients {
  ok: boolean;
  to: string;
  cc: string;
}

/**
 * Who the batch's report email would go to (`send-report` with preview, nothing sent).
 * A POST, so it carries the antiforgery token like any other: without it the API
 * answered 400 "Antiforgery token missing or invalid" and "email report" died at
 * this first step (2026-10-21).
 */
export async function previewPettyCashReportEmail(
  batchId: number,
): Promise<PettyCashReportRecipients> {
  return apiFetchRaw<PettyCashReportRecipients>(
    `/coportal/petty-cash/batches/${batchId}/send-report`,
    { method: 'POST', body: JSON.stringify({ preview: true }) },
    { mutation: true },
  );
}

/**
 * Email the batch's report PDF to Finance (PHP send-petty-cash-report.php):
 * `petty_cash_email_to` as To, `petty_cash_email_cc` as Cc.
 */
export async function sendPettyCashReportEmail(
  batchId: number,
): Promise<{ ok: boolean; message?: string; to?: string; cc?: string }> {
  return apiFetchRaw<{ ok: boolean; message?: string; to?: string; cc?: string }>(
    `/coportal/petty-cash/batches/${batchId}/send-report`,
    { method: 'POST', body: JSON.stringify({}) },
    { mutation: true },
  );
}

// Submits THE current batch (server resolves it) — closes it and opens the
// next batch carrying the closing balance forward. These transitions return a
// bare `{ ok, message, ... }` envelope (no `data`), so they use apiFetchRaw.
export async function submitPettyCashBatch(): Promise<PettyCashBatchActionResult> {
  return apiFetchRaw<PettyCashBatchActionResult>(
    '/coportal/petty-cash/batches/submit',
    { method: 'POST' },
    { mutation: true },
  );
}

export async function approvePettyCashBatch(
  id: number,
): Promise<PettyCashBatchActionResult> {
  return apiFetchRaw<PettyCashBatchActionResult>(
    `/coportal/petty-cash/batches/${id}/approve`,
    { method: 'POST' },
    { mutation: true },
  );
}

export async function rejectPettyCashBatch(
  id: number,
  reason: string,
): Promise<PettyCashBatchActionResult> {
  return apiFetchRaw<PettyCashBatchActionResult>(
    `/coportal/petty-cash/batches/${id}/reject`,
    { method: 'POST', body: JSON.stringify({ Reason: reason }) },
    { mutation: true },
  );
}

export async function markPettyCashBatchReceived(
  id: number,
): Promise<PettyCashBatchActionResult> {
  return apiFetchRaw<PettyCashBatchActionResult>(
    `/coportal/petty-cash/batches/${id}/mark-received`,
    { method: 'POST' },
    { mutation: true },
  );
}

// Re-flows a batch's running balances by sort_order (heals a chain whose
// carried-over opening balance was frozen too high, or stale after edits).
export async function recalculatePettyCashBatch(
  id: number,
): Promise<PettyCashBatchActionResult & { changed?: number }> {
  return apiFetchRaw<PettyCashBatchActionResult & { changed?: number }>(
    `/coportal/petty-cash/batches/${id}/recalculate`,
    { method: 'POST' },
    { mutation: true },
  );
}

// ─── Optional sales ───────────────────────────────────────────────────

export interface OptionalSaleRow {
  /** Postgres uuid, not a bigint. */
  id: string | null;
  cs: string | null;
  tour_date: string | null;
  excursion: string | null;
  ref: string | null;
  agent: string | null;
  guest_name: string | null;
  adults: number | null;
  kids: number | null;
  selling_price: number | null;
  cost: number | null;
  incentive: number | null;
  profit_margin: number | null;
  supplier: string | null;
  collected_by: string | null;
  payment: string | null;
  receipt_copy: boolean | null;
  invoice_created: boolean | null;
  sector: string | null;
  /** DB column is `remarks` — there is no `notes` column. */
  remarks: string | null;
  claimed: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface OptionalSalesFilters {
  sector?: string | null;
  year?: number | null;
  q?: string | null;
}

export async function listOptionalSales(
  filters: OptionalSalesFilters,
  signal?: AbortSignal,
): Promise<OptionalSaleRow[]> {
  const qs = new URLSearchParams();
  if (filters.sector) qs.set('sector', filters.sector);
  if (filters.year) qs.set('year', String(filters.year));
  if (filters.q) qs.set('q', filters.q);
  const path = `/coportal/optional-sales${qs.size ? `?${qs}` : ''}`;
  const data = await apiFetch<OptionalSaleRow[]>(path, { method: 'GET', signal });
  return data ?? [];
}

export interface SaveOptionalSaleRequest {
  id?: string | null;
  cs: string;
  tourDate: string;
  excursion: string;
  ref?: string | null;
  agent?: string | null;
  guestName?: string | null;
  adults?: number | null;
  kids?: number | null;
  sellingPrice?: number | null;
  cost?: number | null;
  incentive?: number | null;
  supplier?: string | null;
  collectedBy?: string | null;
  payment?: string | null;
  receiptCopy?: boolean | null;
  invoiceCreated?: boolean | null;
  sector?: string | null;
  remarks?: string | null;
  claimed?: boolean | null;
}

export async function saveOptionalSale(
  req: SaveOptionalSaleRequest,
): Promise<OptionalSaleRow> {
  const body = {
    Id: req.id ?? null,
    Cs: req.cs,
    TourDate: req.tourDate,
    Excursion: req.excursion,
    Ref: req.ref ?? null,
    Agent: req.agent ?? null,
    GuestName: req.guestName ?? null,
    Adults: req.adults ?? null,
    Kids: req.kids ?? null,
    SellingPrice: req.sellingPrice ?? null,
    Cost: req.cost ?? null,
    Incentive: req.incentive ?? null,
    Supplier: req.supplier ?? null,
    CollectedBy: req.collectedBy ?? null,
    Payment: req.payment ?? null,
    ReceiptCopy: req.receiptCopy ?? null,
    InvoiceCreated: req.invoiceCreated ?? null,
    Sector: req.sector ?? null,
    Remarks: req.remarks ?? null,
    Claimed: req.claimed ?? null,
  };
  return apiFetch<OptionalSaleRow>(
    '/coportal/optional-sales',
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
}

/**
 * Incentive claim workflow. Scope is EITHER an explicit set of row ids (any
 * claimed state) OR a CS staff name (their unclaimed rows only) — ids win.
 * Ports `optional-sales-mark-claimed.php` / `optional-sales-send-report.php`,
 * which the first React pass left out entirely.
 */
export interface OptionalSalesScope {
  ids?: string[];
  cs?: string | null;
}

export async function markOptionalSalesClaimed(
  scope: OptionalSalesScope & { claimed: boolean },
): Promise<{ updated: number }> {
  return apiFetchRaw<{ updated: number }>(
    '/coportal/optional-sales/mark-claimed',
    {
      method: 'POST',
      body: JSON.stringify({
        Ids: scope.ids ?? [],
        Cs: scope.cs ?? null,
        Claimed: scope.claimed,
      }),
    },
    { mutation: true },
  );
}

export interface SendOptionalSalesReportResult {
  ok: boolean;
  rows: number;
  staff: string[];
  total_incentive: number;
  total_incentive_fmt: string;
  to?: string | null;
  cc?: string | null;
  error?: string | null;
}

export async function sendOptionalSalesReport(
  scope: OptionalSalesScope,
): Promise<SendOptionalSalesReportResult> {
  return apiFetchRaw<SendOptionalSalesReportResult>(
    '/coportal/optional-sales/report/send',
    {
      method: 'POST',
      body: JSON.stringify({ Ids: scope.ids ?? [], Cs: scope.cs ?? null }),
    },
    { mutation: true },
  );
}

/**
 * URL of the inline PDF preview, for `window.open`. Auth rides the session
 * cookie, so a plain tab open is authenticated — same shape as
 * `noShowReportPdfUrl`.
 */
export function optionalSalesReportPreviewUrl(scope: OptionalSalesScope): string {
  const qs = new URLSearchParams();
  if (scope.ids?.length) qs.set('ids', scope.ids.join(','));
  else if (scope.cs) qs.set('cs', scope.cs);
  return `/api/coportal/optional-sales/report/preview?${qs}`;
}

export async function deleteOptionalSale(id: string): Promise<void> {
  await apiFetch<void>(
    `/coportal/optional-sales/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Optional sales report ────────────────────────────────────────────

export interface OptionalSalesReportKpis {
  totalSalesMyr: number;
  totalSalesSgd: number;
  totalSalesAllMyr: number;
  totalMargin: number;
  totalIncentive: number;
  totalPax: number;
  toursSold: number;
}

export interface OptionalSalesReportStaffRow {
  cs: string;
  tours: number;
  pax: number;
  salesMyr: number;
  salesSgd: number;
  salesTotalMyr: number;
  margin: number;
  incentive: number;
  target: number;
  pctOfTarget: number;
}

export interface OptionalSalesReportTopTour {
  name: string;
  count: number;
  pax: number;
  sales: number;
  sectors: string[];
}

export interface OptionalSalesReportResponse {
  ok: boolean;
  year: number;
  sgdToMyr: number;
  /** The home currency the "-eq" totals are in (G4; absent on an older API = MYR). */
  homeCurrency?: string;
  kpis: OptionalSalesReportKpis;
  monthly: { month: number; label: string; total: number }[];
  staff: OptionalSalesReportStaffRow[];
  topTours: OptionalSalesReportTopTour[];
  allYears: number[];
}

export async function getOptionalSalesReport(
  year: number | null,
  signal?: AbortSignal,
): Promise<OptionalSalesReportResponse> {
  const qs = new URLSearchParams();
  if (year) qs.set('year', String(year));
  const path = `/coportal/optional-sales-report${qs.size ? `?${qs}` : ''}`;
  return apiFetchRaw<OptionalSalesReportResponse>(path, { method: 'GET', signal });
}

// ─── Optional tours catalog ───────────────────────────────────────────

export interface OptionalTour {
  /** Postgres uuid, not a bigint. */
  id: string | null;
  name: string | null;
  destination: string | null;
  product_id: string | null;
  /** Five per-group-size tiers — the table has no adult/kid split. */
  price_1pax: number | null;
  price_2pax: number | null;
  price_3_6pax: number | null;
  price_7_10pax: number | null;
  price_sic: number | null;
  supplier: string | null;
  inclusions: string | null;
  description: string | null;
  picture_url: string | null;
  /** Soft-archive toggle. The column is `is_visible`, not `active`. */
  is_visible: boolean | null;
  created_at: string | null;
}

export interface OptionalToursFilters {
  destination?: string | null;
  visibleOnly?: boolean | null;
  q?: string | null;
}

export async function listOptionalTours(
  filters: OptionalToursFilters,
  signal?: AbortSignal,
): Promise<OptionalTour[]> {
  const qs = new URLSearchParams();
  if (filters.destination) qs.set('destination', filters.destination);
  if (filters.visibleOnly) qs.set('visibleOnly', 'true');
  if (filters.q) qs.set('q', filters.q);
  const path = `/coportal/optional-tours${qs.size ? `?${qs}` : ''}`;
  const data = await apiFetch<OptionalTour[]>(path, { method: 'GET', signal });
  return data ?? [];
}

export interface SaveOptionalTourRequest {
  id?: string | null;
  name: string;
  destination: string;
  productId?: string | null;
  price1Pax?: number | null;
  price2Pax?: number | null;
  price3To6Pax?: number | null;
  price7To10Pax?: number | null;
  priceSic?: number | null;
  supplier?: string | null;
  inclusions?: string | null;
  description?: string | null;
  pictureUrl?: string | null;
  isVisible?: boolean | null;
}

export async function saveOptionalTour(
  req: SaveOptionalTourRequest,
): Promise<OptionalTour> {
  const body = {
    Id: req.id ?? null,
    Name: req.name,
    Destination: req.destination,
    ProductId: req.productId ?? null,
    Price1Pax: req.price1Pax ?? null,
    Price2Pax: req.price2Pax ?? null,
    Price3To6Pax: req.price3To6Pax ?? null,
    Price7To10Pax: req.price7To10Pax ?? null,
    PriceSic: req.priceSic ?? null,
    Supplier: req.supplier ?? null,
    Inclusions: req.inclusions ?? null,
    Description: req.description ?? null,
    PictureUrl: req.pictureUrl ?? null,
    IsVisible: req.isVisible ?? null,
  };
  return apiFetch<OptionalTour>(
    '/coportal/optional-tours',
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
}

export async function deleteOptionalTour(id: string): Promise<void> {
  await apiFetch<void>(
    `/coportal/optional-tours/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Super transfers ──────────────────────────────────────────────────

export interface SuperTransferRow {
  id: string | null;
  line_ref: string | null;
  ref: string | null;
  kuoni_ref: string | null;
  passenger_name: string | null;
  pax: number | null;
  flight: string | null;
  pickup_time: string | null;
  pickup_location: string | null;
  dropoff_location: string | null;
  sector: string | null;
  log: string | null;
  status: string | null;
  vip: boolean | null;
  is_group: boolean | null;
  transfer: string | null;
  transfer_needed: boolean | null;
  no_direct_comm: boolean | null;
  agent: string | null;
  supplier: string | null;
  contact_details: string | null;
  service_date: string | null;
}

export interface SuperTransfersResponse {
  ok: boolean;
  from: string;
  to: string;
  data: SuperTransferRow[];
}

export interface SuperTransfersFilters {
  dateFrom: string;
  dateTo: string;
  sector?: string | null;
  missingOnly?: boolean | null;
}

export async function listSuperTransfers(
  filters: SuperTransfersFilters,
  signal?: AbortSignal,
): Promise<SuperTransfersResponse> {
  const qs = new URLSearchParams({
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
  });
  if (filters.sector) qs.set('sector', filters.sector);
  if (filters.missingOnly) qs.set('missingOnly', 'true');
  return apiFetchRaw<SuperTransfersResponse>(
    `/coportal/super-transfers?${qs}`,
    { method: 'GET', signal },
  );
}

export interface BulkSetTransferRequest {
  ids: string[];
  transfer: string;
}

export async function bulkSetTransfer(
  req: BulkSetTransferRequest,
): Promise<{ ok: boolean; updated: number }> {
  return apiFetchRaw<{ ok: boolean; updated: number }>(
    '/coportal/super-transfers/bulk-set',
    {
      method: 'POST',
      body: JSON.stringify({ Ids: req.ids, Transfer: req.transfer }),
    },
    { mutation: true },
  );
}

export interface SaveSicTransferRequest {
  id: string;
  guestIndex: number;
  sicTotal: number;
  transfer: string;
}

/**
 * Per-guest SIC cascade. When every guest ends up with the same value AND
 * the dictionary is full (`sicTotal` reached), the server collapses the
 * JSON map back to a plain string broadcast — mirroring PHP
 * `save-transfer.php`.
 */
export async function saveSicTransfer(
  req: SaveSicTransferRequest,
): Promise<{ ok: boolean; transfer: unknown }> {
  return apiFetchRaw<{ ok: boolean; transfer: unknown }>(
    `/coportal/super-transfers/${encodeURIComponent(req.id)}/sic`,
    {
      method: 'POST',
      body: JSON.stringify({
        GuestIndex: req.guestIndex,
        SicTotal: req.sicTotal,
        Transfer: req.transfer,
      }),
    },
    { mutation: true },
  );
}

// ─── Gift catalog ─────────────────────────────────────────────────────
//
// The `gift_catalog` table as it is (PHP coportal/setup/migration.sql:275):
// prices are free TEXT ("MYR 120"), `sector` and `product` are required.

export interface GiftCatalogItem {
  id: string | null;
  sector: string | null;
  product: string | null;
  description: string | null;
  supplier: string | null;
  location: string | null;
  pickup: string | null;
  link: string | null;
  cost: string | null;
  postage: string | null;
  markup: string | null;
  price_local: string | null;
  price_usd: string | null;
  price_eur: string | null;
  currency: string | null;
  remarks: string | null;
  is_available: boolean | null;
  photo_url: string | null;
}

export interface GiftCatalogFilters {
  sector?: string | null;
  availableOnly?: boolean | null;
}

export async function listGiftCatalog(
  filters: GiftCatalogFilters,
  signal?: AbortSignal,
): Promise<GiftCatalogItem[]> {
  const qs = new URLSearchParams();
  if (filters.sector) qs.set('sector', filters.sector);
  if (filters.availableOnly) qs.set('availableOnly', 'true');
  const path = `/coportal/gift-catalog${qs.size ? `?${qs}` : ''}`;
  const data = await apiFetch<GiftCatalogItem[]>(path, { method: 'GET', signal });
  return data ?? [];
}

export interface SaveGiftCatalogItemRequest {
  id?: string | null;
  sector: string;
  product: string;
  description?: string | null;
  supplier?: string | null;
  location?: string | null;
  pickup?: string | null;
  link?: string | null;
  cost?: string | null;
  postage?: string | null;
  markup?: string | null;
  priceLocal?: string | null;
  priceUsd?: string | null;
  priceEur?: string | null;
  currency?: string | null;
  remarks?: string | null;
  isAvailable?: boolean | null;
  photoUrl?: string | null;
}

export async function saveGiftCatalogItem(
  req: SaveGiftCatalogItemRequest,
): Promise<GiftCatalogItem> {
  const body = {
    Id: req.id ?? null,
    Sector: req.sector,
    Product: req.product,
    Description: req.description ?? null,
    Supplier: req.supplier ?? null,
    Location: req.location ?? null,
    Pickup: req.pickup ?? null,
    Link: req.link ?? null,
    Cost: req.cost ?? null,
    Postage: req.postage ?? null,
    Markup: req.markup ?? null,
    PriceLocal: req.priceLocal ?? null,
    PriceUsd: req.priceUsd ?? null,
    PriceEur: req.priceEur ?? null,
    Currency: req.currency ?? null,
    Remarks: req.remarks ?? null,
    IsAvailable: req.isAvailable ?? null,
    PhotoUrl: req.photoUrl ?? null,
  };
  return apiFetch<GiftCatalogItem>(
    '/coportal/gift-catalog',
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
}

/** Stores a product photo; the drawer saves the returned url into `photo_url`. */
export async function uploadGiftPhoto(file: File): Promise<string | null> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await apiFetchRaw<{ url?: string | null }>(
    '/coportal/gift-catalog/photo-upload',
    { method: 'POST', body: fd },
    { mutation: true },
  );
  return res?.url ?? null;
}

export async function deleteGiftCatalogItem(id: string): Promise<void> {
  await apiFetch<void>(
    `/coportal/gift-catalog/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Gift orders ──────────────────────────────────────────────────────

export interface GiftOrder {
  id: string | null;
  ref: string | null;
  file_manager: string | null;
  guest_name: string | null;
  sector: string | null;
  catalog_item_id: string | null;
  gift_name: string | null;
  quantity: number | null;
  order_date: string | null;
  delivery_date: string | null;
  status: string | null;
  notes: string | null;
  created_at: string | null;
}

export interface GiftOrdersFilters {
  status?: string | null;
  sector?: string | null;
  q?: string | null;
}

export async function listGiftOrders(
  filters: GiftOrdersFilters,
  signal?: AbortSignal,
): Promise<GiftOrder[]> {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.sector) qs.set('sector', filters.sector);
  if (filters.q) qs.set('q', filters.q);
  const path = `/coportal/gift-orders${qs.size ? `?${qs}` : ''}`;
  const data = await apiFetch<GiftOrder[]>(path, { method: 'GET', signal });
  return data ?? [];
}

export interface SaveGiftOrderRequest {
  id?: string | null;
  ref?: string | null;
  fileManager?: string | null;
  guestName?: string | null;
  sector?: string | null;
  catalogItemId?: string | null;
  giftName: string;
  quantity?: number | null;
  orderDate?: string | null;
  deliveryDate?: string | null;
  status?: string | null;
  notes?: string | null;
}

export async function saveGiftOrder(
  req: SaveGiftOrderRequest,
): Promise<GiftOrder> {
  const body = {
    Id: req.id ?? null,
    Ref: req.ref ?? null,
    FileManager: req.fileManager ?? null,
    GuestName: req.guestName ?? null,
    Sector: req.sector ?? null,
    CatalogItemId: req.catalogItemId ?? null,
    GiftName: req.giftName,
    Quantity: req.quantity ?? null,
    OrderDate: req.orderDate ?? null,
    DeliveryDate: req.deliveryDate ?? null,
    Status: req.status ?? null,
    Notes: req.notes ?? null,
  };
  return apiFetch<GiftOrder>(
    '/coportal/gift-orders',
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
}

export async function deleteGiftOrder(id: string): Promise<void> {
  await apiFetch<void>(
    `/coportal/gift-orders/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Agent scorecard ──────────────────────────────────────────────────

export interface AgentScorecardRow {
  agent: string;
  bookings: number;
  uniqueRefs: number;
  cancelled: number;
  cancellationRate: number;
  issues: number;
  issuesCritical: number;
  issuesPerBooking: number;
  noshows: number;
  optSalesRevenue: number;
}

export interface AgentScorecardTotals {
  bookings: number;
  uniqueRefs: number;
  cancelled: number;
  issues: number;
  issuesCritical: number;
  noshows: number;
  optSalesRevenue: number;
}

export interface AgentScorecardResponse {
  ok: boolean;
  dateFrom: string;
  data: AgentScorecardRow[];
  totals: AgentScorecardTotals;
}

export async function getAgentScorecard(
  signal?: AbortSignal,
): Promise<AgentScorecardResponse> {
  return apiFetchRaw<AgentScorecardResponse>('/coportal/agent-scorecard', {
    method: 'GET',
    signal,
  });
}

// ─── Kuoni Monthly Report ─────────────────────────────────────────────
//
// Server: MonthlyReportsController.cs (`/api/coportal/monthly-reports`).
// One row per (agent='Kuoni', report_month) — upsert keyed on that pair.
// Response rows use snake_case wire names (MonthlyReport model has
// [JsonPropertyName] on every field). `error_rows` / `issue_selections`
// are free-form JSONB arrays edited opaquely by the tab.

/** Free-form error-summary row. The PDF renderer reads these exact keys
 *  (BuildErrorAggregates: area/source/issue/actions/ref). */
export interface MonthlyReportErrorRow {
  area?: string | null;
  source?: string | null;
  issue?: string | null;
  actions?: string | null;
  ref?: string | null;
}

/** Snapshot of an issues_log row imported into a draft report. Mirrors
 *  MonthlyReportIssueSnapshot (import-issues response). */
export interface MonthlyReportIssueSnapshot {
  id: number | null;
  service_date: string | null;
  ref: string | null;
  ref_kuoni: string | null;
  guest_name: string | null;
  issue: string | null;
  solution: string | null;
  sector: string | null;
  supplier: string | null;
  status: string | null;
  cause: string | null;
  importance: number | null;
}

/** Persisted monthly_reports row (MonthlyReport model, snake_case wire). */
export interface MonthlyReport {
  id: number | null;
  agent: string | null;
  destination: string | null;
  report_month: string | null;
  general_feedback: string | null;
  successes: string | null;
  nps_review: string | null;
  nps_issues: string | null;
  nps_ideas: string | null;
  error_rows: MonthlyReportErrorRow[] | null;
  issue_selections: MonthlyReportIssueSnapshot[] | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Save/upsert body (SaveMonthlyReportRequest). Bound case-insensitively
 *  by System.Text.Json; we send PascalCase to match the record. */
export interface SaveMonthlyReportRequest {
  reportMonth: string;
  destination?: string | null;
  generalFeedback?: string | null;
  successes?: string | null;
  npsReview?: string | null;
  npsIssues?: string | null;
  npsIdeas?: string | null;
  errorRows?: MonthlyReportErrorRow[] | null;
  issueSelections?: MonthlyReportIssueSnapshot[] | null;
}

/** One aggregated NPS bucket row (nps-pull `rows[]`). */
export interface MonthlyReportNpsRow {
  label: string;
  detractors: number;
  passives: number;
  promoters: number;
  total: number;
  nps: number;
}

/** nps-pull response (full object, NOT an {ok,data} envelope). */
export interface MonthlyReportNpsPullResponse {
  ok: boolean;
  year: number;
  month: string;
  summary: string;
  nps_review: string;
  nps_issues: string;
  nps_ideas: string;
  rows: MonthlyReportNpsRow[];
}

/** GET /coportal/monthly-reports?month=&destination= → MonthlyReport[]
 *  (always agent=Kuoni server-side). */
export async function listMonthlyReports(
  month?: string,
  destination?: string,
  signal?: AbortSignal,
): Promise<MonthlyReport[]> {
  const qs = new URLSearchParams();
  if (month) qs.set('month', month);
  if (destination) qs.set('destination', destination);
  const suffix = qs.toString() ? `?${qs}` : '';
  const res = await apiFetchRaw<{ ok?: boolean; data?: MonthlyReport[] }>(
    `/coportal/monthly-reports${suffix}`,
    { method: 'GET', signal },
  );
  return res.data ?? [];
}

/** GET /coportal/monthly-reports/{id} → MonthlyReport | null. */
export async function getMonthlyReport(
  id: number,
  signal?: AbortSignal,
): Promise<MonthlyReport | null> {
  const res = await apiFetchRaw<{ ok?: boolean; data?: MonthlyReport }>(
    `/coportal/monthly-reports/${id}`,
    { method: 'GET', signal },
  );
  return res.data ?? null;
}

/** POST /coportal/monthly-reports (upsert on agent+report_month). */
export async function saveMonthlyReport(
  req: SaveMonthlyReportRequest,
): Promise<MonthlyReport | null> {
  const body = {
    ReportMonth: req.reportMonth,
    Destination: req.destination ?? null,
    GeneralFeedback: req.generalFeedback ?? null,
    Successes: req.successes ?? null,
    NpsReview: req.npsReview ?? null,
    NpsIssues: req.npsIssues ?? null,
    NpsIdeas: req.npsIdeas ?? null,
    ErrorRows: req.errorRows ?? null,
    IssueSelections: req.issueSelections ?? null,
  };
  const res = await apiFetchRaw<{ ok?: boolean; data?: MonthlyReport }>(
    '/coportal/monthly-reports',
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
  return res.data ?? null;
}

/** DELETE /coportal/monthly-reports/{id}. */
export async function deleteMonthlyReport(id: number): Promise<void> {
  await apiFetch<void>(
    `/coportal/monthly-reports/${id}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

/** POST /coportal/monthly-reports/import-issues — pull Kuoni issues_log
 *  rows for a month (optionally narrowed to `issueIds`). */
export async function importMonthlyReportIssues(
  req: { month: string; destination?: string | null; issueIds?: number[] },
  signal?: AbortSignal,
): Promise<MonthlyReportIssueSnapshot[]> {
  const body: Record<string, unknown> = { month: req.month };
  if (req.destination) body.destination = req.destination;
  if (req.issueIds && req.issueIds.length > 0) body.issue_ids = req.issueIds;
  const res = await apiFetchRaw<{ ok?: boolean; data?: MonthlyReportIssueSnapshot[] }>(
    '/coportal/monthly-reports/import-issues',
    { method: 'POST', body: JSON.stringify(body), signal },
    { mutation: true },
  );
  return res.data ?? [];
}

/** POST /coportal/monthly-reports/nps-pull — aggregate agent_nps into the
 *  12-row NPS table + verbatim-bucketed nps_* summary text. */
export async function pullMonthlyReportNps(
  req: { month: string; destination?: string | null },
  signal?: AbortSignal,
): Promise<MonthlyReportNpsPullResponse> {
  const body: Record<string, unknown> = { month: req.month };
  if (req.destination) body.destination = req.destination;
  return apiFetchRaw<MonthlyReportNpsPullResponse>(
    '/coportal/monthly-reports/nps-pull',
    { method: 'POST', body: JSON.stringify(body), signal },
    { mutation: true },
  );
}

// PDF endpoints stream `application/pdf`; apiFetch(Raw) both call res.json()
// so we hand-roll the fetch to read the binary body. XSRF-safe (echoes the
// readable XSRF-TOKEN cookie) + credentials:'include' for the auth cookie.
async function fetchMonthlyReportPdf(
  path: string,
  body: unknown,
): Promise<{ blob: Blob; filename: string }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const xsrf = readXsrfToken();
  if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    let msg = `PDF request failed with status ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string } | null;
      if (j?.error) msg = j.error;
    } catch {
      /* non-JSON error body — keep default */
    }
    throw new ApiError(res.status, msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const filename = m?.[1]
    ? decodeURIComponent(m[1])
    : `${COMPANY.keyAgent.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-monthly-report.pdf`;
  return { blob, filename };
}

/** POST /coportal/monthly-reports/pdf — render from the live (possibly
 *  unsaved) draft payload. */
export async function downloadMonthlyReportPdf(
  req: SaveMonthlyReportRequest,
): Promise<{ blob: Blob; filename: string }> {
  const body = {
    ReportMonth: req.reportMonth,
    Destination: req.destination ?? null,
    GeneralFeedback: req.generalFeedback ?? null,
    Successes: req.successes ?? null,
    NpsReview: req.npsReview ?? null,
    NpsIssues: req.npsIssues ?? null,
    NpsIdeas: req.npsIdeas ?? null,
    ErrorRows: req.errorRows ?? null,
    IssueSelections: req.issueSelections ?? null,
  };
  return fetchMonthlyReportPdf('/coportal/monthly-reports/pdf', body);
}

/** POST /coportal/monthly-reports/{id}/pdf — render from the persisted row. */
export async function downloadMonthlyReportPdfById(
  id: number,
): Promise<{ blob: Blob; filename: string }> {
  return fetchMonthlyReportPdf(`/coportal/monthly-reports/${id}/pdf`, {});
}

// ─── Destinations ─────────────────────────────────────────────────────

export interface Destination {
  id: string | null;
  name: string | null;
  code: string | null;
  country: string | null;
  description: string | null;
  image_url: string | null;
  sort_order: number | null;
  is_active: boolean | null;
  public_visible: boolean | null;
  show_optional_tours: boolean | null;
  show_local_recs: boolean | null;
  areas: string[] | null;
}

export async function listDestinations(
  activeOnly = false,
  signal?: AbortSignal,
): Promise<Destination[]> {
  const qs = activeOnly ? '?activeOnly=true' : '';
  const data = await apiFetch<Destination[]>(`/coportal/destinations${qs}`, {
    method: 'GET',
    signal,
  });
  return data ?? [];
}

export interface SaveDestinationRequest {
  id?: string | null;
  name: string;
  code?: string | null;
  country?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
  publicVisible?: boolean | null;
  showOptionalTours?: boolean | null;
  showLocalRecs?: boolean | null;
  areas?: string[] | null;
}

export async function saveDestination(
  req: SaveDestinationRequest,
): Promise<Destination> {
  const body = {
    Id: req.id ?? null,
    Name: req.name,
    Code: req.code ?? null,
    Country: req.country ?? null,
    Description: req.description ?? null,
    ImageUrl: req.imageUrl ?? null,
    SortOrder: req.sortOrder ?? null,
    IsActive: req.isActive ?? null,
    PublicVisible: req.publicVisible ?? null,
    ShowOptionalTours: req.showOptionalTours ?? null,
    ShowLocalRecs: req.showLocalRecs ?? null,
    Areas: req.areas ?? null,
  };
  return apiFetch<Destination>(
    '/coportal/destinations',
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
}

export async function deleteDestination(id: string): Promise<void> {
  await apiFetch<void>(
    `/coportal/destinations/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Arrival points ───────────────────────────────────────────────────

export interface ArrivalPoint {
  id: number | null;
  name: string | null;
  alt_names: string | null;
  region: string | null;
  description: string | null;
  paging_board: boolean | null;
  photo_url: string | null;
  secondary_photos: string[] | null;
}

export interface ArrivalPointsFilters {
  region?: string | null;
  pagingOnly?: boolean | null;
}

export async function listArrivalPoints(
  filters: ArrivalPointsFilters,
  signal?: AbortSignal,
): Promise<ArrivalPoint[]> {
  const qs = new URLSearchParams();
  if (filters.region) qs.set('region', filters.region);
  if (filters.pagingOnly) qs.set('pagingOnly', 'true');
  const path = `/coportal/arrival-points${qs.size ? `?${qs}` : ''}`;
  const data = await apiFetch<ArrivalPoint[]>(path, { method: 'GET', signal });
  return data ?? [];
}

export interface SaveArrivalPointRequest {
  id?: string | null;
  name: string;
  region?: string | null;
  description?: string | null;
  altNames?: string | null;
  pagingBoard?: boolean | null;
  photoUrl?: string | null;
  secondaryPhotos?: string[] | null;
}

export async function saveArrivalPoint(
  req: SaveArrivalPointRequest,
): Promise<ArrivalPoint> {
  const body = {
    Id: req.id ?? null,
    Name: req.name,
    Region: req.region ?? null,
    Description: req.description ?? null,
    AltNames: req.altNames ?? null,
    PagingBoard: req.pagingBoard ?? null,
    PhotoUrl: req.photoUrl ?? null,
    SecondaryPhotos: req.secondaryPhotos ?? null,
  };
  return apiFetch<ArrivalPoint>(
    '/coportal/arrival-points',
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
}

export async function deleteArrivalPoint(id: string | number): Promise<void> {
  await apiFetch<void>(
    `/coportal/arrival-points/${encodeURIComponent(String(id))}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Guest contacts ───────────────────────────────────────────────────

export interface GuestContact {
  id: string | null;
  guest_name: string | null;
  ref: string | null;
  contact_details: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export async function listGuestContacts(
  q: string | null | undefined,
  signal?: AbortSignal,
): Promise<GuestContact[]> {
  const qs = new URLSearchParams();
  if (q) qs.set('q', q);
  const path = `/coportal/guest-contacts${qs.size ? `?${qs}` : ''}`;
  const data = await apiFetch<GuestContact[]>(path, { method: 'GET', signal });
  return data ?? [];
}

export interface SaveGuestContactRequest {
  id?: string | null;
  guestName?: string | null;
  ref?: string | null;
  contactDetails?: string | null;
  notes?: string | null;
}

export async function saveGuestContact(
  req: SaveGuestContactRequest,
): Promise<GuestContact> {
  const body = {
    Id: req.id ?? null,
    GuestName: req.guestName ?? null,
    Ref: req.ref ?? null,
    ContactDetails: req.contactDetails ?? null,
    Notes: req.notes ?? null,
  };
  return apiFetch<GuestContact>(
    '/coportal/guest-contacts',
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
}

export async function deleteGuestContact(id: string): Promise<void> {
  await apiFetch<void>(
    `/coportal/guest-contacts/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Kuoni contacts (agent-specific guest directory) ──────────────────
//
// Port of `hub/coportal/api/kuoni-contacts.php`. The list comes back
// already sorted `arrival_date desc nullsfirst, guest_name asc`. Save,
// import and match-refs answer with SIBLING fields next to `ok`
// (`autoSync`, `inserted`, `matched`, `message`…), so they go through
// `apiFetchRaw` — `apiFetch` would unwrap `data` and drop them.

export interface KuoniContact {
  id: string | null;
  guest_name: string | null;
  ref_kuoni: string | null;
  ref_malaysia: string | null;
  ref_singapore: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  /** `yyyy-mm-dd` or null. */
  arrival_date: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface KuoniSyncResult {
  matched: number;
  kuoniSynced: number;
  logsScanned: number;
  message: string | null;
}

export async function listKuoniContacts(
  q: string | null | undefined,
  signal?: AbortSignal,
): Promise<KuoniContact[]> {
  const qs = new URLSearchParams();
  if (q) qs.set('q', q);
  const path = `/coportal/kuoni-contacts${qs.size ? `?${qs}` : ''}`;
  const data = await apiFetch<KuoniContact[]>(path, { method: 'GET', signal });
  return data ?? [];
}

export interface SaveKuoniContactRequest {
  id?: string | null;
  guestName: string;
  refKuoni?: string | null;
  refMalaysia?: string | null;
  refSingapore?: string | null;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
  /** `yyyy-mm-dd` or null. */
  arrivalDate?: string | null;
}

export interface SaveKuoniContactResult {
  data: KuoniContact | null;
  autoSync: KuoniSyncResult | null;
}

export async function saveKuoniContact(
  req: SaveKuoniContactRequest,
): Promise<SaveKuoniContactResult> {
  const body = {
    id: req.id ?? null,
    guestName: req.guestName,
    refKuoni: req.refKuoni ?? null,
    refMalaysia: req.refMalaysia ?? null,
    refSingapore: req.refSingapore ?? null,
    phone: req.phone ?? null,
    email: req.email ?? null,
    notes: req.notes ?? null,
    arrivalDate: req.arrivalDate ?? null,
  };
  const res = await apiFetchRaw<{
    ok?: boolean;
    error?: string;
    data?: KuoniContact | null;
    autoSync?: KuoniSyncResult | null;
  } | null>(
    '/coportal/kuoni-contacts',
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
  if (res && res.ok === false) throw new Error(res.error ?? 'Save failed.');
  return { data: res?.data ?? null, autoSync: res?.autoSync ?? null };
}

export async function deleteKuoniContact(id: string): Promise<void> {
  await apiFetch<void>(
    `/coportal/kuoni-contacts/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

export interface KuoniImportRow {
  guestName: string;
  refKuoni: string;
  /** `yyyy-mm-dd` or empty string. */
  arrivalDate: string;
  phone: string;
  email: string;
}

export interface KuoniImportResult {
  inserted: number;
  updated: number;
  skipped: number;
  matched: number;
  message: string | null;
}

export async function importKuoniContacts(
  rows: KuoniImportRow[],
): Promise<KuoniImportResult> {
  const res = await apiFetchRaw<{
    ok?: boolean;
    error?: string;
    inserted?: number;
    updated?: number;
    skipped?: number;
    matched?: number;
    message?: string | null;
  } | null>(
    '/coportal/kuoni-contacts/import',
    { method: 'POST', body: JSON.stringify({ rows }) },
    { mutation: true },
  );
  if (res && res.ok === false) throw new Error(res.error ?? 'Import failed.');
  return {
    inserted: res?.inserted ?? 0,
    updated: res?.updated ?? 0,
    skipped: res?.skipped ?? 0,
    matched: res?.matched ?? 0,
    message: res?.message ?? null,
  };
}

export async function matchKuoniRefs(): Promise<KuoniSyncResult> {
  const res = await apiFetchRaw<{
    ok?: boolean;
    error?: string;
    matched?: number;
    kuoniSynced?: number;
    logsScanned?: number;
    message?: string | null;
  } | null>(
    '/coportal/kuoni-contacts/match-refs',
    { method: 'POST' },
    { mutation: true },
  );
  if (res && res.ok === false) throw new Error(res.error ?? 'Sync failed.');
  return {
    matched: res?.matched ?? 0,
    kuoniSynced: res?.kuoniSynced ?? 0,
    logsScanned: res?.logsScanned ?? 0,
    message: res?.message ?? null,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// Round-9 wrappers — attachments, my-notes, directories, mobile-hotline.
// ═══════════════════════════════════════════════════════════════════════

// ─── My Notes (canonical /api/notes endpoint, shared with Lemax/Restools) ──

/** Note color literals understood by the server. */
export const NOTE_COLORS = [
  'yellow', 'blue', 'green', 'pink',
  'purple', 'orange', 'red', 'teal', 'gray',
] as const;
export type NoteColor = (typeof NOTE_COLORS)[number];

export interface SaveNoteRequest {
  id?: string | null;
  title?: string | null;
  content?: string | null;
  color?: NoteColor | null;
  pinned?: boolean | null;
}

export async function saveMyNote(req: SaveNoteRequest): Promise<UserNote | null> {
  // POST /api/notes returns `{ok, data: UserNote}`. `apiFetch` strips the
  // envelope (see web/src/auth/api.ts lines 85-91), so we receive the bare
  // UserNote — same shape B27 (iter-8 commit 544ee97) fixed in restools.
  const saved = await apiFetch<UserNote>(
    '/notes',
    {
      method: 'POST',
      body: JSON.stringify({
        Id: req.id ?? null,
        Title: req.title ?? '',
        Content: req.content ?? '',
        Color: req.color ?? 'yellow',
        Pinned: req.pinned ?? false,
      }),
    },
    { mutation: true },
  );
  return saved ?? null;
}

export async function deleteMyNote(id: string): Promise<void> {
  await apiFetchRaw<{ ok: boolean }>(
    `/notes/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Booking attachments (document library) ───────────────────────────

export interface BookingAttachment {
  id: string | null;
  ref: string | null;
  ref_ats: string | null;
  passenger_name: string | null;
  destination: string | null;
  arrival_date: string | null;
  original_name: string | null;
  filename: string | null;
  file_url: string | null;
  file_type: string | null;
  file_size: number | null;
  uploaded_by: string | null;
  shared_with_guests: boolean | null;
  notes: string | null;
  created_at: string | null;
}

export interface AttachmentsFilters {
  ref?: string | null;
  q?: string | null;
  limit?: number;
  offset?: number;
}

export async function listAttachments(
  filters: AttachmentsFilters,
  signal?: AbortSignal,
): Promise<BookingAttachment[]> {
  const qs = new URLSearchParams();
  if (filters.ref) qs.set('ref', filters.ref);
  if (filters.q) qs.set('q', filters.q);
  if (filters.limit) qs.set('limit', String(filters.limit));
  if (filters.offset) qs.set('offset', String(filters.offset));
  const res = await apiFetchRaw<{ data?: BookingAttachment[] }>(
    `/coportal/attachments${qs.size ? `?${qs}` : ''}`,
    { method: 'GET', signal },
  );
  return res?.data ?? [];
}

export interface SaveAttachmentMetaRequest {
  id?: string | null;
  ref: string;
  refAts?: string | null;
  passengerName?: string | null;
  destination?: string | null;
  arrivalDate?: string | null;
  notes?: string | null;
  sharedWithGuests?: boolean | null;
}

export async function saveAttachmentMeta(
  req: SaveAttachmentMetaRequest,
): Promise<BookingAttachment | null> {
  // Wire shape `{ok, data: BookingAttachment}` — apiFetch strips. Same
  // class as B27 (iter-8 commit 544ee97).
  const saved = await apiFetch<BookingAttachment>(
    '/coportal/attachments',
    {
      method: 'POST',
      body: JSON.stringify({
        Id: req.id ?? null,
        Ref: req.ref,
        RefAts: req.refAts ?? null,
        PassengerName: req.passengerName ?? null,
        Destination: req.destination ?? null,
        ArrivalDate: req.arrivalDate ?? null,
        Notes: req.notes ?? null,
        SharedWithGuests: req.sharedWithGuests ?? null,
      }),
    },
    { mutation: true },
  );
  return saved ?? null;
}

/**
 * Multipart upload — bypasses `apiFetch`'s JSON envelope and posts a
 * FormData payload directly. Cookies/XSRF still ride along.
 */
export async function uploadAttachment(
  fields: {
    ref: string;
    refAts?: string;
    passengerName?: string;
    destination?: string;
    arrivalDate?: string;
    sharedWithGuests?: boolean;
  },
  file: File,
): Promise<BookingAttachment | null> {
  const fd = new FormData();
  fd.set('ref', fields.ref);
  if (fields.refAts) fd.set('ref_ats', fields.refAts);
  if (fields.passengerName) fd.set('passenger_name', fields.passengerName);
  if (fields.destination) fd.set('destination', fields.destination);
  if (fields.arrivalDate) fd.set('arrival_date', fields.arrivalDate);
  if (fields.sharedWithGuests != null)
    fd.set('shared_with_guests', fields.sharedWithGuests ? 'true' : 'false');
  fd.set('file', file);
  const xsrf =
    typeof document !== 'undefined'
      ? document.cookie
          .split(';')
          .map((s) => s.trim())
          .find((s) => s.startsWith('XSRF-TOKEN='))
          ?.split('=')[1]
      : undefined;
  const res = await fetch('/api/coportal/attachments/upload', {
    method: 'POST',
    credentials: 'include',
    headers: xsrf ? { 'X-XSRF-TOKEN': decodeURIComponent(xsrf) } : {},
    body: fd,
  });
  if (!res.ok) {
    let msg = `Upload failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const json = (await res.json()) as { data?: BookingAttachment };
  return json?.data ?? null;
}

export async function deleteAttachment(id: string): Promise<void> {
  await apiFetchRaw<{ ok: boolean }>(
    `/coportal/attachments/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

export async function setAttachmentShared(
  id: string,
  shared: boolean,
): Promise<void> {
  await apiFetchRaw<{ ok: boolean }>(
    `/coportal/attachments/${encodeURIComponent(id)}/share`,
    {
      method: 'POST',
      body: JSON.stringify({ Shared: shared }),
    },
    { mutation: true },
  );
}

// ─── Hotel contacts ───────────────────────────────────────────────────

export interface HotelContact {
  id: string | null;
  hotel_name: string | null;
  city: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  contact_person: string | null;
  position: string | null;
  notes: string | null;
  category: string | null;
  /** Comma-separated alternate names (edited as tags; see `@/shared/aliases`). */
  alt_names?: string | null;
  created_at: string | null;
}

export interface HotelContactsFilters {
  city?: string | null;
  category?: string | null;
  q?: string | null;
}

export async function listHotelContacts(
  filters: HotelContactsFilters,
  signal?: AbortSignal,
): Promise<HotelContact[]> {
  const qs = new URLSearchParams();
  if (filters.city) qs.set('city', filters.city);
  if (filters.category) qs.set('category', filters.category);
  if (filters.q) qs.set('q', filters.q);
  const res = await apiFetchRaw<{ data?: HotelContact[] }>(
    `/coportal/hotel-contacts${qs.size ? `?${qs}` : ''}`,
    { method: 'GET', signal },
  );
  return res?.data ?? [];
}

export interface SaveHotelContactRequest {
  id?: string | null;
  hotelName: string;
  city?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  contactPerson?: string | null;
  position?: string | null;
  notes?: string | null;
  category?: string | null;
  /** `""` clears the column; `undefined` / `null` leaves it untouched. */
  altNames?: string | null;
}

export async function saveHotelContact(
  req: SaveHotelContactRequest,
): Promise<HotelContact | null> {
  // Wire shape `{ok, data: HotelContact}` — apiFetch strips. Same class
  // as B27 (iter-8 commit 544ee97).
  const saved = await apiFetch<HotelContact>(
    '/coportal/hotel-contacts',
    {
      method: 'POST',
      body: JSON.stringify({
        Id: req.id ?? null,
        HotelName: req.hotelName,
        City: req.city ?? null,
        Country: req.country ?? null,
        Phone: req.phone ?? null,
        Email: req.email ?? null,
        Address: req.address ?? null,
        ContactPerson: req.contactPerson ?? null,
        Position: req.position ?? null,
        AltNames: req.altNames ?? null,
        Notes: req.notes ?? null,
        Category: req.category ?? null,
      }),
    },
    { mutation: true },
  );
  return saved ?? null;
}

export async function deleteHotelContact(id: string): Promise<void> {
  await apiFetchRaw<{ ok: boolean }>(
    `/coportal/hotel-contacts/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Inventory ────────────────────────────────────────────────────────

export interface InventoryItem {
  id: string | null;
  item_name: string | null;
  item_type: string | null;
  quantity: number | null;
  sector: string | null;
  condition: string | null;
  location_with: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface InventoryFilters {
  sector?: string | null;
  itemType?: string | null;
  q?: string | null;
}

export async function listInventory(
  filters: InventoryFilters,
  signal?: AbortSignal,
): Promise<InventoryItem[]> {
  const qs = new URLSearchParams();
  if (filters.sector) qs.set('sector', filters.sector);
  if (filters.itemType) qs.set('itemType', filters.itemType);
  if (filters.q) qs.set('q', filters.q);
  const res = await apiFetchRaw<{ data?: InventoryItem[] }>(
    `/coportal/inventory${qs.size ? `?${qs}` : ''}`,
    { method: 'GET', signal },
  );
  return res?.data ?? [];
}

export interface SaveInventoryItemRequest {
  id?: string | null;
  itemName: string;
  itemType?: string | null;
  quantity: number;
  sector?: string | null;
  condition?: string | null;
  locationWith?: string | null;
  notes?: string | null;
}

export async function saveInventoryItem(
  req: SaveInventoryItemRequest,
): Promise<InventoryItem | null> {
  // Wire shape `{ok, data: InventoryItem}` — apiFetch strips. Same class
  // as B27 (iter-8 commit 544ee97).
  const saved = await apiFetch<InventoryItem>(
    '/coportal/inventory',
    {
      method: 'POST',
      body: JSON.stringify({
        Id: req.id ?? null,
        ItemName: req.itemName,
        ItemType: req.itemType ?? null,
        Quantity: req.quantity,
        Sector: req.sector ?? null,
        Condition: req.condition ?? null,
        LocationWith: req.locationWith ?? null,
        Notes: req.notes ?? null,
      }),
    },
    { mutation: true },
  );
  return saved ?? null;
}

export async function deleteInventoryItem(id: string): Promise<void> {
  await apiFetchRaw<{ ok: boolean }>(
    `/coportal/inventory/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Self-drive kits ──────────────────────────────────────────────────

export interface SelfDriveKit {
  id: string | null;
  pnr: string | null;
  agent: string | null;
  guest_name: string | null;
  rental_company: string | null;
  co_date: string | null;
  co_time: string | null;
  co_location: string | null;
  do_date: string | null;
  do_time: string | null;
  do_location: string | null;
  date_received: string | null;
  due_date: string | null;
  sent_on: string | null;
  remarks: string | null;
  requested_by: string | null;
  attachments: unknown;
  created_at: string | null;
  updated_at: string | null;
}

export async function listSelfDriveKits(
  q: string | null | undefined,
  signal?: AbortSignal,
): Promise<SelfDriveKit[]> {
  const qs = new URLSearchParams();
  if (q) qs.set('q', q);
  const res = await apiFetchRaw<{ data?: SelfDriveKit[] }>(
    `/coportal/self-drive-kits${qs.size ? `?${qs}` : ''}`,
    { method: 'GET', signal },
  );
  return res?.data ?? [];
}

export interface SaveSelfDriveKitRequest {
  id?: string | null;
  guestName: string;
  pnr?: string | null;
  agent?: string | null;
  rentalCompany?: string | null;
  coDate?: string | null;
  coTime?: string | null;
  coLocation?: string | null;
  doDate?: string | null;
  doTime?: string | null;
  doLocation?: string | null;
  dateReceived?: string | null;
  dueDate?: string | null;
  sentOn?: string | null;
  remarks?: string | null;
}

export async function saveSelfDriveKit(
  req: SaveSelfDriveKitRequest,
): Promise<SelfDriveKit | null> {
  // Wire shape `{ok, data: SelfDriveKit}` — apiFetch strips. Same class
  // as B27 (iter-8 commit 544ee97).
  const saved = await apiFetch<SelfDriveKit>(
    '/coportal/self-drive-kits',
    {
      method: 'POST',
      body: JSON.stringify({
        Id: req.id ?? null,
        GuestName: req.guestName,
        Pnr: req.pnr ?? null,
        Agent: req.agent ?? null,
        RentalCompany: req.rentalCompany ?? null,
        CoDate: req.coDate ?? null,
        CoTime: req.coTime ?? null,
        CoLocation: req.coLocation ?? null,
        DoDate: req.doDate ?? null,
        DoTime: req.doTime ?? null,
        DoLocation: req.doLocation ?? null,
        DateReceived: req.dateReceived ?? null,
        DueDate: req.dueDate ?? null,
        SentOn: req.sentOn ?? null,
        Remarks: req.remarks ?? null,
        Attachments: null,
      }),
    },
    { mutation: true },
  );
  return saved ?? null;
}

export async function deleteSelfDriveKit(id: string): Promise<void> {
  await apiFetchRaw<{ ok: boolean }>(
    `/coportal/self-drive-kits/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Informations ─────────────────────────────────────────────────────

export interface Information {
  id: string | null;
  destination: string | null;
  title: string | null;
  content: string | null;
  is_active: boolean | null;
  sort_order: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface InformationsFilters {
  destination?: string | null;
  activeOnly?: boolean | null;
}

export async function listInformations(
  filters: InformationsFilters,
  signal?: AbortSignal,
): Promise<Information[]> {
  const qs = new URLSearchParams();
  if (filters.destination) qs.set('destination', filters.destination);
  if (filters.activeOnly) qs.set('activeOnly', 'true');
  const res = await apiFetchRaw<{ data?: Information[] }>(
    `/coportal/informations${qs.size ? `?${qs}` : ''}`,
    { method: 'GET', signal },
  );
  return res?.data ?? [];
}

export interface SaveInformationRequest {
  id?: string | null;
  destination: string;
  title?: string | null;
  content: string;
  isActive?: boolean | null;
  sortOrder?: number | null;
}

export async function saveInformation(
  req: SaveInformationRequest,
): Promise<Information | null> {
  // Wire shape `{ok, data: Information}` — apiFetch strips. Same class
  // as B27 (iter-8 commit 544ee97).
  const saved = await apiFetch<Information>(
    '/coportal/informations',
    {
      method: 'POST',
      body: JSON.stringify({
        Id: req.id ?? null,
        Destination: req.destination,
        Title: req.title ?? null,
        Content: req.content,
        IsActive: req.isActive ?? null,
        SortOrder: req.sortOrder ?? null,
      }),
    },
    { mutation: true },
  );
  return saved ?? null;
}

export async function deleteInformation(id: string): Promise<void> {
  await apiFetchRaw<{ ok: boolean }>(
    `/coportal/informations/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Local recommendations ────────────────────────────────────────────
//
// Per the R9 brief, the live PHP schema has a `badges` JSONB column that
// the .NET `LocalRecommendation` model has not yet caught up with. We
// expose `badges` as `unknown` on the wire so the UI can render anything
// the server forwards through; older API builds simply omit the field.

export interface LocalRecommendation {
  id: string | null;
  destination: string | null;
  type: string | null;
  name: string | null;
  description: string | null;
  maps_url: string | null;
  picture_url: string | null;
  area: string | null;
  sort_order: number | null;
  badges?: unknown; // jsonb — string[] or { label, color }[] (server-side TBD)
  created_at: string | null;
}

export interface LocalRecommendationsFilters {
  destination?: string | null;
  type?: string | null;
}

export async function listLocalRecommendations(
  filters: LocalRecommendationsFilters,
  signal?: AbortSignal,
): Promise<LocalRecommendation[]> {
  const qs = new URLSearchParams();
  if (filters.destination) qs.set('destination', filters.destination);
  if (filters.type) qs.set('type', filters.type);
  const res = await apiFetchRaw<{ data?: LocalRecommendation[] }>(
    `/coportal/local-recommendations${qs.size ? `?${qs}` : ''}`,
    { method: 'GET', signal },
  );
  return res?.data ?? [];
}

export interface SaveLocalRecommendationRequest {
  id?: string | null;
  destination: string;
  type: string;
  name: string;
  description?: string | null;
  mapsUrl?: string | null;
  pictureUrl?: string | null;
  area?: string | null;
  sortOrder?: number | null;
  // R10 close-out: `badges` write-side. Pass `string[]` (simple labels)
  // or `{ label: string; color?: string }[]` (palette-aware). Pass
  // `undefined` to leave the column untouched on update.
  badges?: string[] | Array<{ label: string; color?: string }> | null;
}

export async function saveLocalRecommendation(
  req: SaveLocalRecommendationRequest,
): Promise<LocalRecommendation | null> {
  const body: Record<string, unknown> = {
    Id: req.id ?? null,
    Destination: req.destination,
    Type: req.type,
    Name: req.name,
    Description: req.description ?? null,
    MapsUrl: req.mapsUrl ?? null,
    PictureUrl: req.pictureUrl ?? null,
    Area: req.area ?? null,
    SortOrder: req.sortOrder ?? null,
  };
  if (req.badges !== undefined) body.Badges = req.badges;
  // Wire shape `{ok, data: LocalRecommendation}` — apiFetch strips. Same
  // class as B27 (iter-8 commit 544ee97).
  const saved = await apiFetch<LocalRecommendation>(
    '/coportal/local-recommendations',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    { mutation: true },
  );
  return saved ?? null;
}

export async function deleteLocalRecommendation(id: string): Promise<void> {
  await apiFetchRaw<{ ok: boolean }>(
    `/coportal/local-recommendations/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Round-10 additions: search · add-service · auto-reports · mobile-hotline
// ═══════════════════════════════════════════════════════════════════════

// ─── Global search ─────────────────────────────────────────────────────
//
// PHP source: `hub/coportal/pages/search.php` + `api/data.php?action=search`.
// .NET source: SearchController (`/api/coportal/search?q=&sector=&limit=`).
// Server envelope is custom (`{ ok, data, q, sector, count, fallback?, note? }`)
// so we read it raw and surface the count + fallback flag.

export interface SearchHit extends LogRow {
  line_ref: string | null;
  kuoni_ref: string | null;
  flight: string | null;
}

export interface SearchResponse {
  hits: SearchHit[];
  q: string;
  sector: string;
  count: number;
  fallback: boolean;
  note?: string;
}

/** `'all'` or a destination code of the tenant's geography (MY, SG, BN for Asian Trails). */
export type SearchSector = string;

export async function searchLogs(
  q: string,
  sector: SearchSector = 'all',
  limit = 100,
  signal?: AbortSignal,
): Promise<SearchResponse> {
  const qs = new URLSearchParams();
  qs.set('q', q);
  qs.set('sector', sector);
  qs.set('limit', String(limit));
  const res = await apiFetchRaw<{
    ok: boolean;
    data?: SearchHit[];
    q?: string;
    sector?: string;
    count?: number;
    fallback?: boolean;
    note?: string;
  }>(`/coportal/search?${qs}`, { method: 'GET', signal });
  return {
    hits: res.data ?? [],
    q: res.q ?? q,
    sector: res.sector ?? sector,
    count: res.count ?? (res.data?.length ?? 0),
    fallback: Boolean(res.fallback),
    note: res.note,
  };
}

// ─── Add Service (sibling row inheriting from log ref) ────────────────
//
// PHP source: `hub/coportal/pages/add-service.php` (462 LoC) +
//             `hub/coportal/api/add-service.php` (file-manager loader).
// .NET source: LogsController POST /api/coportal/logs/add-service.

export interface AddServiceRequest {
  log_type: 'MY' | 'BN' | 'SG' | string;
  ref: string;
  service_date: string;
  pickup_time?: string | null;
  agent: string;
  passenger_name: string;
  supplier: string;
  product: string;
  pickup_location: string;
  dropoff_location: string;
  pax?: number | null;
  flight?: string | null;
  ops_remarks?: string | null;
  cs_remarks?: string | null;
  transfer?: string | null;
  contact_details?: string | null;
  file_manager?: string | null;
  /** PHP form fields the port omitted until 2026-09-11. */
  courtesy?: string | null;
  transfer_message?: string | null;
  special_service?: string | null;
}

export async function addServiceLine(
  req: AddServiceRequest,
): Promise<LogRow | null> {
  // Server takes Pascal-case keys (record positional names → ASP.NET binder
  // accepts camel/Pascal interchangeably with default JSON options).
  const body = {
    LogType: req.log_type,
    Ref: req.ref,
    ServiceDate: req.service_date,
    PickupTime: req.pickup_time ?? null,
    Agent: req.agent,
    PassengerName: req.passenger_name,
    Supplier: req.supplier,
    Product: req.product,
    PickupLocation: req.pickup_location,
    DropoffLocation: req.dropoff_location,
    Pax: req.pax ?? null,
    Flight: req.flight ?? null,
    OpsRemarks: req.ops_remarks ?? null,
    CsRemarks: req.cs_remarks ?? null,
    Transfer: req.transfer ?? null,
    ContactDetails: req.contact_details ?? null,
    FileManager: req.file_manager ?? null,
    Courtesy: req.courtesy ?? null,
    TransferMessage: req.transfer_message ?? null,
    SpecialService: req.special_service ?? null,
  };
  const res = await apiFetchRaw<{ ok: boolean; data?: LogRow }>(
    '/coportal/logs/add-service',
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
  return res.data ?? null;
}

// ─── Edit a single log row — R13-A2 FINAL-POLISH ──────────────────────
//
// LogsController.Edit (POST /api/coportal/logs/{id}/edit) takes a generic
// `{ Page, Patch, OldValues?, ModificationRemark?, AutoApprove? }` payload.
// The server runs the auto-approve decision tree + cascades (vip /
// contact_details / autolink) before persisting; the wrapper just round-
// trips the patch dict. Callers should send only the fields they touch.
export interface EditLogRequest {
  /** Page slug — `logs-all` / `log-my` / `log-bn` / `log-sg` / `log-prep`. */
  page: string;
  /** Field name → new value. Allowed keys live in `LogsEditService`. */
  patch: Record<string, unknown>;
  /** Optional original values for the decision tree (no-op when omitted). */
  oldValues?: Record<string, unknown>;
  /** Free-text remark surfaced in the modifications log. */
  modificationRemark?: string | null;
  /** Override the user's auto_approve flag for this single write. */
  autoApprove?: boolean | null;
}

export interface EditLogOutcome {
  ok: boolean;
  applied: boolean;
  pending: boolean;
  modification_id?: string | null;
  data?: LogRow | null;
}

export async function editLogRow(
  id: string,
  req: EditLogRequest,
): Promise<EditLogOutcome> {
  const body = {
    Page: req.page,
    Patch: req.patch,
    OldValues: req.oldValues ?? null,
    ModificationRemark: req.modificationRemark ?? null,
    AutoApprove: req.autoApprove ?? null,
  };
  const res = await apiFetchRaw<EditLogOutcome>(
    `/coportal/logs/${encodeURIComponent(id)}/edit`,
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
  return res;
}

// ─── Batch actions over `logs` (LogsBatchController) ───────────────────
//
// Server returns `{ ok, count, pending?, error? }` — same envelope across
// all four endpoints. `pending: true` means the change was queued for
// manager approval; otherwise it was applied directly.

export interface BatchActionOutcome {
  ok: boolean;
  count?: number;
  pending?: boolean;
  error?: string;
}

export type LogsBatchTable = 'logs' | 'logs_preparation';

export type LogsBatchFlag =
  | 'overland'
  | 'vip'
  | 'is_group'
  | 'no_direct_comm';

export async function batchSetStatus(args: {
  table: LogsBatchTable;
  ids: string[];
  value: 'active' | 'cancelled' | 'no-show';
  autoApprove?: boolean;
}): Promise<BatchActionOutcome> {
  return apiFetchRaw<BatchActionOutcome>(
    '/coportal/logs/batch/set-status',
    {
      method: 'POST',
      body: JSON.stringify({
        Table: args.table,
        Ids: args.ids,
        Value: args.value,
        AutoApprove: args.autoApprove ?? null,
      }),
    },
    { mutation: true },
  );
}

export async function batchSetFlag(args: {
  table: LogsBatchTable;
  ids: string[];
  flag: LogsBatchFlag;
  value: boolean;
  autoApprove?: boolean;
}): Promise<BatchActionOutcome> {
  return apiFetchRaw<BatchActionOutcome>(
    '/coportal/logs/batch/set-flag',
    {
      method: 'POST',
      body: JSON.stringify({
        Table: args.table,
        Ids: args.ids,
        Flag: args.flag,
        Value: args.value,
        AutoApprove: args.autoApprove ?? null,
      }),
    },
    { mutation: true },
  );
}

export async function batchSetFields(args: {
  table: LogsBatchTable;
  ids: string[];
  fields: Record<string, unknown>;
  autoApprove?: boolean;
}): Promise<BatchActionOutcome> {
  return apiFetchRaw<BatchActionOutcome>(
    '/coportal/logs/batch/set-fields',
    {
      method: 'POST',
      body: JSON.stringify({
        Table: args.table,
        Ids: args.ids,
        Fields: args.fields,
        AutoApprove: args.autoApprove ?? null,
      }),
    },
    { mutation: true },
  );
}

export async function batchDeleteLogs(args: {
  table: LogsBatchTable;
  ids: string[];
  autoApprove?: boolean;
}): Promise<BatchActionOutcome> {
  return apiFetchRaw<BatchActionOutcome>(
    '/coportal/logs/batch/delete',
    {
      method: 'POST',
      body: JSON.stringify({
        Table: args.table,
        Ids: args.ids,
        AutoApprove: args.autoApprove ?? null,
      }),
    },
    { mutation: true },
  );
}

/**
 * Strict shape kept for backwards compatibility with the add-service page.
 * The .NET FileManagersController (Chunk 6) may return nulls for email
 * or market — callers that need non-null guarantees should filter with
 * `narrowFileManagerOptions` before consuming.
 */
export interface FileManagerOption {
  email: string;
  name: string;
  market: string;
}

/**
 * Loose row shape returned by `/api/coportal/file-managers`. Used by the
 * edit modal's grouped FM dropdown (Chunk 6 · E8). PHP source: the
 * `_fmCached` data array in `hub/coportal/includes/header.php:1083-1099`.
 */
export interface FileManagerRow {
  id?: string | null;
  name?: string | null;
  email?: string | null;
  market?: string | null;
}

/**
 * Filter + coerce the loose `FileManagerRow[]` into the strict
 * `FileManagerOption[]` shape. Drops any row missing email or name.
 */
export function narrowFileManagerOptions(
  rows: ReadonlyArray<FileManagerRow>,
): FileManagerOption[] {
  const out: FileManagerOption[] = [];
  for (const r of rows) {
    const email = (r.email ?? '').trim();
    const name = (r.name ?? '').trim();
    if (!email || !name) continue;
    out.push({ email, name, market: (r.market ?? '').trim() });
  }
  return out;
}

/**
 * Convenience helper: groups the flat FM list by market (preserving
 * server sort order). Mirrors PHP `$_fmGrouped` from header.php.
 */
export function groupFileManagersByMarket(
  rows: ReadonlyArray<FileManagerRow>,
): Record<string, FileManagerRow[]> {
  const out: Record<string, FileManagerRow[]> = {};
  for (const r of rows) {
    const key = (r.market && r.market.trim()) || 'Other';
    const bucket = out[key] ?? (out[key] = []);
    bucket.push(r);
  }
  return out;
}

// Fetches the canonical file_managers list from the .NET API. Server
// caches for 5 min (FileManagersController). Used by the edit modal,
// add-service page, and batch-edit modal. Returns the loose
// `FileManagerRow[]` shape; the add-service page uses
// `narrowFileManagerOptions` to coerce to the strict variant.
export async function listFileManagers(
  signal?: AbortSignal,
): Promise<FileManagerRow[]> {
  try {
    const res = await apiFetchRaw<{
      ok?: boolean;
      data?: FileManagerRow[];
    }>('/coportal/file-managers', { method: 'GET', signal });
    return res.data ?? [];
  } catch {
    return [];
  }
}

// ─── Auto-reports (report_schedules CRUD) ──────────────────────────────
//
// PHP source: `hub/coportal/pages/auto-reports.php` + `api/auto-reports.php`.
// .NET source: AutoReportsController (`/api/admin/auto-reports`).

export type AutoReportType =
  | 'daily-issues'
  | 'daily-csv'
  | 'daily-brief'
  | 'weekly-brief'
  | 'weekend-recap'
  | 'flight-check'
  | 'kuoni-sync'
  | 'custom';

export type AutoReportCadence =
  | 'daily'
  | 'weekly-mon'
  | 'weekly-sun'
  | 'monthly'
  | 'custom';

export interface AutoReport {
  id: string | null;
  name: string | null;
  report_type: string | null;
  cadence: string | null;
  enabled: boolean | null;
  recipients: string[] | null;
  filters: Record<string, unknown> | null;
  last_sent_at: string | null;
  last_run_status: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface AutoReportRaw {
  id?: string | null;
  name?: string | null;
  report_type?: string | null;
  cadence?: string | null;
  enabled?: boolean | null;
  recipients?: unknown;
  filters?: unknown;
  last_sent_at?: string | null;
  last_run_status?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

function normalizeAutoReport(raw: AutoReportRaw): AutoReport {
  let recipients: string[] | null = null;
  if (Array.isArray(raw.recipients))
    recipients = raw.recipients.map((x) => String(x));
  else if (typeof raw.recipients === 'string') {
    try {
      const parsed = JSON.parse(raw.recipients);
      if (Array.isArray(parsed)) recipients = parsed.map((x) => String(x));
    } catch {
      recipients = raw.recipients
        .split(/[,;\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  let filters: Record<string, unknown> | null = null;
  if (raw.filters && typeof raw.filters === 'object' && !Array.isArray(raw.filters))
    filters = raw.filters as Record<string, unknown>;
  return {
    id: raw.id ?? null,
    name: raw.name ?? null,
    report_type: raw.report_type ?? null,
    cadence: raw.cadence ?? null,
    enabled: raw.enabled ?? null,
    recipients,
    filters,
    last_sent_at: raw.last_sent_at ?? null,
    last_run_status: raw.last_run_status ?? null,
    created_by: raw.created_by ?? null,
    created_at: raw.created_at ?? null,
    updated_at: raw.updated_at ?? null,
  };
}

export async function listAutoReports(
  signal?: AbortSignal,
): Promise<AutoReport[]> {
  try {
    const res = await apiFetchRaw<{ ok: boolean; data?: AutoReportRaw[] }>(
      '/admin/auto-reports',
      { method: 'GET', signal },
    );
    return (res.data ?? []).map(normalizeAutoReport);
  } catch {
    return [];
  }
}

export interface SaveAutoReportRequest {
  id?: string | null;
  name: string;
  reportType: string;
  cadence: string;
  enabled: boolean;
  recipients: string[];
  filters?: Record<string, unknown>;
}

export async function saveAutoReport(
  req: SaveAutoReportRequest,
): Promise<AutoReport | null> {
  const body = {
    id: req.id ?? null,
    name: req.name,
    report_type: req.reportType,
    cadence: req.cadence,
    enabled: req.enabled,
    recipients: req.recipients,
    filters: req.filters ?? {},
  };
  const res = await apiFetchRaw<{ ok: boolean; data?: AutoReportRaw }>(
    '/admin/auto-reports',
    { method: 'POST', body: JSON.stringify(body) },
    { mutation: true },
  );
  return res.data ? normalizeAutoReport(res.data) : null;
}

export async function deleteAutoReport(id: string): Promise<void> {
  await apiFetchRaw<{ ok: boolean }>(
    `/admin/auto-reports/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

export async function fireAutoReport(id: string): Promise<void> {
  await apiFetchRaw<{ ok: boolean }>(
    `/admin/auto-reports/${encodeURIComponent(id)}/run`,
    { method: 'POST' },
    { mutation: true },
  );
}

// ─── Mobile hotline (today + tomorrow services snapshot) ───────────────
//
// PHP source: `hub/coportal/pages/mobile-hotline.php` (871 LoC).
// .NET surface for the hotline schedule is not yet wired (BACKEND-R10).
// The mobile view itself reuses `LogsController.List` filtered to
// today/tomorrow and groups SIC rows client-side. When the dedicated
// /mobile-hotline endpoint lands it'll replace this composite.

export interface MobileHotlineRow extends LogRow {
  line_ref: string | null;
  contact_details: string | null;
  ops_remarks: string | null;
  no_direct_comm: boolean | null;
  overland: boolean | null;
  flight: string | null;
  // `sic_line_overrides` is inherited from `LogRow` (now typed as
  // `SicLineOverride[] | Record<string, SicLineOverride> | null`); the
  // hotline view feeds the same column straight through `normalizeSicLineOverrides`
  // when expanding SIC parents into per-passenger cards. The previous
  // `string | null` redeclaration was a wire-format stub that never
  // matched the actual JSONB payload — removing it lets every consumer
  // share the same normalization helper.
}

export async function listMobileHotlineRows(
  dateFrom: string,
  dateTo: string,
  signal?: AbortSignal,
): Promise<MobileHotlineRow[]> {
  // Hit the regular logs endpoint twice (today + tomorrow) and merge.
  // logs-all already excludes cancelled because LogsPages.BaseFilters
  // applies the standard server-side guards. We then drop status=cancelled
  // client-side as a safety net.
  const qs = new URLSearchParams();
  qs.set('page', 'logs-all');
  qs.set('limit', '500');
  qs.set('offset', '0');
  qs.set('date_from', dateFrom);
  qs.set('date_to', dateTo);
  try {
    const res = await apiFetchRaw<{ ok: boolean; data?: MobileHotlineRow[] }>(
      `/coportal/logs?${qs}`,
      { method: 'GET', signal },
    );
    const rows = res.data ?? [];
    return rows.filter(
      (r) => (r.status ?? '').toLowerCase().trim() !== 'cancelled',
    );
  } catch {
    return [];
  }
}

// ─── Mobile-hotline composite (R10 close-out — dedicated endpoint) ────
//
// New surface backing the React mobile-hotline page. Returns SIC-expanded
// log cards with resolved contact + supplier phones in one envelope so
// the on-call view doesn't need three round-trips.

export interface HotlineCard {
  id: string;
  serviceDate: string | null;
  pickupTime: string | null;
  passengerName: string;
  ref: string | null;
  agent: string | null;
  flight: string | null;
  pickupLocation: string | null;
  dropoffLocation: string | null;
  supplier: string | null;
  product: string | null;
  status: string | null;
  vip: boolean | null;
  pax: number | null;
  opsRemarks: string | null;
  sector: string | null;
  overland: boolean | null;
  isGroup: boolean | null;
  noDirectComm: boolean | null;
  lineRef: string | null;
  resolvedContact: string | null;
  resolvedPhone: string | null;
  resolvedWaNumber: string | null;
  supplierPhone: string | null;
  supplierPhoneClean: string | null;
  sicLineIndex: number | null;
}

export interface KuoniEmergencyContact { guestName: string; phone: string }
export interface SupplierEmergencyContact { name: string; phone: string }

export interface MobileHotlinePayload {
  date: string;
  tomorrow: string;
  cards: HotlineCard[];
  kuoniEmergencyContacts: KuoniEmergencyContact[];
  supplierEmergencyContacts: SupplierEmergencyContact[];
}

export async function getMobileHotline(
  date: string | null | undefined,
  signal?: AbortSignal,
): Promise<MobileHotlinePayload> {
  const qs = new URLSearchParams();
  if (date) qs.set('date', date);
  const res = await apiFetchRaw<{ ok: boolean; data: MobileHotlinePayload }>(
    `/coportal/mobile-hotline${qs.size ? `?${qs}` : ''}`,
    { method: 'GET', signal },
  );
  return res.data;
}

export interface LogHotlineContactInput {
  logId?: string | null;
  ref?: string | null;
  passengerName?: string | null;
  phone?: string | null;
  channel: 'call' | 'whatsapp' | 'supplier';
  note?: string | null;
}

export async function logHotlineContact(input: LogHotlineContactInput): Promise<void> {
  await apiFetchRaw<{ ok: boolean }>(
    '/coportal/mobile-hotline/contact',
    {
      method: 'POST',
      body: JSON.stringify({
        LogId: input.logId ?? null,
        Ref: input.ref ?? null,
        PassengerName: input.passengerName ?? null,
        Phone: input.phone ?? null,
        Channel: input.channel,
        Note: input.note ?? null,
      }),
    },
    { mutation: true },
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Round-12 wrappers — closing the long tail of CS-Portal placeholder pages.
// ═══════════════════════════════════════════════════════════════════════

// ─── Welcome meetings (save / delete) ─────────────────────────────────

export interface SaveWelcomeMeetingRequest {
  id?: string | null;
  meetingDate?: string | null;
  meetingTime?: string | null;
  guestName?: string | null;
  ref?: string | null;
  kuoniRef?: string | null;
  agent?: string | null;
  location?: string | null;
  csToMeet?: string | null;
  sectors?: string | null;
  reportedBy?: string | null;
  report?: string | null;
  /** The log a "Set WM" / log-sourced save came from (PHP `log_id`). */
  logId?: string | null;
}

export async function saveWelcomeMeeting(
  req: SaveWelcomeMeetingRequest,
): Promise<WelcomeMeeting> {
  return apiFetch<WelcomeMeeting>(
    '/coportal/welcome-meetings',
    {
      method: 'POST',
      body: JSON.stringify({
        Id: req.id ?? null,
        MeetingDate: req.meetingDate ?? null,
        MeetingTime: req.meetingTime ?? null,
        GuestName: req.guestName ?? null,
        Ref: req.ref ?? null,
        KuoniRef: req.kuoniRef ?? null,
        Agent: req.agent ?? null,
        Location: req.location ?? null,
        CsToMeet: req.csToMeet ?? null,
        Sectors: req.sectors ?? null,
        ReportedBy: req.reportedBy ?? null,
        Report: req.report ?? null,
        LogId: req.logId ?? null,
      }),
    },
    { mutation: true },
  );
}

export async function deleteWelcomeMeeting(id: string): Promise<void> {
  await apiFetch<void>(
    `/coportal/welcome-meetings/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── VIPs ─────────────────────────────────────────────────────────────

export interface Vip {
  id: string | null;
  ref: string | null;
  guest_name: string | null;
  agent: string | null;
  flag_reason: string | null;
  notes: string | null;
  created_at: string | null;
}

export interface SaveVipRequest {
  id?: string | null;
  ref?: string | null;
  guestName?: string | null;
  agent?: string | null;
  flagReason?: string | null;
  notes?: string | null;
}

export async function listVips(signal?: AbortSignal): Promise<Vip[]> {
  try {
    const data = await apiFetch<Vip[]>('/coportal/vips', {
      method: 'GET',
      signal,
    });
    return data ?? [];
  } catch {
    return [];
  }
}

export async function saveVip(req: SaveVipRequest): Promise<Vip> {
  return apiFetch<Vip>(
    '/coportal/vips',
    {
      method: 'POST',
      body: JSON.stringify({
        Id: req.id ?? null,
        Ref: req.ref ?? null,
        GuestName: req.guestName ?? null,
        Agent: req.agent ?? null,
        FlagReason: req.flagReason ?? null,
        Notes: req.notes ?? null,
      }),
    },
    { mutation: true },
  );
}

export async function deleteVip(id: string): Promise<void> {
  await apiFetch<void>(
    `/coportal/vips/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Positive feedback ────────────────────────────────────────────────

export interface PositiveFeedbackRow {
  id: number | null;
  ref: string | null;
  guest_name: string | null;
  feedback_date: string | null;
  about_type: string | null;
  about_name: string | null;
  linked_supplier_id: number | null;
  linked_supplier_ids: number[] | null;
  feedback: string | null;
  source: string | null;
  sector: string | null;
  /**
   * Comma-joined country codes ("MY", "SG", "MY,SG") — the PHP drawer's
   * dest-cb checkboxes. There is NO `region` column on the table; the R12
   * port invented one and every read/write that touched it 400'd.
   */
  destination: string | null;
  added_by: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  created_at: string | null;
}

export interface PositiveFeedbackFilters {
  aboutType?: string | null;
  sector?: string | null;
  destination?: string | null;
  q?: string | null;
  supplierId?: number | null;
}

export async function listPositiveFeedback(
  filters: PositiveFeedbackFilters,
  signal?: AbortSignal,
): Promise<PositiveFeedbackRow[]> {
  const qs = new URLSearchParams();
  if (filters.aboutType) qs.set('aboutType', filters.aboutType);
  if (filters.sector) qs.set('sector', filters.sector);
  if (filters.destination) qs.set('destination', filters.destination);
  if (filters.q) qs.set('q', filters.q);
  if (filters.supplierId) qs.set('supplier_id', String(filters.supplierId));
  const path = `/coportal/positive-feedback${qs.size ? `?${qs}` : ''}`;
  const data = await apiFetch<PositiveFeedbackRow[]>(path, {
    method: 'GET',
    signal,
  });
  return data ?? [];
}

export interface SavePositiveFeedbackRequest {
  id?: number | null;
  ref?: string | null;
  guestName?: string | null;
  feedbackDate?: string | null;
  aboutType?: string | null;
  aboutName: string;
  linkedSupplierIds?: number[] | null;
  linkedSupplierId?: number | null;
  feedback: string;
  source?: string | null;
  sector?: string | null;
  destination?: string | null;
  addedBy?: string | null;
  attachmentUrl?: string | null;
  attachmentName?: string | null;
  removeAttachment?: boolean | null;
}

export async function savePositiveFeedback(
  req: SavePositiveFeedbackRequest,
): Promise<PositiveFeedbackRow> {
  return apiFetch<PositiveFeedbackRow>(
    '/coportal/positive-feedback',
    {
      method: 'POST',
      body: JSON.stringify({
        Id: req.id ?? null,
        Ref: req.ref ?? null,
        GuestName: req.guestName ?? null,
        FeedbackDate: req.feedbackDate ?? null,
        AboutType: req.aboutType ?? null,
        AboutName: req.aboutName,
        LinkedSupplierIds: req.linkedSupplierIds ?? null,
        LinkedSupplierId: req.linkedSupplierId ?? null,
        Feedback: req.feedback,
        Source: req.source ?? null,
        Sector: req.sector ?? null,
        Destination: req.destination ?? null,
        AddedBy: req.addedBy ?? null,
        AttachmentUrl: req.attachmentUrl ?? null,
        AttachmentName: req.attachmentName ?? null,
        RemoveAttachment: req.removeAttachment ?? null,
      }),
    },
    { mutation: true },
  );
}

export async function deletePositiveFeedback(id: number): Promise<void> {
  await apiFetch<void>(
    `/coportal/positive-feedback/${id}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Super-transfers resources (excluded agents, overrides, templates) ──

export type SuperTransferResourceKind =
  | 'excluded_agent'
  | 'agent_override'
  | 'message_template';

export interface SuperTransferResource {
  id: string | null;
  kind: SuperTransferResourceKind | string | null;
  pattern: string | null;
  condition: string | null;
  label: string | null;
  company: string | null;
  company_sig: string | null;
  name: string | null;
  body: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface SuperTransferResourcesResponse {
  ok: boolean;
  can_edit: boolean;
  data: SuperTransferResource[];
  error?: string | null;
}

export async function listSuperTransferResources(
  kind?: SuperTransferResourceKind | null,
  signal?: AbortSignal,
): Promise<SuperTransferResourcesResponse> {
  const qs = kind ? `?kind=${kind}` : '';
  try {
    const raw = await apiFetchRaw<{
      ok: boolean;
      can_edit?: boolean;
      data?: SuperTransferResource[];
      error?: string | null;
    }>(`/coportal/super-transfers/resources${qs}`, { method: 'GET', signal });
    return {
      ok: !!raw?.ok,
      can_edit: !!raw?.can_edit,
      data: raw?.data ?? [],
      error: raw?.error ?? null,
    };
  } catch {
    return { ok: false, can_edit: false, data: [], error: null };
  }
}

export interface SaveSuperTransferResourceRequest {
  id?: string | null;
  kind: SuperTransferResourceKind;
  pattern?: string | null;
  condition?: string | null;
  label?: string | null;
  company?: string | null;
  companySig?: string | null;
  name?: string | null;
  body?: string | null;
}

export async function saveSuperTransferResource(
  req: SaveSuperTransferResourceRequest,
): Promise<SuperTransferResource> {
  return apiFetch<SuperTransferResource>(
    '/coportal/super-transfers/resources',
    {
      method: 'POST',
      body: JSON.stringify({
        Id: req.id ?? null,
        Kind: req.kind,
        Pattern: req.pattern ?? null,
        Condition: req.condition ?? null,
        Label: req.label ?? null,
        Company: req.company ?? null,
        CompanySig: req.companySig ?? null,
        Name: req.name ?? null,
        Body: req.body ?? null,
      }),
    },
    { mutation: true },
  );
}

export async function deleteSuperTransferResource(id: string): Promise<void> {
  await apiFetch<void>(
    `/coportal/super-transfers/resources/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

// ─── Change password (Preferences module) ─────────────────────────────

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export async function changePassword(req: ChangePasswordRequest): Promise<void> {
  await apiFetchRaw<{ ok: boolean }>(
    '/preferences/change-password',
    {
      method: 'POST',
      body: JSON.stringify({
        CurrentPassword: req.currentPassword,
        NewPassword: req.newPassword,
      }),
    },
    { mutation: true },
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Round-12 A1 — wire shapes for the five hero R12 BACKEND-R12 endpoints.
// Each wrapper degrades gracefully if BACKEND-R12 hasn't shipped: 404 +
// 5xx fall through to a typed empty payload, so the page can render its
// `TODO(BACKEND-R12)` banner without crashing the route. The non-404
// errors still propagate (caller decides how to surface).
// ═══════════════════════════════════════════════════════════════════════

/** Internal helper — swallow only 404 / 501 / 503 so we know the route is
 * absent. Anything else is a real error and must surface to the caller. */
function isNotImplemented(err: unknown): boolean {
  if (err instanceof ApiError) {
    return err.status === 404 || err.status === 501 || err.status === 503;
  }
  return false;
}

// ─── Linked refs (ref_links) ─────────────────────────────────────────

export interface RefLink {
  id: string | null;
  ref_a: string | null;
  ref_b: string | null;
  passenger_name: string | null;
  auto_matched: boolean | null;
  created_at: string | null;
  // Server-side joins (RefLinksController hydrates from logs):
  ctx_a?: { name?: string | null; sector?: string | null; date?: string | null; agent?: string | null } | null;
  ctx_b?: { name?: string | null; sector?: string | null; date?: string | null; agent?: string | null } | null;
}

export interface RefLinksResponse {
  ok: boolean;
  data: RefLink[];
  total: number;
  auto: number;
  manual: number;
  backendMissing?: boolean;
}

export async function listRefLinks(signal?: AbortSignal): Promise<RefLinksResponse> {
  try {
    const raw = await apiFetchRaw<{
      ok?: boolean;
      data?: RefLink[];
      total?: number;
      auto?: number;
      manual?: number;
    }>('/coportal/ref-links', { method: 'GET', signal });
    const data = raw?.data ?? [];
    const auto = raw?.auto ?? data.filter((r) => !!r.auto_matched).length;
    const total = raw?.total ?? data.length;
    return {
      ok: true,
      data,
      total,
      auto,
      manual: total - auto,
    };
  } catch (err) {
    if (isNotImplemented(err)) {
      return { ok: false, data: [], total: 0, auto: 0, manual: 0, backendMissing: true };
    }
    throw err;
  }
}

export interface SaveRefLinkRequest {
  id?: string | null;
  refA: string;
  refB: string;
  passengerName?: string | null;
}

export async function saveRefLink(req: SaveRefLinkRequest): Promise<RefLink> {
  return apiFetch<RefLink>(
    '/coportal/ref-links',
    {
      method: 'POST',
      body: JSON.stringify({
        Id: req.id ?? null,
        RefA: req.refA,
        RefB: req.refB,
        PassengerName: req.passengerName ?? null,
      }),
    },
    { mutation: true },
  );
}

export async function deleteRefLink(id: string): Promise<void> {
  await apiFetch<void>(
    `/coportal/ref-links/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

export interface RefLinkAutoMatchResult {
  ok: boolean;
  added: number;
  backendMissing?: boolean;
}

export async function runRefLinkAutoMatch(): Promise<RefLinkAutoMatchResult> {
  try {
    const raw = await apiFetchRaw<{ ok?: boolean; added?: number }>(
      '/coportal/ref-links/run-auto-match',
      { method: 'POST', body: '{}' },
      { mutation: true },
    );
    return { ok: !!raw?.ok, added: raw?.added ?? 0 };
  } catch (err) {
    if (isNotImplemented(err)) return { ok: false, added: 0, backendMissing: true };
    throw err;
  }
}

// ─── Hotline schedule (hotline_schedule) ─────────────────────────────

export interface HotlineScheduleRow {
  id: string | null;
  month: string | null;
  sort_order: number | null;
  period: string | null;
  period_type: string | null;
  hotline_my: string | null;
  hotline_sg: string | null;
  claimed: boolean | null;
  notes: string | null;
  // P3 parity (2026-05-25): who claimed this slot + when. Both may be
  // null for legacy claimed rows that pre-date the audit migration —
  // the UI falls back to a bare "claimed" badge in that case.
  claimed_by_user_id: string | null;
  claimed_by_email: string | null;
  claimed_at: string | null;
}

export interface HotlineScheduleResponse {
  ok: boolean;
  data: HotlineScheduleRow[];
  can_edit: boolean;
  can_see_claimed: boolean;
  // Admin-only "Send to HR" allowance-report button (PHP parity).
  can_send_report: boolean;
  backendMissing?: boolean;
}

export async function listHotlineSchedule(
  signal?: AbortSignal,
): Promise<HotlineScheduleResponse> {
  try {
    const raw = await apiFetchRaw<{
      ok?: boolean;
      data?: HotlineScheduleRow[];
      can_edit?: boolean;
      can_see_claimed?: boolean;
      can_send_report?: boolean;
    }>('/coportal/hotline-schedule', { method: 'GET', signal });
    return {
      ok: true,
      data: raw?.data ?? [],
      can_edit: !!raw?.can_edit,
      can_see_claimed: !!raw?.can_see_claimed,
      can_send_report: !!raw?.can_send_report,
    };
  } catch (err) {
    if (isNotImplemented(err)) {
      return {
        ok: false,
        data: [],
        can_edit: false,
        can_see_claimed: false,
        can_send_report: false,
        backendMissing: true,
      };
    }
    throw err;
  }
}

export interface SaveHotlineScheduleRequest {
  id?: string | null;
  month: string;
  sortOrder?: number | null;
  period?: string | null;
  periodType?: string | null;
  hotlineMy?: string | null;
  hotlineSg?: string | null;
  claimed?: boolean | null;
  notes?: string | null;
}

export async function saveHotlineScheduleRow(
  req: SaveHotlineScheduleRequest,
): Promise<HotlineScheduleRow> {
  return apiFetch<HotlineScheduleRow>(
    '/coportal/hotline-schedule',
    {
      method: 'POST',
      body: JSON.stringify({
        Id: req.id ?? null,
        Month: req.month,
        SortOrder: req.sortOrder ?? null,
        Period: req.period ?? null,
        PeriodType: req.periodType ?? null,
        HotlineMy: req.hotlineMy ?? null,
        HotlineSg: req.hotlineSg ?? null,
        Claimed: req.claimed ?? null,
        Notes: req.notes ?? null,
      }),
    },
    { mutation: true },
  );
}

export async function deleteHotlineScheduleRow(id: string): Promise<void> {
  await apiFetch<void>(
    `/coportal/hotline-schedule/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

export async function claimHotlineScheduleRow(
  id: string,
  claimed: boolean,
): Promise<HotlineScheduleRow | null> {
  try {
    return await apiFetch<HotlineScheduleRow>(
      `/coportal/hotline-schedule/${encodeURIComponent(id)}/claim`,
      { method: 'POST', body: JSON.stringify({ Claimed: claimed }) },
      { mutation: true },
    );
  } catch (err) {
    if (isNotImplemented(err)) return null;
    throw err;
  }
}

// Email the month's roster + allowance summary to HR (admin only —
// mirrors the PHP `send_report` action). Throws ApiError with the server
// message when HR email is unconfigured or SMTP fails.
export async function sendHotlineScheduleReport(month: string): Promise<void> {
  await apiFetch<void>(
    '/coportal/hotline-schedule/send-report',
    { method: 'POST', body: JSON.stringify({ Month: month }) },
    { mutation: true },
  );
}

// ─── Welcome meetings — “logs without a WM row” banner ───────────────

// Wire shape of `WelcomeMeetingsController.ToSet`. The name field is
// `passenger_name` (the logs column) — the earlier `guest_name` here never
// matched the server, so every banner chip rendered "—".
export interface WmToSetRow {
  id: string | null;
  ref: string | null;
  passenger_name: string | null;
  service_date: string | null;
  pickup_time: string | null;
  pickup_location: string | null;
  /** The hotel — PHP pre-fills Set WM's location with it. */
  dropoff_location: string | null;
  sector: string | null;
  agent: string | null;
}

export interface WmToSetResponse {
  ok: boolean;
  data: WmToSetRow[];
  /** `wm_default_location`, used when the arrival has no hotel. */
  defaultLocation?: string;
  backendMissing?: boolean;
}

/**
 * PHP has no upper bound on "to set" (upcoming only, limit 500); the server
 * caps the window at 366 days, so the page asks for all of it.
 */
export async function listWelcomeMeetingsToSet(
  signal?: AbortSignal,
  days = 366,
): Promise<WmToSetResponse> {
  try {
    const raw = await apiFetchRaw<{
      ok?: boolean;
      data?: WmToSetRow[];
      default_location?: string;
    }>(
      `/coportal/welcome-meetings/to-set?days=${days}`,
      { method: 'GET', signal },
    );
    return {
      ok: true,
      data: raw?.data ?? [],
      defaultLocation: raw?.default_location || 'Hotel',
    };
  } catch (err) {
    if (isNotImplemented(err)) return { ok: false, data: [], backendMissing: true };
    throw err;
  }
}

// ─── VIP services join (logs ⨝ vips) ─────────────────────────────────
//
// Backend: `GET /api/coportal/vips/{id}/services` (R12, see
// `VipsController.Services`). Returns a slice of the `logs` table matched
// against the VIP via `logs.ref = vip.ref` (primary) or a fuzzy
// passenger last-name gate (fallback when `vip.ref` is empty). Shape
// below mirrors the `LogServiceRow` projection on the server.

export interface VipServiceRow {
  id: string | null;
  service_date: string | null;
  ref: string | null;
  sector: string | null;
  agent: string | null;
  passenger_name: string | null;
  pax: number | null;
  flight: string | null;
  pickup_time: string | null;
  pickup_location: string | null;
  dropoff_location: string | null;
  supplier: string | null;
  product: string | null;
  ops_remarks: string | null;
  cs_remarks: string | null;
  transfer: string | null;
  status: string | null;
}

export interface VipServicesResponse {
  ok: boolean;
  data: VipServiceRow[];
  /** "ref" when the VIP carried a ref; "last_name" for the surname fallback. */
  match?: 'ref' | 'last_name';
}

export async function listVipServices(
  vipId: string,
  signal?: AbortSignal,
): Promise<VipServicesResponse> {
  const raw = await apiFetchRaw<{
    ok?: boolean;
    data?: VipServiceRow[];
    match?: 'ref' | 'last_name';
  }>(
    `/coportal/vips/${encodeURIComponent(vipId)}/services`,
    { method: 'GET', signal },
  );
  return { ok: true, data: raw?.data ?? [], match: raw?.match };
}

// ─── Positive feedback — two-step file upload ────────────────────────

export interface PositiveFeedbackUploadResult {
  attachment_url: string;
  attachment_name: string;
}

/**
 * Two-step upload helper backing `<FileUpload>`:
 *   1. POST /api/coportal/positive-feedback/upload (multipart) → { attachment_url, attachment_name }
 *   2. Caller follows with `savePositiveFeedback({ ..., attachmentUrl, attachmentName })`.
 *
 * Returns null if BACKEND-R12 hasn't shipped the endpoint yet so the
 * caller can show its TODO banner instead of crashing the drawer.
 */
export async function uploadPositiveFeedbackAttachment(
  file: File,
): Promise<PositiveFeedbackUploadResult | null> {
  const fd = new FormData();
  fd.set('file', file);
  const xsrf =
    typeof document !== 'undefined'
      ? document.cookie
          .split(';')
          .map((s) => s.trim())
          .find((s) => s.startsWith('XSRF-TOKEN='))
          ?.split('=')[1]
      : undefined;
  const res = await fetch('/api/coportal/positive-feedback/upload', {
    method: 'POST',
    credentials: 'include',
    headers: xsrf ? { 'X-XSRF-TOKEN': decodeURIComponent(xsrf) } : {},
    body: fd,
  });
  if (res.status === 404 || res.status === 501 || res.status === 503) {
    return null;
  }
  if (!res.ok) {
    let msg = `Upload failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const json = (await res.json()) as
    | { data?: PositiveFeedbackUploadResult; attachment_url?: string; attachment_name?: string }
    | null;
  if (json?.data?.attachment_url) return json.data;
  if (json?.attachment_url) {
    return { attachment_url: json.attachment_url, attachment_name: json.attachment_name ?? file.name };
  }
  return null;
}

// ═══════════════════════════════════════════════════════════════════════
// Round-13 (IMPORT-LOGS) wire shapes — preview / commit CSV import wizard.
// Mirrors `hub/coportal/api/import-logs.php`. The .NET `LogsImportController`
// hasn't shipped yet, so each call falls back to `backendMissing: true` and
// the wizard renders against the per-row LogsController.AddService fallback.
// ═══════════════════════════════════════════════════════════════════════

export const IMPORT_LOG_COLS = [
  { key: 'ref',               label: 'Ref',              required: true,  boolean: false },
  { key: 'log',               label: 'Log',              required: true,  boolean: false },
  { key: 'service_date',      label: 'Service Date',     required: true,  boolean: false },
  { key: 'pickup_time',       label: 'Pickup Time',      required: true,  boolean: false },
  { key: 'agent',             label: 'Agent',            required: true,  boolean: false },
  { key: 'passenger_name',    label: 'Passenger Name',   required: true,  boolean: false },
  { key: 'supplier',          label: 'Supplier',         required: true,  boolean: false },
  { key: 'product',           label: 'Product',          required: true,  boolean: false },
  { key: 'pickup_location',   label: 'Pickup Location',  required: true,  boolean: false },
  { key: 'dropoff_location',  label: 'Dropoff Location', required: false, boolean: false },
  { key: 'pax',               label: 'Pax',              required: false, boolean: false },
  { key: 'flight',            label: 'Flight',           required: false, boolean: false },
  { key: 'ops_remarks',       label: 'Ops Remarks',      required: false, boolean: false },
  { key: 'cs_remarks',        label: 'CS Remarks',       required: false, boolean: false },
  { key: 'courtesy',          label: 'Courtesy',         required: false, boolean: false },
  { key: 'transfer',          label: 'Transfer',         required: false, boolean: false },
  { key: 'transfer_message',  label: 'Transfer Message', required: false, boolean: false },
  { key: 'overland',          label: 'Overland',         required: false, boolean: true  },
  { key: 'is_group',          label: 'Group',            required: false, boolean: true  },
  { key: 'contact_details',   label: 'Contact Details',  required: false, boolean: false },
  { key: 'file_manager',      label: 'File Manager',     required: false, boolean: false },
  // A getter: this module loads with the app, before the company profile
  // saved in Admin ▸ Company arrives, so the key agent is read on use.
  { key: 'kuoni_ref', get label() { return `${COMPANY.keyAgent} Ref`; }, required: false, boolean: false },
] as const;
export type ImportLogColKey = (typeof IMPORT_LOG_COLS)[number]['key'];

export interface ImportLogPreviewRow {
  rowNum: number;
  values: Partial<Record<ImportLogColKey, string>>;
  errors: string[];
  resolvedAgentId?: string | null;
  resolvedPicUserId?: string | null;
  resolutionConfidence?: 'exact' | 'fuzzy' | 'unresolved';
}

export interface ImportLogPreviewResponse {
  ok: boolean;
  token: string | null;
  rows: ImportLogPreviewRow[];
  inserted: number;
  failed: number;
  errors: Array<{ row: number; error: string }>;
  // Entities the resolver couldn't match — the backend emits these on the
  // PREVIEW response only (commit does not), so the wizard captures them here
  // and carries them to the Done banner.
  unmatched_agents: string[];
  unmatched_suppliers: string[];
  backendMissing?: boolean;
}

export interface ImportLogPreviewRequest {
  /** Rows already parsed + column-mapped in the browser (the wizard applies
   *  `columnMap` + the `dateFormat` picker client-side, per import-logs-spec
   *  §8.1 "client-parse + send rows"). Keys are the portal column keys, which
   *  are exactly the snake_case field names the .NET `ValidatePreviewRows`
   *  reader expects (`ref`, `service_date`, `pickup_time`, …). */
  rows: Array<Partial<Record<ImportLogColKey, string>>>;
  batchLabel: string;
  /** Wizard destination — translated to the backend's `logs` | `preparation`
   *  on the wire (the UI calls the live table "live", the API calls it
   *  "logs"). */
  destination: 'preparation' | 'live';
  /** Import helper (brief 2026-10-08 §6): where the rows came from. Omitted
   *  for a plain import, which then sends exactly what it always sent. */
  source?: 'ai-document' | 'ai-map' | 'profile' | null;
  /** The file or profile name shown on the Log in Preparation batch. */
  sourceDetail?: string | null;
  /** Per-row AI confidence and source note, in the order of `rows`. */
  rowMeta?: Array<{ confidence: number | null; note: string | null } | null> | null;
  /** The saved import profile the rows were read with: the server applies its agent aliases. */
  profileId?: string | null;
}

/** Wizard destination → backend `destination` value. The UI labels the live
 *  logs table "live"; the .NET controller only accepts "logs" | "preparation"
 *  (LogsImportController.Preview rejects anything else with a 400). */
function backendDestination(d: 'preparation' | 'live'): 'logs' | 'preparation' {
  return d === 'live' ? 'logs' : 'preparation';
}

/** Result of `POST /coportal/import-logs/parse-sheet`. */
export interface ParsedSheetResponse {
  headers: string[];
  rows: Record<string, string>[];
  /** Worksheet actually read — the first one with cells in it. */
  sheetName: string | null;
  /** Every tab in the book, so the UI can say which one it took. */
  sheetNames: string[];
  /**
   * Columns where a value Excel was rendering as `4.9173E+11` was read back
   * from the underlying number instead. Shown to the operator, because a
   * silent repair is still a change to their data.
   */
  recoveredNumericColumns: string[];
  /** Title / banner rows skipped above the real header row (0 normally). */
  skippedTitleRows: number;
}

/**
 * Read an .xlsx workbook into the same `{ headers, rows }` the CSV parser
 * produces, so the rest of the wizard does not know which it got.
 *
 * Server-side on purpose. CSV stays in the browser — `decodeTextFile.ts`
 * sniffs CP1252 there, and that fix is correct. XLSX has no encoding to
 * sniff and no npm reader worth adding (the `xlsx` package is frozen at
 * 0.18.5 on the public registry with open advisories), while ClosedXML is
 * already a backend dependency. See ImportSheetParser.
 */
export async function parseSheetUpload(file: File): Promise<ParsedSheetResponse> {
  const fd = new FormData();
  fd.set('file', file);
  const xsrf = readXsrfToken();
  const res = await fetch('/api/coportal/import-logs/parse-sheet', {
    method: 'POST',
    credentials: 'include',
    headers: xsrf ? { 'X-XSRF-TOKEN': xsrf } : {},
    body: fd,
  });
  let body: { ok?: boolean; error?: string } & Partial<ParsedSheetResponse> = {};
  try {
    body = await res.json();
  } catch {
    /* non-JSON error page — fall through to the status-code message */
  }
  if (!res.ok || body.ok === false) {
    throw new Error(body.error || `Could not read that workbook (${res.status}).`);
  }
  return {
    headers: body.headers ?? [],
    rows: body.rows ?? [],
    sheetName: body.sheetName ?? null,
    sheetNames: body.sheetNames ?? [],
    recoveredNumericColumns: body.recoveredNumericColumns ?? [],
    skippedTitleRows: body.skippedTitleRows ?? 0,
  };
}

export async function previewLogsImport(
  req: ImportLogPreviewRequest,
  signal?: AbortSignal,
): Promise<ImportLogPreviewResponse> {
  try {
    const raw = await apiFetchRaw<{
      ok?: boolean;
      token?: string;
      rows?: ImportLogPreviewRow[];
      inserted?: number;
      failed?: number;
      errors?: Array<{ row: number; error: string }>;
      unmatched_agents?: string[];
      unmatched_suppliers?: string[];
    }>(
      '/coportal/import-logs/preview',
      {
        method: 'POST',
        // Field names MUST be the exact snake_case `[JsonPropertyName]`s on
        // `LogsImportRequest` (`rows` / `destination` / `batch_label`). MVC
        // JSON binding is case-insensitive but underscore-sensitive, so the
        // old `BatchLabel` never bound to `batch_label` and `CsvText` never
        // bound to `csv` → both `csv` and `rows` came through null → 400.
        body: JSON.stringify({
          rows: req.rows,
          destination: backendDestination(req.destination),
          batch_label: req.batchLabel,
          ...(req.source
            ? { source: req.source, source_detail: req.sourceDetail ?? null, row_meta: req.rowMeta ?? null }
            : {}),
          ...(req.profileId ? { profile_id: req.profileId } : {}),
        }),
        signal,
      },
      { mutation: true },
    );
    return {
      ok: !!raw?.ok,
      token: raw?.token ?? null,
      rows: raw?.rows ?? [],
      inserted: raw?.inserted ?? 0,
      failed: raw?.failed ?? 0,
      errors: raw?.errors ?? [],
      unmatched_agents: raw?.unmatched_agents ?? [],
      unmatched_suppliers: raw?.unmatched_suppliers ?? [],
    };
  } catch (err) {
    if (isNotImplemented(err)) {
      return {
        ok: false,
        token: null,
        rows: [],
        inserted: 0,
        failed: 0,
        errors: [],
        unmatched_agents: [],
        unmatched_suppliers: [],
        backendMissing: true,
      };
    }
    throw err;
  }
}

export interface ImportLogCommitRequest {
  token: string | null;
  /** Used in the BACKEND-R13 fallback path — when no token, the wizard
   *  posts the same row shape to a per-row endpoint and aggregates. */
  rows: Array<Partial<Record<ImportLogColKey, string>>>;
  batchLabel: string;
  destination: 'preparation' | 'live';
}

export interface ImportLogCommitResponse {
  ok: boolean;
  inserted: number;
  failed: number;
  errors: Array<{ row: number; error: string }>;
  batch_id: string | null;
  unmatched_agents: string[];
  unmatched_suppliers: string[];
  backendMissing?: boolean;
}

/** Commit chunk size — mirrors the PHP importer's `$BATCH = 100` and the
 *  .NET `?offset=&limit=` slicing. Keeps each POST body well under nginx /
 *  Traefik limits on large CSVs while all chunks land in one preparation
 *  batch via `_batch_id`. */
const COMMIT_CHUNK = 100;

/** Raw per-chunk commit envelope from `POST /coportal/import-logs/commit`. */
interface CommitChunkRaw {
  ok?: boolean;
  inserted?: number;
  failed?: number;
  errors?: Array<{ row: number; error: string }>;
  batch_id?: string;
  unmatched_agents?: string[];
  unmatched_suppliers?: string[];
}

export async function commitLogsImport(
  req: ImportLogCommitRequest,
): Promise<ImportLogCommitResponse> {
  // Preferred: token-backed commit. The server holds the validated rows for
  // this token (10-min sliding TTL); we commit them in ≤100-row chunks via
  // `?offset=&limit=`, reusing the `_batch_id` returned by the first chunk so
  // every chunk lands in a single preparation_batches row. The token is only
  // consumed on the final chunk (offset+limit ≥ total), so a re-commit before
  // then is safe. `req.rows` is the client's clean-row list — same count +
  // order as the server's cached set — so its length drives the chunk plan.
  if (req.token) {
    try {
      const destination = backendDestination(req.destination);
      const total = req.rows.length;
      const chunks = Math.max(1, Math.ceil(total / COMMIT_CHUNK));
      let inserted = 0;
      let failed = 0;
      const errors: Array<{ row: number; error: string }> = [];
      let batchId: string | null = null;
      let lastRaw: CommitChunkRaw | null = null;

      for (let c = 0; c < chunks; c++) {
        const offset = c * COMMIT_CHUNK;
        const raw: CommitChunkRaw = await apiFetchRaw<CommitChunkRaw>(
          `/coportal/import-logs/commit?offset=${offset}&limit=${COMMIT_CHUNK}`,
          {
            method: 'POST',
            // Exact snake_case `[JsonPropertyName]`s on `LogsImportCommitRequest`:
            // `token` / `destination` / `batch_label` / `_batch_id`. The old
            // `Token` bound (case-insensitive) but `BatchLabel`/`_batch_id`
            // did not, so chunked batch reuse was impossible.
            body: JSON.stringify({
              token: req.token,
              destination,
              batch_label: req.batchLabel,
              _batch_id: batchId,
            }),
          },
          { mutation: true },
        );
        inserted += raw?.inserted ?? 0;
        failed += raw?.failed ?? 0;
        if (raw?.errors?.length) errors.push(...raw.errors);
        if (raw?.batch_id) batchId = raw.batch_id;
        lastRaw = raw;
      }

      return {
        ok: failed === 0 && (lastRaw?.ok ?? true),
        inserted,
        failed,
        errors,
        batch_id: batchId,
        unmatched_agents: lastRaw?.unmatched_agents ?? [],
        unmatched_suppliers: lastRaw?.unmatched_suppliers ?? [],
      };
    } catch (err) {
      if (!isNotImplemented(err)) throw err;
      // fall through to per-row fallback
    }
  }

  // TODO(BACKEND-R13) fallback path — post each row through the existing
  // `POST /api/coportal/logs/add-service` until the bulk controller lands.
  const errors: Array<{ row: number; error: string }> = [];
  let inserted = 0;
  let failed = 0;
  for (let i = 0; i < req.rows.length; i++) {
    const row = req.rows[i]!;
    const logRaw = (row.log ?? '').toString().trim().toUpperCase();
    // The log's destination (MY, BN, SG for Asian Trails), else the first.
    const logType = destinationOfLog(logRaw) ?? defaultDestination();
    const payload = {
      Ref: row.ref ?? '',
      LogType: logType,
      ServiceDate: row.service_date ?? null,
      PickupTime: row.pickup_time ?? null,
      Agent: row.agent ?? null,
      PassengerName: row.passenger_name ?? null,
      Supplier: row.supplier ?? null,
      Product: row.product ?? null,
      PickupLocation: row.pickup_location ?? null,
      DropoffLocation: row.dropoff_location ?? null,
      Pax: row.pax ?? null,
      Flight: row.flight ?? null,
      OpsRemarks: row.ops_remarks ?? null,
      CsRemarks: row.cs_remarks ?? null,
      Courtesy: row.courtesy ?? null,
      Transfer: row.transfer ?? null,
      TransferMessage: row.transfer_message ?? null,
      Overland: (row.overland ?? '').toString().toLowerCase() === 'true',
      IsGroup: (row.is_group ?? '').toString().toLowerCase() === 'true',
      ContactDetails: row.contact_details ?? null,
      FileManager: row.file_manager ?? null,
      KuoniRef: row.kuoni_ref ?? null,
    };
    try {
      await apiFetch('/coportal/logs/add-service', {
        method: 'POST',
        body: JSON.stringify(payload),
      }, { mutation: true });
      inserted++;
    } catch (e: unknown) {
      failed++;
      const message = e instanceof Error ? e.message : 'add-service failed';
      errors.push({ row: i + 1, error: message });
    }
  }
  return {
    ok: failed === 0,
    inserted,
    failed,
    errors,
    batch_id: null,
    unmatched_agents: [],
    unmatched_suppliers: [],
    backendMissing: !req.token,
  };
}

// ── Log-preparation batch management ────────────────────────────────
//
// Wrappers for LogsPreparationController (PHP api/log-preparation.php).
// Batch selector strip + info bar + approve/delete/add-row. Rows are
// returned as LogRow[] so the existing logs table components can render
// them. Envelopes are `{ ok, … }` (NOT `{ ok, data }`), so these go
// through apiFetchRaw and read the named field directly.

export interface PreparationBatch {
  id: string;
  label: string | null;
  status: string | null;
  created_at: string | null;
  created_by: string | null;
  approved_at: string | null;
  approved_by: string | null;
  /** Import helper (db/2026-10-15_import_ai.sql): 'ai-document' | 'ai-map' |
   *  'profile', or null/absent for a plain import. */
  source?: string | null;
  /** The file or import profile the batch was read from. */
  source_detail?: string | null;
}

export interface PrepApproveOutcome {
  ok: boolean;
  inserted: number;
  failed: number;
  errors: Array<{ row: string; error: string }>;
  batch_done: boolean;
  /** Supplier recap + CS/OPS notification emails actually delivered. */
  emails_sent?: number;
  /**
   * Suppliers with no email on file, plus send failures. These are NOT
   * promote failures — the rows are in the live log either way — but the
   * operator has to know which suppliers were never told about their
   * assignments so they can be contacted by hand.
   */
  email_errors?: string[];
  /** Per-supplier outcome, also persisted to `preparation_batches.email_status`. */
  email_details?: Array<{
    supplier: string;
    email: string;
    rows: number;
    ok: boolean;
  }>;
  /**
   * IDs of prep rows that were inserted into the live log but whose
   * staging-row delete failed — they are still present in the batch. The
   * server sets `ok: false` and lists them here so the operator does NOT
   * blind re-approve (which would double-insert). Absent/empty on the
   * happy path.
   */
  still_staged?: string[];
}

/** List DRAFT preparation batches (selector strip). */
export async function listPreparationBatches(
  signal?: AbortSignal,
): Promise<PreparationBatch[]> {
  const res = await apiFetchRaw<{ ok?: boolean; batches?: PreparationBatch[] }>(
    '/coportal/log-preparation/batches',
    { method: 'GET', signal },
  );
  return res.batches ?? [];
}

/** Rows staged in a single batch. */
export async function listPreparationRows(
  batchId: string,
  signal?: AbortSignal,
): Promise<LogRow[]> {
  const res = await apiFetchRaw<{ ok?: boolean; rows?: LogRow[] }>(
    `/coportal/log-preparation/rows?batch_id=${encodeURIComponent(batchId)}`,
    { method: 'GET', signal },
  );
  return res.rows ?? [];
}

/** Append a blank row to a batch. Returns the created row (raw). */
export async function addPreparationRow(
  batchId: string,
): Promise<LogRow | null> {
  const res = await apiFetchRaw<{ ok?: boolean; row?: LogRow }>(
    '/coportal/log-preparation/add-row',
    { method: 'POST', body: JSON.stringify({ batch_id: batchId }) },
    { mutation: true },
  );
  return res.row ?? null;
}

/** Delete an entire batch (admin / page-access gated on the server). */
export async function deletePreparationBatch(batchId: string): Promise<void> {
  await apiFetchRaw<{ ok?: boolean }>(
    '/coportal/log-preparation/delete-batch',
    { method: 'POST', body: JSON.stringify({ batch_id: batchId }) },
    { mutation: true },
  );
}

/**
 * Promote rows from a batch into the live log. Pass `approveAll` to close
 * the whole batch, or `rowIds` to promote a selected subset. The batch
 * flips to `status='approved'` on the server once it empties.
 */
export async function approvePreparationRows(args: {
  batchId: string;
  approveAll?: boolean;
  rowIds?: string[];
}): Promise<PrepApproveOutcome> {
  return apiFetchRaw<PrepApproveOutcome>(
    '/coportal/log-preparation/approve',
    {
      method: 'POST',
      body: JSON.stringify({
        batch_id: args.batchId,
        approve_all: args.approveAll ?? false,
        row_ids: args.rowIds ?? [],
      }),
    },
    { mutation: true },
  );
}

/**
 * Attention-pill counts for the CS/OPS rail.
 *
 * `modifications` = edits queued for a manager; `noShow` = no-show rows with
 * neither report written yet. Both are already scoped server-side to what the
 * caller may actually open (NavBadgesController), so the rail renders the
 * number verbatim.
 */
export interface NavBadgeCounts {
  modifications: number;
  noShow: number;
  /** In-house vehicles with a road tax / Puspakom / insurance / service line due or expired. */
  vehicles: number;
}

export async function getNavBadges(signal?: AbortSignal): Promise<NavBadgeCounts> {
  const res = await apiFetchRaw<{ ok?: boolean; data?: NavBadgeCounts | null }>(
    '/coportal/nav-badges',
    { method: 'GET', signal },
  );
  return {
    modifications: res.data?.modifications ?? 0,
    noShow: res.data?.noShow ?? 0,
    vehicles: res.data?.vehicles ?? 0,
  };
}

// ─── Dashboard parity sections ────────────────────────────────────────
// Backs the rebuilt CS/OPS dashboard, which mirrors `pages/dashboard.php`
// section for section. Server: DashboardSectionsController.
//
// Four calls rather than one: the briefing fans out across six tables and
// is the slowest thing on the page, so folding it into a single response
// would hold the whole dashboard behind its worst query.

/**
 * Lines per destination (sectors folded server-side): one key per
 * destination of the tenant's geography, MY / SG / BN for Asian Trails.
 */
export type DashboardSectorCounts = Record<string, number>;

export interface DashboardHotlineDuty {
  my: string;
  sg: string;
  period: string;
}

export interface DashboardDay {
  date: string;
  /** "Friday, September 11" — already localised server-side. */
  label: string;
  totalServices: number;
  bySector: DashboardSectorCounts;
  counters: {
    pending: number;
    cancelled: number;
    noShows: number;
    flightMismatch: number;
    flightErrors: number;
  };
  week: {
    from: string;
    to: string;
    /** "5 Sep – 10 Sep". Driven by `log_week_start`/`log_week_end`, so this
     *  is the OPERATIONAL week, not a calendar one. */
    label: string;
    total: number;
    bySector: DashboardSectorCounts;
  };
  hotline: { now: DashboardHotlineDuty; next: DashboardHotlineDuty };
}

export interface CourtesyCallItem {
  id: string;
  ref: string;
  passengerName: string;
  agent: string;
  arrivalDate: string;
  bucket: 'overdue' | 'due_today';
}

export interface CourtesyCallsPanel {
  overdue: number;
  dueToday: number;
  items: CourtesyCallItem[];
}

export interface RecentActivityItem {
  id: string;
  rowId: string;
  passengerName: string;
  ref: string;
  sector: string;
  agent: string;
  userName: string;
  createdAt: string | null;
  /** "flight → MH1455 ETD 1735" or "ref, status, service_date +12 more". */
  changeText: string;
}

export type BriefingCategory =
  | 'vip' | 'transfer' | 'wm' | 'issue' | 'mod' | 'sdk' | 'courtesy';

export interface BriefingItem {
  category: BriefingCategory;
  /** 0 = most urgent. Server returns the list already sorted. */
  priority: number;
  title: string;
  detail: string;
  ref: string | null;
}

export async function getDashboardDay(
  date?: string,
  signal?: AbortSignal,
): Promise<DashboardDay | null> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  const res = await apiFetchRaw<{ ok?: boolean; data?: DashboardDay | null }>(
    `/coportal/dashboard/day${qs}`,
    { method: 'GET', signal },
  );
  return res.data ?? null;
}

export async function getDashboardCourtesyCalls(
  date?: string,
  signal?: AbortSignal,
): Promise<CourtesyCallsPanel> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  const res = await apiFetchRaw<{ ok?: boolean; data?: CourtesyCallsPanel | null }>(
    `/coportal/dashboard/courtesy-calls${qs}`,
    { method: 'GET', signal },
  );
  return res.data ?? { overdue: 0, dueToday: 0, items: [] };
}

export async function getDashboardRecentActivity(
  signal?: AbortSignal,
): Promise<RecentActivityItem[]> {
  const res = await apiFetchRaw<{ ok?: boolean; data?: RecentActivityItem[] | null }>(
    '/coportal/dashboard/recent-activity',
    { method: 'GET', signal },
  );
  return res.data ?? [];
}

export async function getDashboardBriefing(
  signal?: AbortSignal,
): Promise<BriefingItem[]> {
  const res = await apiFetchRaw<{
    ok?: boolean;
    data?: { date: string; items: BriefingItem[] } | null;
  }>('/coportal/dashboard/briefing', { method: 'GET', signal });
  return res.data?.items ?? [];
}
